import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import BrandByline from './components/BrandByline';
import { createPortal } from "react-dom";
import STRINGS, { pick } from "./constants/strings";
import T from "./constants/theme";
import { apiFetch } from "./lib/apiClient";
import { LangContext } from "./hooks/useLang";
import useLang from "./hooks/useLang";
import useMobile from "./hooks/useMobile";
import GlobalStyles from "./components/GlobalStyles";
import MockupStyles from "./components/MockupStyles";
import BottomNav from "./components/BottomNav";
import WaveSurfer from 'wavesurfer.js';
import BottomPlayer, { resolveAudio, VolumeControl } from "./components/BottomPlayer";
import AskModal from "./components/AskModal";
import Sidebar from "./components/Sidebar";
import LangDropdown from "./components/LangDropdown";
import HamburgerMenu, { NavIcons } from "./components/HamburgerMenu";
import LoadingScreen from "./screens/LoadingScreen";
import IntentionScreen from "./screens/IntentionScreen";
import FicheScreen from "./screens/FicheScreen";
import VersionsScreen from "./screens/VersionsScreen";
import OnboardingHints from "./components/OnboardingHints";
import { HOME_STEPS, ONBOARDING_STORAGE_KEYS } from "./constants/onboardingSteps";

import { saveAnalysis, getAnalysis, loadProjects, createProject, renameProject, deleteProject, renameTrack, deleteTrack, moveTrackToProject, reorderTracksInProject, setProjectCoverImage, clearProjectCoverImage, setTrackCoverImage, clearTrackCoverImage, updateTrackIntent, updateVersionIntent, findDuplicateAudio, loadSharedProjectsWithTracks, loadSharedTracks } from "./lib/storage";
import { getPending, clearPending } from "./lib/pendingJob";
import { assignProjectColors, PROJECT_COLOR_COUNT } from "./lib/projectColors";
import { resizeImageFile } from "./lib/image";
import { supabase } from "./lib/supabase";
import useIsAdmin, { clearIsAdminCache } from "./hooks/useIsAdmin";
import { useAuth } from "./hooks/useAuth";
import AuthScreen from "./screens/AuthScreen";
import LandingScreen from "./screens/LandingScreen";
import SampleFicheScreen from "./screens/SampleFicheScreen";
import PricingScreen from "./screens/PricingScreen";
import PluginScreen from "./screens/PluginScreen";
import AdminScreen from "./screens/AdminScreen";
import PublicFicheScreen from "./screens/PublicFicheScreen";
import JoinScreen from "./screens/JoinScreen";
import ProjectMembersModal from "./components/ProjectMembersModal";
import PrivacyScreen from "./screens/PrivacyScreen";
import TermsScreen from "./screens/TermsScreen";
import UpdatePasswordScreen from "./screens/UpdatePasswordScreen";
import ConfirmDeleteAccountScreen from "./screens/ConfirmDeleteAccountScreen";
import ReglagesModal from "./components/ReglagesModal";
import RenameModal from "./components/RenameModal";
import AddModal from "./components/AddModal";
import NoCreditsModal from "./components/NoCreditsModal";
import ArchipelConsentToast from "./components/ArchipelConsentToast";
import FeedbackModal from "./components/FeedbackModal";
import { confirmDialog } from "./lib/confirm.jsx";
import { trackPixelPageView, trackPixelAnalysisStarted } from "./lib/pixel";
import { captureAttribution, logCtaClick } from "./lib/attribution";

/* ── Font loader ────────────────────────────────────────── */
const FontLink = () => (
  <style>{`@import url('https://fonts.googleapis.com/css2?family=Fredoka:wght@500;600;700&family=Bebas+Neue&family=Cormorant+Garamond:ital,wght@0,400;0,600;1,400;1,600&family=DM+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400;1,500&display=swap');`}</style>
);

/* ── Welcome Home Screen ─────────────────────────────────
   Les cartes "conseil" / "pédago" tournent à chaque ouverture de la Home.
   Pour éviter de voir deux fois de suite le même tip, on mémorise le
   dernier index affiché par pool dans localStorage et on tire au hasard
   un index différent à chaque render initial.
*/
const SAVIEZ_VOUS_TIPS = [
  "Faire des pauses régulières permet de conserver une écoute attentive et objective.",
  "Tes oreilles se fatiguent après 45 min — une pause de 10 min te fait gagner 2h de travail.",
  "Écouter ton mix dans un autre contexte (voiture, écouteurs) révèle ce que le studio cache.",
  "Baisser le volume de monitoring aide à repérer les déséquilibres de balance.",
  "Prendre du recul sur un mix pendant 24h change complètement ta perception.",
  "Comparer régulièrement avec une référence recalibre ton oreille et tes choix.",
  "Le silence entre les sessions est aussi important que le travail lui-même.",
  "Écouter à faible volume est le meilleur test : si le mix fonctionne bas, il fonctionnera fort.",
  "Le bas du spectre se juge mieux debout qu'assis — tu sens mieux les sub.",
  "Ton premier jet de mix contient souvent une vérité que tu perds en peaufinant trop.",
  "Un mix qui tient en mono tient presque toujours en stéréo.",
  "La meilleure compression est souvent celle qu'on n'entend pas.",
  "Les ingénieurs pros changent d'enceintes toutes les 30 minutes pour ne pas s'habituer à une seule couleur.",
  "Le cerveau « moyenne » ce qu'il entend : après 20 minutes sur un passage, tu n'entends plus les défauts.",
  "La fatigue auditive touche d'abord les aigus — c'est pour ça qu'on a tendance à ajouter du haut, tard le soir.",
  "Écouter ton mix en marchant te fait remarquer des choses que le studio assis cache.",
  "Un bon mix doit fonctionner sur un téléphone posé sur la table — c'est l'épreuve du feu.",
  "La corrélation stéréo t'indique si ton mix s'effondre quand il est lu en mono.",
  "La dynamique perçue dépend du contexte : -10 LUFS peut sonner fort en stream et faible en vinyle.",
  "Un master trop fort masque les erreurs — quand on baisse, elles ressortent.",
  "Le cerveau adore la nouveauté : écouter une référence avant ton mix te le rend plus « neuf ».",
  "Un casque fermé exagère le bas, un casque ouvert exagère le haut. Croise les deux.",
  "Les 200 premières millisecondes d'un son définissent son impact perçu — c'est là qu'il faut être précis.",
  "Tes décisions sont meilleures le matin — le cerveau est moins biaisé par la fatigue cognitive.",
  "Boire régulièrement de l'eau améliore réellement la clarté auditive.",
  "L'acoustique d'une pièce change avec l'heure : une pièce chauffée n'a pas la même réponse qu'une pièce froide.",
  "Les pros écoutent leurs mix sur des enceintes médiocres pour repérer ce qui tient malgré tout.",
  "Le silence absolu n'existe pas en studio : ton plancher de bruit fait partie de ton écoute.",
  "Un niveau de monitoring constant d'une session à l'autre t'évite de remixer les mêmes choses.",
  "Si tu bouges la tête pendant l'écoute, tu corriges naturellement les colorations de ta pièce.",
  "Le kick et la voix principale occupent souvent la même plage d'énergie : leur équilibre fait un mix.",
];

const PROCHAIN_PAS_TIPS = [
  { title: "Écoute à bas volume", body: "Monitorer à faible volume révèle les déséquilibres de balance. Si le mix fonctionne bas, il fonctionnera fort." },
  { title: "Change d'environnement", body: "Écoute ton mix en voiture ou au casque : les défauts invisibles en studio deviennent évidents ailleurs." },
  { title: "Fais une pause de 24h", body: "Reviens sur ton mix après 24h de recul — ton oreille neuve te dira plus que 2h de travail d'affilée." },
  { title: "Compare avec une référence", body: "Prends un morceau que tu aimes, cale son volume, et alterne. Tu verras ce qui te manque ou ce qui dépasse." },
  { title: "Travaille en mono", body: "Bascule ton mix en mono pour détecter les annulations de phase et vérifier la solidité du bas." },
  { title: "Imprime puis oublie", body: "Exporte ton bounce, ferme la session, écoute-le plus tard ailleurs. Tu l'entendras comme un auditeur." },
  { title: "Vérifie la dynamique", body: "Regarde ton crest factor : trop plat, le mix fatigue ; trop dynamique, il peine à sortir du lecteur." },
  { title: "Écoute en voiture", body: "L'habitacle révèle les basses qui traînent et les médiums agressifs. Un vrai révélateur." },
  { title: "Isole kick + basse", body: "Solo ton kick et ta basse : leur relation fondamentale définit le groove et l'assise de ton mix." },
  { title: "Test du téléphone", body: "Sors ton téléphone, pose-le sur la table, joue ton mix. C'est le test le plus honnête côté public." },
  { title: "Coupe le bas", body: "Passe un high-pass à 150 Hz sur ton master : ce qui reste t'indique si les médiums et aigus tiennent seuls." },
  { title: "Ferme les yeux", body: "Écoute une minute les yeux fermés, sans regarder le DAW. Tu entendras ce que l'œil cachait à l'oreille." },
  { title: "Marche pendant l'écoute", body: "Lance ton mix à bas volume et marche dans la pièce — les problèmes de balance sautent aux oreilles." },
  { title: "Passage à l'autre", body: "Enchaîne ton mix avec un titre commercial. Le « saut » de qualité t'indique précisément où travailler." },
  { title: "Écoute demain matin", body: "Ferme la session, dors dessus, réécoute au petit déjeuner sans toucher au mix. Ta vraie opinion apparaît là." },
  { title: "Vise la médiane", body: "Cherche ce qui fonctionne partout (voiture, casque, enceintes), pas ce qui brille seulement dans ton studio." },
  { title: "Coupe la voix lead", body: "Écoute le mix sans la voix principale : si l'instru ne tient pas, tu masquais un problème derrière elle." },
  { title: "Re-compresse ta musique", body: "Fais un MP3 160 kbps de ton bounce et écoute-le. Les 2/3 des auditeurs t'entendront comme ça." },
  { title: "Teste à volume réaliste", body: "Règle ton monitoring à la hauteur d'une conversation normale. Si ça passe là, ça passe partout." },
];

const PROGRESSION_TIPS_NO_SCORE = [
  "Dès que tu as deux versions d'un même titre, VERSIONS compare les mix et met en lumière ce qui a bougé.",
  "Importe un premier bounce : l'analyse fréquentielle et dynamique se fait en moins d'une minute.",
  "Chaque titre ajouté affine ton oreille et te donne une référence pour les suivants.",
  "Commence par analyser un morceau que tu connais bien — tu valideras l'analyse contre ta perception.",
  "Ajoute d'abord ta V1 brute : elle servira de repère pour voir l'évolution.",
  "Une fiche par titre suffit pour voir apparaître les forces et les manques de ton mix.",
  "Analyse d'abord un titre maîtrisé : tu confronteras ton ressenti aux mesures objectives.",
  "Tu peux importer une référence commerciale — elle te servira d'étalon pour tes propres bounces.",
  "Trois titres, c'est déjà assez pour que des tendances apparaissent dans tes choix de mix.",
  "Tes premières analyses sont aussi celles qui t'apprennent le plus : chaque fiche te donne un nouveau repère.",
];

const PROGRESSION_TIPS_WITH_SCORE = [
  "Continue à comparer tes versions pour affiner l'évaluation. Chaque analyse précise la lecture de ton mix.",
  "Un score ne juge pas ton mix — il pointe ce qui pourrait être amélioré.",
  "Le score évolue avec tes choix : équilibre fréquentiel, dynamique, clarté stéréo.",
  "Regarde les tendances plus que les valeurs absolues : deux versions écartées de 5 points, c'est déjà significatif.",
  "Si le score baisse d'une version à l'autre, c'est peut-être le bon signal pour revenir en arrière.",
  "Note tes versions avec un commentaire : tu verras ensuite quels gestes font vraiment bouger le score.",
  "Un score stable sur trois versions indique que tu tournes en rond — change d'angle d'attaque.",
  "Compare deux versions côte à côte plutôt que de les juger séparément : l'écart est plus parlant.",
  "Le score n'est qu'un repère : c'est la fiche détaillée qui te dit pourquoi.",
  "Si une version très retravaillée score moins que la précédente, fais-toi confiance et reviens en arrière.",
];

const A_QUOI_CA_SERT_TIPS = [
  { title: "Analyser comme un pro", body: "Chaque titre que tu importes passe par une analyse objective — équilibre fréquentiel, dynamique, stéréo, saturation — puis par une écoute IA détaillée. Tu obtiens une fiche claire qui pointe ce qui marche et ce qui coince." },
  { title: "Des métriques lisibles", body: "VERSIONS traduit les mesures techniques (LUFS, crest factor, corrélation stéréo) en commentaires compréhensibles. Pas besoin d'être ingé son pour savoir quoi ajuster." },
  { title: "Un second avis fiable", body: "L'écoute IA commente ton mix section par section et t'indique où ton oreille a peut-être fatigué. Un regard extérieur disponible à toute heure." },
  { title: "Une fiche claire", body: "Chaque analyse te livre une fiche structurée : forces, points à retravailler, niveaux, dynamique. Pas d'interprétation, juste des repères." },
  { title: "Repérer la fatigue d'oreille", body: "VERSIONS écoute avec la même fraîcheur quelle que soit l'heure. Elle détecte ce que ton oreille surcompense après plusieurs heures de session." },
  { title: "Sortir de la boucle V2/V3", body: "Quand on hésite entre deux versions, l'analyse comparative tranche objectivement : tu sais laquelle tient mieux, et pourquoi." },
  { title: "Un cahier de bord auditif", body: "Chaque analyse est archivée et datée. Tu peux revenir en arrière, voir l'évolution d'un titre et identifier quand un mix a commencé à dériver." },
  { title: "Un regard sans ego", body: "VERSIONS n'a pas d'intérêt à flatter ton mix ni à le démonter. Elle te dit ce qui est, tel quel, avec les mots qu'il faut." },
  { title: "Préparer le master", body: "Avant d'envoyer ton mix au mastering, VERSIONS te signale les zones à nettoyer. Tu arrives au studio avec un mix propre, pas avec du travail en moins pour l'ingé master." },
  { title: "Gagner des heures d'écoute", body: "Une analyse de 2 minutes remplace des dizaines d'allers-retours entre enceintes, casque et voiture. Tu cibles direct ce qui coince." },
];

const POURQUOI_VERSIONS_TIPS = [
  { title: "Comparer tes mix entre eux", body: "Uploade plusieurs versions d'un même titre : VERSIONS met en évidence ce qui a progressé, ce qui régresse, et les zones à retravailler." },
  { title: "Voir la progression", body: "Chaque version est datée et scorée. Tu vois d'un coup d'œil si la V3 est réellement meilleure que la V2 — ou s'il faut revenir en arrière." },
  { title: "Défaire l'intuition trompeuse", body: "Parfois on pense qu'un mix s'améliore alors qu'il perd en clarté. VERSIONS objective ce ressenti et te donne des points de repère stables." },
  { title: "Garder l'historique", body: "Tes versions restent rangées par projet et datées. Plus besoin de chercher 'quelle est la dernière bonne' dans un dossier." },
  { title: "Un studio de poche", body: "Le recul d'un ingénieur son disponible à n'importe quelle heure, sans rendez-vous, sans coût de studio, sans attente de retour par mail." },
  { title: "Rompre la solitude du home-studio", body: "Quand on mixe seul, on perd l'œil extérieur. VERSIONS remplit ce rôle — un avis tangible, étayé, qui relance ton écoute." },
  { title: "Confirmer ses choix", body: "Parfois on sait ce qui cloche mais on n'ose pas trancher. VERSIONS confirme (ou infirme) ton intuition avec des données à l'appui." },
  { title: "Apprendre en mixant", body: "Chaque fiche est aussi une leçon : tu apprends à nommer ce que tu entends et à reconnaître les patterns d'un bon mix." },
  { title: "Arrêter de douter à 3h du matin", body: "Tard le soir, l'oreille ment. VERSIONS te donne un point de repère stable quand la fatigue rend tes décisions incertaines." },
  { title: "Sortir du perfectionnisme stérile", body: "VERSIONS t'indique quand un mix est bon — pour couper le cycle infini des ajustements et oser le clore." },
];

/* HOME_TAGLINES
   Utilise des crochets [mot] pour marquer le mot-clé qui sera mis en
   orange via renderTagline(). Un seul mot par tagline pour garder
   l'accent visuel.
*/
const HOME_TAGLINES = [
  "L'[assistant] studio qui suit tes mix.",
  "La meilleure [version] de ton mix commence ici.",
  "Un avis [extérieur] disponible à toute heure.",
  "Ton oreille, mais [reposée].",
  "Le regard [neuf] que ton mix attend.",
  "Des heures de [recul], en quelques secondes.",
  "Une écoute [attentive], sans jugement.",
  "La [patience] d'un studio, la rapidité d'un clic.",
  "Pour que tes choix ne reposent plus sur la [fatigue].",
  "Un second avis, aussi [juste] qu'une nuit de sommeil.",
  "Tes versions, lues à [froid].",
  "L'assistant qui ne se [lasse] jamais de ton mix.",
  "Parce que le meilleur mix est celui qu'on a [comparé].",
  "Ton [copilote] quand tu ne sais plus si tu avances.",
  "Le point de [repère] qui manque à ta session.",
  "Pour sortir de la [boucle] V2, V3, V4 sans jamais trancher.",
  "Une écoute [professionnelle], sans louer de studio.",
  "Écouter [mieux], pas plus longtemps.",
  "Le second [souffle] de ton mix.",
  "Chaque version mérite une écoute [neuve].",
  "Le recul que l'heure [tardive] t'enlève.",
  "Quand tes oreilles saturent, l'analyse, elle, reste [fraîche].",
  "[Avance] sur ton mix sans tourner en rond.",
  "Ton studio [silencieux], à toute heure.",
  "L'oreille que tu n'as plus à la fin de la [session].",
  "Un regard qui ne [juge] pas, qui éclaire.",
  "La [clarté] qui manque quand tu as trop écouté.",
  "Le [sparring partner] de ton mix.",
  "Ce que ton oreille [fatiguée] ne te dit plus.",
  "L'écoute [stable] dont ta session manquait.",
  "[Respire] — VERSIONS écoute pour toi.",
  "Quand le doute s'installe, VERSIONS [tranche].",
  "Parce que mixer seul n'est pas mixer [aveuglément].",
  "Moins de [doutes], plus de décisions.",
  "Ton [oreille témoin], toujours fraîche.",
  "Un œil [extérieur] qui ne dort jamais.",
  "Sors du tunnel de la [V7] qui sonne comme la V3.",
  "[Trancher] ne devrait plus prendre trois soirées.",
  "L'[objectivité] que la fatigue t'enlève.",
  "Le vrai [recul], pas celui de 2h du matin.",
  "Un filet de [sécurité] avant le master.",
  "Le [calme] d'une écoute qui n'a pas d'ego.",
  "Pour avancer sans [tourner] autour du mix.",
  "Ton mix vaut une écoute [neuve] — chaque fois.",
  "L'écoute [pro] sans l'agenda d'un pro.",
  "Une oreille de plus, [patiente] et précise.",
  "Ta [boussole] quand tu ne sais plus où est le nord.",
  "[Finir] un mix, enfin.",
];

const CONSEIL_TIPS = [
  { title: "Commence simple", body: "Pas besoin d'un master commercial — même un bounce rapide depuis Logic ou Ableton suffit pour un premier tour. Tu peux aussi tester sur une référence que tu aimes pour calibrer ton oreille." },
  { title: "Trois versions suffisent", body: "Tu n'as pas besoin de 10 versions pour voir le progrès. Une V1 (brute), une V2 (retravaillée), une V3 (mix presque final) suffisent pour lire l'évolution." },
  { title: "Note chaque version", body: "Ajoute un commentaire court à chaque upload : 'passe de comp', 'EQ plus nette', 'low cut sur les claviers'. Ça te permet de relier le score aux choix que tu fais." },
  { title: "Teste la référence", body: "Balance dans VERSIONS un titre commercial que tu aimes et regarde son profil. Ça te donne un étalon réaliste pour tes propres mix." },
  { title: "Analyse à froid", body: "Uploade une version et ne lis la fiche que le lendemain. Les commentaires te sauteront aux yeux avec une oreille reposée." },
  { title: "Un titre par projet au départ", body: "Ne commence pas par uploader ton catalogue entier. Prends un seul titre, travaille-le jusqu'à comprendre la fiche, puis élargis." },
  { title: "Mix puis pause puis fiche", body: "Termine ta session, exporte, sors marcher 30 minutes, reviens lire la fiche. Ton cerveau sera beaucoup plus réceptif à la critique." },
  { title: "Ne corrige pas tout d'un coup", body: "Si la fiche signale cinq points, choisis-en un ou deux pour la version suivante. Corriger tout en bloc brouille la lecture." },
  { title: "Garde la V1 brute", body: "Ne jette jamais ton bounce initial. C'est ton zéro, ta référence. Sans lui, tu ne sauras plus si tu as vraiment progressé." },
  { title: "Importe aussi tes vieux mix", body: "Uploader un mix qui a six mois te montre ce que ton oreille a gagné depuis. Précieux pour calibrer tes exigences." },
  { title: "Lis la fiche dans l'ordre", body: "Commence par les forces, puis les points à retravailler, puis les mesures techniques. C'est ainsi que tu gardes une lecture équilibrée du mix." },
  { title: "Imprime tes fiches importantes", body: "Pour les titres clés, exporte ou garde un PDF de la fiche. Tu y reviendras avant le master pour valider les gestes finaux." },
];

/* ── EN translations of the same pools. Kept parallel to FR so the picker
   logic can select the pool matching the active language. ─────────── */
const SAVIEZ_VOUS_TIPS_EN = [
  "Taking regular breaks keeps your listening attentive and objective.",
  "Your ears fatigue after 45 min — a 10 min break saves you 2h of work.",
  "Listening to your mix in another context (car, headphones) reveals what the studio hides.",
  "Lowering the monitoring volume helps you spot balance imbalances.",
  "Stepping away from a mix for 24h completely changes your perception.",
  "Regularly comparing with a reference recalibrates your ear and your choices.",
  "Silence between sessions matters as much as the work itself.",
  "Listening at low volume is the best test: if the mix works quiet, it will work loud.",
  "The low end is easier to judge standing than sitting — you feel the sub better.",
  "Your first mix draft often contains a truth you lose when over-polishing.",
  "A mix that holds in mono almost always holds in stereo.",
  "The best compression is often the one you don't hear.",
  "Pro engineers switch speakers every 30 minutes to avoid getting used to a single color.",
  "The brain averages what it hears: after 20 minutes on a passage, you stop noticing flaws.",
  "Ear fatigue first hits the highs — that's why we tend to add top end late at night.",
  "Listening to your mix while walking reveals things a seated studio hides.",
  "A good mix should work on a phone placed on a table — that's the ultimate test.",
  "Stereo correlation tells you whether your mix collapses when summed to mono.",
  "Perceived dynamics depend on context: -10 LUFS can sound loud on streaming and weak on vinyl.",
  "A master that's too loud masks mistakes — when you turn down, they reappear.",
  "The brain loves novelty: listening to a reference before your mix makes it feel fresh again.",
  "Closed headphones exaggerate the low end, open ones exaggerate the highs. Cross-check both.",
  "The first 200 ms of a sound define its perceived impact — that's where you need to be precise.",
  "Your decisions are better in the morning — the brain is less biased by cognitive fatigue.",
  "Drinking water regularly actually improves hearing clarity.",
  "A room's acoustics shift with temperature: a heated room doesn't respond like a cold one.",
  "Pros listen on cheap speakers to catch what holds up despite them.",
  "Absolute silence doesn't exist in a studio: your noise floor is part of what you hear.",
  "A consistent monitoring level from session to session saves you from re-mixing the same things.",
  "Moving your head while listening naturally corrects your room's colorations.",
  "The kick and the lead vocal often share the same energy band: their balance makes the mix.",
];

const PROCHAIN_PAS_TIPS_EN = [
  { title: "Listen at low volume", body: "Monitoring quietly reveals balance imbalances. If the mix works quiet, it will work loud." },
  { title: "Change environments", body: "Listen in the car or on headphones: flaws invisible in the studio become obvious elsewhere." },
  { title: "Take a 24h break", body: "Come back to your mix after 24 hours — a fresh ear tells you more than 2 hours of continuous work." },
  { title: "Compare with a reference", body: "Grab a track you love, level-match it, and toggle. You'll see what's missing or too much." },
  { title: "Work in mono", body: "Flip your mix to mono to catch phase cancellations and check low-end solidity." },
  { title: "Print then forget", body: "Export, close the session, listen elsewhere later. You'll hear it like a listener." },
  { title: "Check the dynamics", body: "Look at your crest factor: too flat, the mix fatigues; too dynamic, it struggles to come through." },
  { title: "Listen in the car", body: "Car cabins reveal lingering basses and aggressive mids. A real tell-tale." },
  { title: "Solo kick + bass", body: "Solo kick and bass: their core relationship defines your mix's groove and foundation." },
  { title: "The phone test", body: "Grab your phone, set it on the table, play the mix. Most honest audience-side test." },
  { title: "Cut the low end", body: "High-pass your master at 150 Hz: what remains tells you if mids and highs hold on their own." },
  { title: "Close your eyes", body: "Listen for a minute with eyes closed, no DAW visual. You'll hear what your eyes were hiding from your ears." },
  { title: "Walk while listening", body: "Play the mix quietly and walk around — balance issues jump out." },
  { title: "Back-to-back compare", body: "Follow your mix with a commercial track. The quality 'jump' tells you exactly where to work." },
  { title: "Listen tomorrow morning", body: "Close the session, sleep on it, listen at breakfast without touching anything. Your real opinion appears there." },
  { title: "Aim for the middle", body: "Look for what works everywhere (car, headphones, speakers), not what only shines in your studio." },
  { title: "Mute the lead vocal", body: "Listen without the lead: if the instrumental doesn't hold, you were masking a problem behind the voice." },
  { title: "Re-compress your music", body: "Make a 160 kbps MP3 of your bounce and listen. Two-thirds of listeners will hear it that way." },
  { title: "Test at realistic volume", body: "Set monitoring to conversation level. If it works there, it works everywhere." },
];

const PROGRESSION_TIPS_NO_SCORE_EN = [
  "As soon as you have two versions of a track, VERSIONS compares the mixes and highlights what has changed.",
  "Import a first bounce: frequency and dynamics analysis happens in under a minute.",
  "Each track added sharpens your ear and gives you a reference for the next ones.",
  "Start by analyzing a track you know well — you'll validate the analysis against your own perception.",
  "First upload your raw V1: it serves as a reference point to see the evolution.",
  "One report per track is enough to reveal the strengths and gaps of your mix.",
  "Analyze a well-mastered track first: you'll confront your feelings with objective measurements.",
  "You can import a commercial reference — it becomes your benchmark for your own bounces.",
  "Three tracks is already enough for trends to emerge in your mix choices.",
  "Your first analyses teach you the most: each report gives you a new reference point.",
];

const PROGRESSION_TIPS_WITH_SCORE_EN = [
  "Keep comparing your versions to refine evaluation. Each analysis sharpens how you read your mix.",
  "A score doesn't judge your mix — it points out what could be improved.",
  "The score evolves with your choices: frequency balance, dynamics, stereo clarity.",
  "Watch trends rather than absolute values: two versions 5 points apart is already meaningful.",
  "If the score drops between versions, it might be the signal to go back.",
  "Add a comment to each version: you'll see which moves actually shift the score.",
  "A stable score across three versions means you're going in circles — change your angle of attack.",
  "Compare two versions side-by-side rather than judging them separately: the gap speaks louder.",
  "The score is just a reference — the detailed report tells you why.",
  "If a heavily reworked version scores lower than the previous one, trust yourself and go back.",
];

const A_QUOI_CA_SERT_TIPS_EN = [
  { title: "Analyze like a pro", body: "Each track you import goes through an objective analysis — frequency balance, dynamics, stereo, saturation — then through a detailed AI listen. You get a clear report showing what works and what doesn't." },
  { title: "Readable metrics", body: "VERSIONS translates technical measurements (LUFS, crest factor, stereo correlation) into understandable comments. No need to be a sound engineer to know what to adjust." },
  { title: "A reliable second opinion", body: "The AI listen comments on your mix section by section and points out where your ears may have fatigued. An outside perspective, available anytime." },
  { title: "A clear report", body: "Each analysis delivers a structured report: strengths, points to rework, levels, dynamics. No interpretation, just reference points." },
  { title: "Spot ear fatigue", body: "VERSIONS listens with the same freshness at any hour. It detects what your ears overcompensate for after several hours of work." },
  { title: "Break the V2/V3 loop", body: "When torn between two versions, comparative analysis decides objectively: you know which holds up better, and why." },
  { title: "An auditory logbook", body: "Every analysis is archived and dated. You can look back, see a track's evolution, and spot when a mix started to drift." },
  { title: "An ego-less perspective", body: "VERSIONS has no interest in flattering your mix or tearing it apart. It tells you what is, with the right words." },
  { title: "Prepare for mastering", body: "Before sending your mix to mastering, VERSIONS flags what to clean up. You arrive at the studio with a clean mix." },
  { title: "Save hours of listening", body: "A 2-minute analysis replaces dozens of trips between speakers, headphones, and car. You target what's wrong, fast." },
];

const POURQUOI_VERSIONS_TIPS_EN = [
  { title: "Compare your mixes", body: "Upload multiple versions of the same track: VERSIONS highlights what has progressed, what has regressed, and where to rework." },
  { title: "See progress", body: "Each version is dated and scored. At a glance, you see if V3 is actually better than V2 — or if you should go back." },
  { title: "Undo misleading intuition", body: "Sometimes you think a mix is improving when it's losing clarity. VERSIONS objectifies this feeling and gives you stable reference points." },
  { title: "Keep the history", body: "Your versions stay organized by project and dated. No more hunting for 'the last good one' in a folder." },
  { title: "A pocket studio", body: "The perspective of a sound engineer, available anytime, no appointment, no studio cost, no waiting for feedback." },
  { title: "Break home-studio loneliness", body: "Mixing alone, you lose the outside eye. VERSIONS fills that role — a tangible, backed opinion that renews your listening." },
  { title: "Confirm your choices", body: "Sometimes you know what's off but can't decide. VERSIONS confirms (or disproves) your intuition with data to back it up." },
  { title: "Learn while mixing", body: "Each report is also a lesson: you learn to name what you hear and recognize the patterns of a good mix." },
  { title: "Stop doubting at 3 AM", body: "Late at night, your ears lie. VERSIONS gives you a stable reference point when fatigue blurs your decisions." },
  { title: "Escape sterile perfectionism", body: "VERSIONS tells you when a mix is good — to break the endless cycle of adjustments and finally close it." },
];

const HOME_TAGLINES_EN = [
  "The studio [assistant] that follows your mixes.",
  "The best [version] of your mix starts here.",
  "An outside [opinion], available anytime.",
  "Your ear, but [rested].",
  "The [fresh] take your mix is waiting for.",
  "Hours of [distance], in seconds.",
  "An [attentive] listen, free of judgment.",
  "A studio's [patience], a click's speed.",
  "So your choices no longer rest on [fatigue].",
  "A second opinion, as [fair] as a night's sleep.",
  "Your versions, read [cold].",
  "The assistant that never gets [tired] of your mix.",
  "Because the best mix is the one that's been [compared].",
  "Your [copilot] when you're unsure you're moving forward.",
  "The [reference] point your session is missing.",
  "To escape the V2, V3, V4 [loop] without ever deciding.",
  "A [professional] listen, without renting a studio.",
  "Listen [better], not longer.",
  "The second [wind] of your mix.",
  "Every version deserves a [fresh] listen.",
  "The distance the [late] hour takes from you.",
  "When your ears saturate, the analysis stays [fresh].",
  "[Move forward] on your mix without going in circles.",
  "Your [silent] studio, at any hour.",
  "The ear you've lost by the end of the [session].",
  "A perspective that doesn't [judge], that illuminates.",
  "The [clarity] missing when you've listened too long.",
  "The [sparring partner] for your mix.",
  "What your [tired] ear no longer tells you.",
  "The [stable] listen your session was missing.",
  "[Breathe] — VERSIONS listens for you.",
  "When doubt sets in, VERSIONS [decides].",
  "Because mixing alone isn't mixing [blindly].",
  "Less [doubt], more decisions.",
  "Your [witness ear], always fresh.",
  "An [outside] eye that never sleeps.",
  "Leave the tunnel where [V7] sounds like V3.",
  "[Deciding] shouldn't take three evenings anymore.",
  "The [objectivity] that fatigue takes from you.",
  "Real [distance], not 2 AM distance.",
  "A [safety] net before mastering.",
  "The [calm] of a listen with no ego.",
  "To move forward without [circling] the mix.",
  "Your mix deserves a [fresh] listen — every time.",
  "A [pro] listen without a pro's calendar.",
  "One more ear, [patient] and precise.",
  "Your [compass] when you've lost north.",
  "[Finish] a mix, at last.",
];

const CONSEIL_TIPS_EN = [
  { title: "Start simple", body: "No need for a commercial master — even a quick bounce from Logic or Ableton works for a first pass. You can also test on a reference you love to calibrate your ear." },
  { title: "Three versions are enough", body: "You don't need 10 versions to see progress. A V1 (raw), V2 (reworked), V3 (near-final mix) is enough to read the evolution." },
  { title: "Label each version", body: "Add a short comment on each upload: 'comp pass', 'cleaner EQ', 'low cut on keys'. It ties the score to the choices you make." },
  { title: "Test the reference", body: "Drop a commercial track you love into VERSIONS and look at its profile. It gives you a realistic benchmark for your own mixes." },
  { title: "Analyze cold", body: "Upload a version and read the report the next day. The comments will jump out with a rested ear." },
  { title: "One track per project at first", body: "Don't upload your whole catalog. Take one track, work it until you understand the report, then expand." },
  { title: "Mix then pause then report", body: "Finish your session, export, go walk 30 minutes, then read the report. Your brain will be more open to critique." },
  { title: "Don't fix everything at once", body: "If the report flags five points, pick one or two for the next version. Fixing everything at once blurs the read." },
  { title: "Keep the raw V1", body: "Never throw away your initial bounce. It's your zero, your reference. Without it, you won't know if you've really progressed." },
  { title: "Import your old mixes too", body: "Uploading a mix from six months ago shows what your ear has gained since. Valuable for calibrating your standards." },
  { title: "Read the report in order", body: "Start with strengths, then points to rework, then technical measurements. That way you keep a balanced read of the mix." },
  { title: "Print your key reports", body: "For important tracks, export or save a PDF of the report. You'll come back to it before mastering to validate final moves." },
];

function pickTip(pool, storageKey) {
  if (!pool?.length) return null;
  let lastIdx = -1;
  try {
    const raw = localStorage.getItem(storageKey);
    if (raw != null) lastIdx = Number(raw);
  } catch { /* ignore */ }
  let next;
  if (pool.length <= 1) {
    next = 0;
  } else {
    do { next = Math.floor(Math.random() * pool.length); } while (next === lastIdx);
  }
  try { localStorage.setItem(storageKey, String(next)); } catch { /* ignore */ }
  return pool[next];
}

/* renderTagline(text)
   Parse les marqueurs [mot] dans une tagline et retourne un tableau
   de nœuds React où les parties entre crochets sont colorées en orange
   (var(--amber)). Le reste est rendu tel quel en blanc.
*/
function renderTagline(text) {
  if (!text) return null;
  const parts = text.split(/\[(.+?)\]/);
  return parts.map((part, i) =>
    i % 2 === 1
      ? <span key={i} style={{ color: 'var(--amber)' }}>{part}</span>
      : part
  );
}

/* ── Hero waveform ────────────────────────────────────────
   WaveSurfer attachée au même HTMLAudioElement (audioPool) que le
   BottomPlayer : play/pause/seek sont synchrones entre les deux vues,
   sans double décodage coûteux puisque WaveSurfer partage le media.
*/
function HeroWaveform({ storagePath, isActive, resetKey = 0, onFinish }) {
  const containerRef = useRef(null);
  const wsRef = useRef(null);
  const audioRef = useRef(null);
  const rafRef = useRef(null);
  const lastPathRef = useRef(null);
  // Ref miroir pour lire l'état le plus récent d'isActive depuis l'async loader
  // (évite de devoir re-déclencher l'effet à chaque toggle play/pause)
  const isActiveRef = useRef(isActive);
  useEffect(() => { isActiveRef.current = isActive; }, [isActive]);
  // Ref miroir pour onFinish : permet d'attacher un seul listener 'ended' par
  // audio sans devoir re-binder quand la prop change.
  const onFinishRef = useRef(onFinish);
  useEffect(() => { onFinishRef.current = onFinish; }, [onFinish]);

  // Charge audio + WaveSurfer. Sur changement de storagePath OU resetKey
  // (relance du même titre), on remet currentTime=0 et on lance si actif.
  // Sur la home, le BottomPlayer est masqué : c'est ici qu'on pilote le play().
  useEffect(() => {
    if (!storagePath || !containerRef.current) return;

    let cancelled = false;
    let endedCleanup = null;
    (async () => {
      try {
        const audio = await resolveAudio(storagePath);
        if (cancelled || !containerRef.current) return;

        const samePath = lastPathRef.current === storagePath;
        const prev = audioRef.current;
        if (prev && prev !== audio) {
          try { prev.pause(); } catch { /* noop */ }
        }

        // Toujours reset au début quand la clé change (nouveau titre ou replay)
        try { audio.currentTime = 0; } catch { /* noop */ }

        if (!samePath || !wsRef.current) {
          if (wsRef.current) {
            try { wsRef.current.destroy(); } catch { /* noop */ }
            wsRef.current = null;
          }
          const ws = WaveSurfer.create({
            container: containerRef.current,
            waveColor: 'rgba(255,255,255,0.18)',
            progressColor: '#f5b056',
            cursorColor: 'rgba(245,176,86,0.85)',
            cursorWidth: 1,
            barWidth: 2,
            barGap: 2,
            barRadius: 2,
            height: 56,
            normalize: true,
            interact: true,
            media: audio,
          });
          wsRef.current = ws;
        }
        audioRef.current = audio;
        lastPathRef.current = storagePath;

        // Écoute la fin du titre pour enchaîner la playlist sur la home
        // (le BottomPlayer est masqué ici, donc son propre handler 'finish'
        // ne tire pas — on pilote l'auto-advance depuis le hero).
        const handleEnded = () => {
          const cb = onFinishRef.current;
          if (typeof cb === 'function') cb();
        };
        audio.addEventListener('ended', handleEnded);
        endedCleanup = () => {
          try { audio.removeEventListener('ended', handleEnded); } catch { /* noop */ }
        };

        // Si on doit être en lecture (heroIsPlaying), on lance maintenant
        if (isActiveRef.current) {
          try { await audio.play(); } catch { /* autoplay bloqué, user-gesture requis */ }
        }
      } catch (err) {
        console.warn('[hero wave] load error:', err?.message || err);
      }
    })();

    return () => {
      cancelled = true;
      if (endedCleanup) endedCleanup();
    };
  }, [storagePath, resetKey]);

  // Sync isActive → play/pause sur l'audio actif (toggle sans reload)
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isActive && audio.paused) {
      const p = audio.play();
      if (p && typeof p.catch === 'function') p.catch(() => { /* noop */ });
    } else if (!isActive && !audio.paused) {
      try { audio.pause(); } catch { /* noop */ }
    }
  }, [isActive]);

  // Sync manuel du curseur : on manipule directement le shadow DOM de WaveSurfer
  // (contourne renderer.renderProgress qui peut être minifié/indisponible en prod).
  // Coût : ~3 writes DOM par frame, négligeable.
  useEffect(() => {
    const tick = () => {
      const hero = containerRef.current;
      const holder = hero?.firstElementChild;
      const shadow = holder?.shadowRoot;
      const ws = wsRef.current;
      const audio = (ws && typeof ws.getMediaElement === 'function')
        ? ws.getMediaElement()
        : audioRef.current;
      if (shadow && audio && audio.duration) {
        const ratio = Math.max(0, Math.min(1, audio.currentTime / audio.duration));
        const pct = ratio * 100;
        const cursor = shadow.querySelector('.cursor');
        const progress = shadow.querySelector('.progress');
        const canvases = shadow.querySelector('.canvases');
        if (cursor) cursor.style.left = `${pct}%`;
        if (progress) progress.style.width = `${pct}%`;
        if (canvases) {
          canvases.style.clipPath = `polygon(${pct}% 0%, 100% 0%, 100% 100%, ${pct}% 100%)`;
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, []);

  // Nettoie l'instance quand le composant disparaît (changement d'écran)
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        try { wsRef.current.destroy(); } catch { /* noop */ }
        wsRef.current = null;
        lastPathRef.current = null;
      }
      audioRef.current = null;
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="wh-hero-wave"
      style={{ opacity: isActive ? 1 : 0.75 }}
      aria-label="Forme d'onde du titre"
    />
  );
}

/**
 * DashboardRail — rail utilitaire flottant bas-gauche (pill crédits +
 * picto Réglages + picto Déconnexion). Rendu sur le dashboard ET sur
 * la landing quand l'utilisateur est connecté, pour qu'il ait toujours
 * accès rapide à son solde + ses actions de compte.
 * CSS vit dans MockupStyles (.db-utility-*).
 */
function DashboardRail({ credits, onGoPricing, onGoReglages, onSignOut, onGoAdmin, onGoFeedback, user }) {
  const { s } = useLang();
  // Admin : gating piloté par la base (table public.admin_users via la
  // RPC is_admin(), cf. hooks/useIsAdmin). Permet d'atteindre #/admin en
  // un clic depuis n'importe quel écran qui rend le DashboardRail
  // (home, pricing, dashboard).
  const isAdmin = useIsAdmin(user);
  return (
    <div className="db-utility-rail" aria-label="Outils du compte">
      {/* Feedback testeur — phase beta. Pill complète (icône + label
          visible) avec animation pulse + wiggle périodique pour attirer
          l'œil. Posée sur SA PROPRE LIGNE au-dessus de la rangée
          crédits/admin/réglages/déconnexion (le rail est en
          flex-direction: column). Pré-positionnée tout en haut pour
          rester ce qui ressort en premier visuellement. */}
      {onGoFeedback && (
        <button
          type="button"
          className="db-utility-feedback"
          onClick={onGoFeedback}
          aria-label={s.feedback?.triggerTitle || 'Donne ton avis sur Versions'}
          title={s.feedback?.triggerTitle || 'Donne ton avis sur Versions'}
        >
          <svg className="db-utility-feedback-icon" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="none" aria-hidden="true">
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
          </svg>
          <span>{s.feedback?.triggerLabel || 'Ton avis compte'}</span>
        </button>
      )}
      {/* Ligne crédits + utilitaires (admin / réglages / déconnexion).
          Sortie dans son propre wrapper pour rester en flex row tandis
          que le rail englobant est en column (cf. .db-utility-row CSS). */}
      <div className="db-utility-row">
        {credits != null && (
          <button
            type="button"
            className="db-utility-credits"
            onClick={onGoPricing}
            title="Acheter des crédits"
          >
            {credits === 1
              ? (s.sidebar?.creditsSingular || '1 crédit')
              : (s.sidebar?.creditsPlural || '{count} crédits').replace('{count}', String(credits))}
          </button>
        )}
        {isAdmin && onGoAdmin && (
          <button
            type="button"
            className="db-utility-btn db-utility-btn-admin"
            onClick={onGoAdmin}
            aria-label="Admin — coûts"
            title="Admin — coûts"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 2l3 7h7l-5.5 4 2 8L12 17l-6.5 4 2-8L2 9h7z" />
            </svg>
          </button>
        )}
        <button
          type="button"
          className="db-utility-btn"
          onClick={onGoReglages}
          aria-label={s.sidebar?.reglages || 'Réglages'}
          title={s.sidebar?.reglages || 'Réglages'}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="3"/>
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
          </svg>
        </button>
        <button
          type="button"
          className="db-utility-btn"
          onClick={onSignOut}
          aria-label={s.sidebar?.signOut || 'Se déconnecter'}
          title={s.sidebar?.signOut || 'Se déconnecter'}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
            <polyline points="16 17 21 12 16 7"/>
            <line x1="21" y1="12" x2="9" y2="12"/>
          </svg>
        </button>
      </div>
    </div>
  );
}

/**
 * DashboardTopbar — topbar desktop pour les écrans authentifiés sans
 * sidebar (à ce jour : welcome). Calé sur le topbar de la landing /
 * pricing pour garder une identité visuelle homogène d'un écran à
 * l'autre. Logo + wordmark à gauche (clic → landing publique), nav à
 * droite avec liens principaux, accès Réglages, déconnexion, FR/EN.
 *
 * CSS inline pour rester encapsulé. Si on déploie ce topbar sur d'autres
 * écrans (admin, versions...), ça vaudra le coup d'extraire le CSS dans
 * MockupStyles ou un fichier shared.
 */
function DashboardTopbar({ currentScreen, onGoLanding, onGoDashboard, onGoPricing, onGoPlugin, onGoReglages, onSignOut, onGoAdmin, onGoFeedback, user, lang, setLang, credits, planLabel = null }) {
  const { s } = useLang();
  // Admin : gating piloté par la base (table public.admin_users via la
  // RPC is_admin(), cf. hooks/useIsAdmin). Permet d'atteindre #/admin en
  // un clic depuis le menu hamburger sur tous les écrans en topbar layout.
  const isAdmin = useIsAdmin(user);
  const utilityItems = [
    ...(isAdmin && onGoAdmin ? [{ key: 'admin', label: 'Admin', icon: NavIcons.admin, onSelect: onGoAdmin }] : []),
    ...(onGoFeedback ? [{ key: 'feedback', label: s.feedback?.triggerLabel || 'Ton avis compte', icon: NavIcons.feedback, onSelect: onGoFeedback }] : []),
    ...(onGoReglages ? [{ key: 'reglages', label: s.sidebar?.reglages || 'Réglages', icon: NavIcons.settings, onSelect: onGoReglages }] : []),
    ...(onSignOut ? [{ key: 'signout', label: s.sidebar?.signOut || 'Se déconnecter', icon: NavIcons.signOut, onSelect: onSignOut, danger: true }] : []),
  ];
  return (
    <>
      {/* Orbes colorées : déplacées vers le layer global .va-bg-orbs
          (monté côté body dans App.jsx). Continu d'une page à l'autre,
          même cycle d'animation, même teintes. */}

      <header className="db-topbar">
        <button
          type="button"
          className="db-topbar-brand"
          onClick={onGoLanding}
          aria-label={s.sidebar?.aboutLink || 'Accueil'}
        >
          <img src="/Logo-Versions-2.png" alt="" className="db-topbar-logo" />
          <span className="brand-col">
            <span className="db-topbar-wordmark">
              VER<span className="accent">Si</span>ONS
            </span>
            <BrandByline />
          </span>
        </button>
        {/* Slot réservé pour des contenus contextuels rendus via portail
            (ex: les chips de versions sur la fiche, montés depuis
            FicheScreen via ReactDOM.createPortal sur cet id). Rien à
            afficher tant qu'aucun écran ne pousse de contenu. */}
        <div id="topbar-context-slot" className="db-topbar-slot" />
        <nav className="db-topbar-nav" aria-label="Navigation">
          {/* Desktop : nav texte (Accueil/Tarifs/Tableau de bord). Mobile :
              hamburger via media query. Lang dropdown + hamburger
              accessibles partout (mobile + desktop). */}
          <button type="button" className="db-topbar-link db-topbar-link-home" onClick={onGoLanding}>
            {s.pricing?.topbarHome || 'Accueil'}
          </button>
          {/* Lien Plugin + point notif — même ordre que la topbar de la
              landing (Accueil · Plugin · Tarifs), pour que le plugin soit
              découvrable depuis tous les écrans (demande David 2026-07-05). */}
          {onGoPlugin && (
            <button type="button" className="db-topbar-link db-topbar-plugin" onClick={onGoPlugin}>
              {s.landing?.pluginNav || 'Plugin'}
              <span className="db-new-dot" aria-hidden="true" />
            </button>
          )}
          <button type="button" className="db-topbar-link" onClick={onGoPricing}>
            {s.pricing?.topbarCurrent || 'Tarifs'}
          </button>
          {currentScreen === 'welcome' ? (
            <span className="db-topbar-current" aria-current="page">
              {s.sidebar?.dashboardLink || 'Tableau de bord'}
            </span>
          ) : (
            <button type="button" className="db-topbar-link" onClick={onGoDashboard}>
              {s.sidebar?.dashboardLink || 'Tableau de bord'}
            </button>
          )}
          {/* Hamburger — visible uniquement en mobile (CSS gating).
              Contient le même nav + utilitaires (admin/réglages/signout)
              + footer crédits/abonnement. */}
          <HamburgerMenu
            items={[
              { key: 'home', label: s.pricing?.topbarHome || 'Accueil', icon: NavIcons.home, onSelect: onGoLanding },
              ...(onGoPlugin ? [{ key: 'plugin', label: s.landing?.pluginNav || 'Plugin', icon: NavIcons.plugin, onSelect: onGoPlugin }] : []),
              { key: 'pricing', label: s.pricing?.topbarCurrent || 'Tarifs', icon: NavIcons.pricing, onSelect: onGoPricing },
              {
                key: 'dashboard',
                label: s.sidebar?.dashboardLink || 'Tableau de bord',
                icon: NavIcons.dashboard,
                current: currentScreen === 'welcome',
                onSelect: currentScreen !== 'welcome' ? onGoDashboard : undefined,
              },
            ]}
            utilityItems={utilityItems}
            credits={credits}
            planLabel={planLabel}
            onPlanClick={onGoPricing}
          />
          <LangDropdown lang={lang} setLang={setLang} />
        </nav>
      </header>

      {/* Rail utilitaire bas-gauche — extrait dans <DashboardRail/>
          pour pouvoir le réutiliser sur la landing connectée. */}
      <DashboardRail
        credits={credits}
        onGoPricing={onGoPricing}
        onGoReglages={onGoReglages}
        onSignOut={onSignOut}
        onGoAdmin={onGoAdmin}
        onGoFeedback={onGoFeedback}
        user={user}
      />

      <style>{`
        /* DASHBOARD TOPBAR — calé sur .lp-topbar / .pr-topbar (logo 38,
           wordmark 27, padding 22/18, nav pill mono). Ajout d'un lien
           "soft" pour la déconnexion (typo plus discrète, distance des
           liens nav). */
        .db-topbar {
          position: relative; z-index: 2;
          padding: 22px 18px;
          display: flex; align-items: center; justify-content: space-between;
          gap: 24px;
        }
        .db-topbar-brand {
          display: inline-flex; align-items: center; gap: 8px;
          background: transparent; border: 0; cursor: pointer;
          padding: 0; border-radius: 8px;
          transition: opacity .15s;
          flex-shrink: 0;
        }
        .db-topbar-brand:hover { opacity: 0.82; }
        /* Slot contextuel — prend la place disponible entre brand et nav.
           Permet aux écrans (ex: FicheScreen) d'injecter des contrôles
           via portail React. Refonte 2026-04-30 : aligné avec le début
           de la colonne de contenu (920 px centré) — le padding-left
           calc pousse le contenu à la même abscisse que la colonne
           visible en dessous. ~228 px = brand + padding + gap. */
        .db-topbar-slot {
          flex: 1;
          display: inline-flex; align-items: center; justify-content: flex-start;
          gap: 12px; min-width: 0;
          padding-left: max(16px, calc((100vw - 920px) / 2 - 204px));
          padding-right: 16px;
          overflow: hidden;
        }
        .db-topbar-slot:empty { padding: 0; }
        /* Conteneur des contrôles fiche (portail) — réunit eyebrow,
           dropdown versions, [BPM/LUFS + genre stackés], actions, sur
           une rangée dans la topbar globale. */
        .fiche-topbar-portal {
          display: inline-flex; align-items: center;
          gap: 12px;
          flex-wrap: nowrap;
          min-width: 0;
        }
        /* Colonne BPM + Genre — DspBadge en haut, ligne genre en dessous */
        .fiche-topbar-meta-col {
          display: inline-flex; flex-direction: column;
          align-items: flex-start;
          gap: 4px;
          min-width: 0;
        }
        .fiche-topbar-genre-row {
          display: inline-flex; align-items: center;
          padding: 0 4px;
          line-height: 1;
        }
        /* Sur narrow on autorise le wrap pour ne pas tout pousser hors écran */
        @media (max-width: 1100px) {
          .fiche-topbar-portal { gap: 8px; flex-wrap: wrap; justify-content: center; }
        }
        .db-topbar-logo { height: 38px; width: auto; filter: drop-shadow(0 0 16px rgba(245,166,35,0.18)); }
        .db-topbar-wordmark { font-family: 'Fredoka', var(--body); font-weight: 700; font-size: 27px; letter-spacing: 0.5px; color: var(--text); line-height: 1; }
        .db-topbar-wordmark .accent { color: var(--amber); font-style: normal; }
        .db-topbar-nav { display: inline-flex; align-items: center; gap: 8px; flex-wrap: wrap; justify-content: flex-end; }
        .db-topbar-link, .db-topbar-current {
          font-family: var(--mono); font-size: 11px; font-weight: 500;
          letter-spacing: 1.6px; text-transform: uppercase;
          padding: 9px 16px; border-radius: 999px;
          transition: all .15s;
        }
        .db-topbar-link {
          background: transparent; color: var(--text-soft, var(--muted));
          border: 1px solid transparent; cursor: pointer;
        }
        /* Lien Plugin — point notif ambre (même grammaire que .lp-new-dot
           sur la landing). */
        .db-topbar-plugin { position: relative; }
        .db-new-dot {
          position: absolute; top: 6px; right: 8px;
          width: 5px; height: 5px; border-radius: 50%;
          background: var(--amber);
          box-shadow: 0 0 6px rgba(245,166,35,0.8);
          pointer-events: none;
        }
        .db-topbar-link:hover {
          color: var(--text); background: rgba(255,255,255,0.04);
          border-color: rgba(255,255,255,0.10);
        }
        .db-topbar-current {
          color: var(--amber);
          background: rgba(245,166,35,0.06);
          border: 1px solid rgba(245,166,35,0.32);
        }

        /* CSS du rail (.db-utility-*) déplacé dans MockupStyles
           (chargé global) pour qu'il fonctionne aussi quand on rend
           DashboardRail seul, en dehors de DashboardTopbar (ex: sur
           la landing pour les utilisateurs connectés). */
        /* Mobile/responsive — la topbar mobile est gérée par MobileMenu,
           mais si jamais ce topbar s'affiche sur tablette intermédiaire,
           on raccourcit. */
        @media (max-width: 720px) {
          .db-topbar { padding: 16px 14px; gap: 12px; }
          .db-topbar-wordmark { font-size: 22px; }
          .db-topbar-logo { height: 28px; }
          .db-topbar-nav { gap: 6px; }
          .db-topbar-link, .db-topbar-current {
            font-size: 10px; letter-spacing: 1.2px;
            padding: 7px 11px;
          }
          /* Sur narrow on masque "Accueil" (logo cliquable s'en charge)
             et le badge "Tableau de bord" current (on est déjà sur
             cette page). Reste sur mobile : Tarifs + FR/EN —
             strictement aligné sur le pattern pricing mobile. */
          .db-topbar-link-home,
          .db-topbar-nav > .db-topbar-current {
            display: none;
          }
        }
        /* Mobile gating est géré globalement dans MockupStyles
           (.hb-menu visible uniquement <768px, .db-topbar-link/current
           cachés en parallèle). Plus de règle locale ici. */
      `}</style>
    </>
  );
}

/**
 * AddPill — pill flottante "Ajouter" côté gauche, miroir statique du
 * .chat-pill (qui flotte à droite avec animation peek). Différences :
 *  - posée à gauche (.add-pill-wrap)
 *  - PAS d'animation auto (pas de cycle qui s'ouvre tout seul) — David :
 *    "il ne s'anime pas tout seul si on ne le survole pas"
 *  - hover/focus : la pill s'étend pour révéler le label "Ajouter" + un
 *    sous-titre type "Projet, titre, version…"
 *  - click : ouvre la modale AddModal (qui se charge ensuite du flow
 *    projet / titre / version)
 *
 * Visibilité gérée par le parent (gating sur user + !isMinimalScreen).
 * Quand la modale est ouverte, on toggle body.add-modal-open pour cacher
 * la pill (parité avec body.chat-open .chat-pill).
 */
function AddPill({ onOpen, isOpen, layoutWidth = 'fiche', label = 'Ajouter', placeholder = 'Projet, titre, version…' }) {
  // Sync body class — permet au CSS de cacher la pill quand la modale
  // est ouverte (par .add-modal-open .add-pill { opacity: 0; ... }).
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (isOpen) document.body.classList.add('add-modal-open');
    else document.body.classList.remove('add-modal-open');
    return () => { document.body.classList.remove('add-modal-open'); };
  }, [isOpen]);

  // Sync body class pour la largeur de contenu — pilote --app-content-w
  // via CSS (cf. .add-pill-wrap qui calcule sa largeur sur cette var).
  // Fiche = 920px, dashboard = 1080px (cf. maxWidth dans App.jsx).
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.body.classList.remove('app-layout-fiche', 'app-layout-dashboard');
    document.body.classList.add(`app-layout-${layoutWidth}`);
    return () => {
      document.body.classList.remove('app-layout-fiche', 'app-layout-dashboard');
    };
  }, [layoutWidth]);

  return (
    <div className="add-pill-wrap" aria-hidden={isOpen ? 'true' : 'false'}>
      <button
        type="button"
        className="add-pill"
        onClick={onOpen}
        aria-label={label}
        title={label}
      >
        <span className="add-pill-icon" aria-hidden="true">
          {/* Plus 16×16 — symbole d'ajout standard, simple et lisible. */}
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </span>
        {/* Label "Ajouter" toujours visible (état compact) — l'icône
            seule n'était pas assez explicite. Au hover, le placeholder
            (sous-titre) apparaît à côté pour préciser le scope. */}
        <span className="add-pill-label">{label}</span>
        <span className="add-pill-placeholder">{placeholder}</span>
        <span className="add-pill-cta" aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
            <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </button>
    </div>
  );
}

/* ─── Recherche dashboard (projets + titres + versions) ────────────────
   Champ de recherche live en tête du panneau projets. Suggère projets et
   titres dès la 1ère lettre, dropdown avec navigation clavier (↑↓ / Entrée
   / Échap). Insensible aux accents et à la casse (norm() décompose NFD et
   retire les diacritiques). Clic sur un titre → dernière version ; clic sur
   une chip V1/V2 → cette version précise. */
function WhSearch({ projects = [], allTracks = [], onOpenProject, onOpenTrack, onOpenVersion }) {
  const { s } = useLang();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const wrapRef = useRef(null);

  const norm = (str) => (str || '').toString().toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '').trim();
  const q = norm(query);

  const projectHits = q
    ? projects.filter((p) => norm(p.name).includes(q)).slice(0, 6)
    : [];
  const trackHits = q
    ? allTracks
        .filter((t) => norm(t.title).includes(q) || norm(t._projectName).includes(q))
        .slice(0, 12)
    : [];
  // Liste à plat pour la navigation clavier (projets puis titres).
  const flat = [
    ...projectHits.map((p) => ({ type: 'project', item: p })),
    ...trackHits.map((t) => ({ type: 'track', item: t })),
  ];
  const showDrop = open && q.length > 0;

  // Reset de l'index actif quand la requête change.
  useEffect(() => { setActive(0); }, [query]);

  // Fermeture au clic en dehors du wrapper.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const close = () => { setOpen(false); setQuery(''); };
  const pickProject = (p) => { close(); onOpenProject?.(p); };
  const pickTrack = (t) => { close(); onOpenTrack?.(t); };
  const pickVersion = (t, v) => { close(); onOpenVersion?.(t, v); };

  const activate = (idx) => {
    const r = flat[idx];
    if (!r) return;
    if (r.type === 'project') pickProject(r.item);
    else pickTrack(r.item);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { setOpen(false); e.currentTarget.blur(); return; }
    if (!showDrop || flat.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => (i + 1) % flat.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => (i - 1 + flat.length) % flat.length); }
    else if (e.key === 'Enter') { e.preventDefault(); activate(active); }
  };

  // Surligne la portion qui matche, en se basant sur la chaîne normalisée.
  // Garde-fou : on ne surligne que si la longueur normalisée == originale
  // (vrai pour les accents latins courants ; évite un offset faux sur les
  // ligatures rares type œ/æ).
  const renderHL = (text) => {
    const t = text || '';
    const n = norm(t);
    const idx = q ? n.indexOf(q) : -1;
    if (idx < 0 || n.length !== t.length) return t;
    return (
      <>{t.slice(0, idx)}<mark className="wh-search-mark">{t.slice(idx, idx + q.length)}</mark>{t.slice(idx + q.length)}</>
    );
  };

  return (
    <div className="wh-search" ref={wrapRef}>
      <div className="wh-search-field">
        <svg className="wh-search-ic" width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
          <circle cx="7" cy="7" r="5" /><path d="M11 11l3.5 3.5" />
        </svg>
        <input
          className="wh-search-input"
          type="text"
          value={query}
          placeholder={s.home.searchPlaceholder}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          aria-label={s.home.searchPlaceholder}
          autoComplete="off"
          spellCheck="false"
        />
        {query && (
          <button
            type="button"
            className="wh-search-clear"
            onClick={() => { setQuery(''); setOpen(false); }}
            aria-label={s.home.searchClear}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
              <path d="M2.5 2.5l7 7M9.5 2.5l-7 7" />
            </svg>
          </button>
        )}
      </div>

      {showDrop && (
        <div className="wh-search-drop" role="listbox">
          {flat.length === 0 ? (
            <div className="wh-search-empty">{s.home.searchNoResults}</div>
          ) : (
            <>
              {projectHits.length > 0 && (
                <div className="wh-search-group">{s.home.searchProjectsLabel}</div>
              )}
              {projectHits.map((p) => {
                const fi = flat.findIndex((r) => r.type === 'project' && r.item.id === p.id);
                const nTracks = p.tracks?.length || 0;
                return (
                  <button
                    type="button"
                    key={'p' + p.id}
                    className={`wh-search-row${active === fi ? ' active' : ''}`}
                    role="option"
                    aria-selected={active === fi}
                    onMouseEnter={() => setActive(fi)}
                    onClick={() => pickProject(p)}
                  >
                    <span className="wh-search-row-ic project" aria-hidden="true">
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1.8 4.2a1 1 0 0 1 1-1h3.1l1.4 1.6h5.1a1 1 0 0 1 1 1v5.4a1 1 0 0 1-1 1H2.8a1 1 0 0 1-1-1z" />
                      </svg>
                    </span>
                    <span className="wh-search-row-main">
                      <span className="wh-search-row-title">{renderHL(p.name)}</span>
                      <span className="wh-search-row-sub">
                        {nTracks} {nTracks > 1 ? s.home.trackPlural : s.home.trackSingular}
                      </span>
                    </span>
                  </button>
                );
              })}

              {trackHits.length > 0 && (
                <div className="wh-search-group">{s.home.searchTracksLabel}</div>
              )}
              {trackHits.map((t) => {
                const fi = flat.findIndex((r) => r.type === 'track' && r.item.id === t.id);
                const versions = t.versions || [];
                return (
                  <div
                    key={'t' + t.id}
                    className={`wh-search-row track${active === fi ? ' active' : ''}`}
                    role="option"
                    aria-selected={active === fi}
                    onMouseEnter={() => setActive(fi)}
                  >
                    <button
                      type="button"
                      className="wh-search-row-open"
                      onClick={() => pickTrack(t)}
                    >
                      <span className="wh-search-row-ic" aria-hidden="true">
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
                          <path d="M11 1.5v7.2a2.3 2.3 0 1 1-1.2-2V3.8L5.2 5v5.7a2.3 2.3 0 1 1-1.2-2V3.8z" />
                        </svg>
                      </span>
                      <span className="wh-search-row-main">
                        <span className="wh-search-row-title">{renderHL(t.title)}</span>
                        <span className="wh-search-row-sub">{t._projectName}</span>
                      </span>
                    </button>
                    {versions.length > 0 && (
                      <span className="wh-search-vers">
                        {versions.map((v) => (
                          <button
                            type="button"
                            key={v.id}
                            className="wh-search-vchip"
                            title={`${t.title} — ${v.name}`}
                            onClick={(e) => { e.stopPropagation(); pickVersion(t, v); }}
                          >
                            {v.name}
                          </button>
                        ))}
                      </span>
                    )}
                  </div>
                );
              })}

              <div className="wh-search-hint">{s.home.searchHint}</div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function WelcomeHome({ userProfile, currentProjectId, onSetCurrentProject, onNewTrack, onAddVersion, onAnalyze, onSelectVersion, onOpenFiche, onPlay, onToggle, onNext, playerState, projects = [], projectsLoaded = false, onMutate, addModalOpen, setAddModalOpen, addModalCtx = null, setAddModalCtx }) {
  const { lang, s } = useLang();
  const pool = (fr, en) => (lang === 'en' ? en : fr);
  // Rotation des conseils : un tip distinct à chaque ouverture, sans répétition consécutive
  // useMemo + dep [lang] → re-pick dans la pool de la langue active à chaque switch FR/EN
  const tip = useMemo(() => pickTip(pool(SAVIEZ_VOUS_TIPS, SAVIEZ_VOUS_TIPS_EN), 'versions_tip_saviez'), [lang]);
  const prochainPasTip = useMemo(() => pickTip(pool(PROCHAIN_PAS_TIPS, PROCHAIN_PAS_TIPS_EN), 'versions_tip_prochain'), [lang]);
  const aQuoiTip = useMemo(() => pickTip(pool(A_QUOI_CA_SERT_TIPS, A_QUOI_CA_SERT_TIPS_EN), 'versions_tip_aquoi'), [lang]);
  const pourquoiTip = useMemo(() => pickTip(pool(POURQUOI_VERSIONS_TIPS, POURQUOI_VERSIONS_TIPS_EN), 'versions_tip_pourquoi'), [lang]);
  const conseilTip = useMemo(() => pickTip(pool(CONSEIL_TIPS, CONSEIL_TIPS_EN), 'versions_tip_conseil'), [lang]);
  const progressionNoScoreTip = useMemo(() => pickTip(pool(PROGRESSION_TIPS_NO_SCORE, PROGRESSION_TIPS_NO_SCORE_EN), 'versions_tip_progression_noscore'), [lang]);
  const progressionWithScoreTip = useMemo(() => pickTip(pool(PROGRESSION_TIPS_WITH_SCORE, PROGRESSION_TIPS_WITH_SCORE_EN), 'versions_tip_progression_score'), [lang]);
  const homeTagline = useMemo(() => pickTip(pool(HOME_TAGLINES, HOME_TAGLINES_EN), 'versions_tip_tagline'), [lang]);
  // Menu 3-points ouvert pour un projet donné (null = aucun)
  const [openProjectMenuId, setOpenProjectMenuId] = useState(null);
  // true si l'utilisateur a cliqué "+ Nouveau projet" depuis le picker "Nouveau titre"
  // → après création on enchaîne directement sur la saisie du titre.
  const pendingNewTrackRef = useRef(false);

  // Modales
  const [renameProjectTarget, setRenameProjectTarget] = useState(null);
  const [membersTarget, setMembersTarget] = useState(null); // projet dont on gère les membres
  const [membersInitialTrack, setMembersInitialTrack] = useState(null); // portée initiale (titre) de la modale membres
  const [renameTrackTarget, setRenameTrackTarget] = useState(null);

  // Projets partagés AVEC moi (collaboration Phase 2) — chargés à part car
  // loadProjects() ne renvoie que les projets possédés.
  const [sharedProjects, setSharedProjects] = useState([]);
  const [sharedTracks, setSharedTracks] = useState([]);
  const [openSharedId, setOpenSharedId] = useState(null);
  useEffect(() => {
    let alive = true;
    loadSharedProjectsWithTracks().then((list) => { if (alive) setSharedProjects(list); }).catch(() => {});
    loadSharedTracks().then((list) => { if (alive) setSharedTracks(list); }).catch(() => {});
    return () => { alive = false; };
  }, [projectsLoaded]);
  const sharedRoleLabel = (r) => (s.share && s.share.roles && s.share.roles[r]) || r;
  const [renameValue, setRenameValue] = useState('');
  const [newProjectOpen, setNewProjectOpen] = useState(false);
  const [newProjectValue, setNewProjectValue] = useState('');
  const renameInputRef = useRef(null);
  const newProjectInputRef = useRef(null);

  // Ferme le menu 3-points au clic extérieur / Escape
  useEffect(() => {
    if (!openProjectMenuId) return;
    const onDown = (e) => {
      if (!e.target.closest?.('.wh-acc-menu, .wh-acc-menu-btn')) setOpenProjectMenuId(null);
    };
    const onEsc = (e) => { if (e.key === 'Escape') setOpenProjectMenuId(null); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onEsc);
    };
  }, [openProjectMenuId]);

  // ── Animations d'entrée au scroll ─────────────────────────────────
  // Mécanique identique à pricing/landing : un IntersectionObserver
  // ajoute la classe .wh-anim-in aux .wh-anim quand ils entrent dans le
  // viewport (15% de profondeur), animate-once via unobserve.
  // Re-attache l'observer quand projectsLoaded passe à true (parce que
  // la home rend des listes de projets/tracks après ce flag, et leurs
  // cards .wh-anim apparaissent à ce moment-là).
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const els = document.querySelectorAll('.wh-anim:not(.wh-anim-in)');
    if (!els.length) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('wh-anim-in');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0, rootMargin: '0px 0px -15% 0px' });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [projectsLoaded, projects, screen]);

  // Liste à plat de tous les titres (pour le picker "À quel titre ?")
  const allTracks = projects.flatMap((p) => (p.tracks || []).map((t) => ({ ...t, _projectName: p.name, projectId: p.id })));

  // Map projectId → index de couleur. Garantit l'unicité tant que
  // le nombre de projets ≤ PROJECT_COLOR_COUNT. Recalculée à chaque render,
  // mais basée uniquement sur createdAt ⇒ stable quand on réordonne.
  const projectColorMap = assignProjectColors(projects);

  const displayName = userProfile?.prenom || null;

  // ── Helpers ──
  // Score moyen du projet : moyenne des scores globaux des dernières versions
  // de chaque titre. Retourne null si aucune analyse disponible.
  const projectScore = (project) => {
    const scores = (project.tracks || [])
      .map((t) => t.versions?.[t.versions.length - 1]?.analysisResult?.fiche?.globalScore)
      .filter((x) => typeof x === 'number');
    if (!scores.length) return null;
    return Math.round(scores.reduce((sum, x) => sum + x, 0) / scores.length);
  };
  // Classe CSS de couleur du score : mint (>=80), amber (60-79), red (<60).
  const scoreClass = (score) => {
    if (score == null) return 'dash';
    if (score >= 80) return 'good';
    if (score >= 60) return 'mid';
    return 'low';
  };
  // Date la plus récente d'une activité du projet (dernière version créée).
  const projectLastActivityMs = (project) =>
    Math.max(0, ...(project.tracks || []).map(trackLastDateMs));
  const metaLine = (project) => {
    const nTracks = project.tracks?.length || 0;
    const tLabel = `${nTracks} ${nTracks > 1 ? s.home.trackPlural : s.home.trackSingular}`;
    // Total de versions = somme des versions de chaque track du projet.
    const nVersions = (project.tracks || []).reduce(
      (sum, t) => sum + (t.versions?.length || 0),
      0,
    );
    const vLabel = nVersions > 0
      ? `${nVersions} ${nVersions > 1 ? s.home.versionPlural : s.home.versionSingular}`
      : null;
    // Pas de date de dernière analyse ici — chaque track row affiche
    // déjà sa propre date à droite, ça ferait double emploi.
    return [tLabel, vLabel].filter(Boolean).join(' · ');
  };

  const buildProjectPlaylist = (project) =>
    (project.tracks || [])
      .map((t) => {
        const latest = t.versions?.[t.versions.length - 1];
        if (!latest?.storagePath) return null;
        return { trackTitle: t.title, versionName: latest.name, storagePath: latest.storagePath };
      })
      .filter(Boolean);

  // ── Handlers projets ──
  const toggleProject = (projectId) => {
    if (!onSetCurrentProject) return;
    onSetCurrentProject(projectId === currentProjectId ? null : projectId);
  };

  const handlePlayProject = (e, project) => {
    e.stopPropagation();
    const playlist = buildProjectPlaylist(project);
    if (!playlist.length || !onPlay) return;
    // Toggle si déjà en lecture sur ce projet
    const firstTitle = playlist[0].trackTitle;
    if (playerState?.trackTitle && project.tracks?.some(t => t.title === playerState.trackTitle) && onToggle) {
      onToggle();
      return;
    }
    onPlay(playlist[0].trackTitle, playlist[0].versionName, playlist[0].storagePath, playlist, 0);
    // Ouvre l'accordéon si fermé
    if (project.id !== currentProjectId && onSetCurrentProject) onSetCurrentProject(project.id);
    void firstTitle;
  };

  const handlePlayTrack = (track, project) => {
    if (playerState?.trackTitle === track.title && onToggle) { onToggle(); return; }
    const playlist = buildProjectPlaylist(project);
    const idx = playlist.findIndex((p) => p.trackTitle === track.title);
    if (idx < 0 || !onPlay) return;
    onPlay(playlist[idx].trackTitle, playlist[idx].versionName, playlist[idx].storagePath, playlist, idx);
  };

  const handleViewFiche = (track) => {
    const latest = track.versions?.[track.versions.length - 1];
    if (!latest) return;
    if (onOpenFiche) onOpenFiche(track, latest);
    else if (onSelectVersion) onSelectVersion(track, latest);
  };

  // Ouvre une version précise depuis la recherche (chip V1/V2).
  const handleOpenVersionFromSearch = (track, version) => {
    if (!version) { handleViewFiche(track); return; }
    if (onOpenFiche) onOpenFiche(track, version);
    else if (onSelectVersion) onSelectVersion(track, version);
  };

  // Depuis la recherche : déplie le projet ciblé et scrolle dessus.
  const handleOpenProjectFromSearch = (project) => {
    if (onSetCurrentProject) onSetCurrentProject(project.id);
    // Laisse l'accordéon se déplier avant de scroller.
    setTimeout(() => {
      const el = document.getElementById(`wh-acc-${project.id}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 60);
  };

  const handleAddTrackToProject = (project) => {
    if (onSetCurrentProject) onSetCurrentProject(project.id);
    if (onNewTrack) onNewTrack();
  };

  // Renommer projet
  const handleRenameProjectStart = (project) => {
    setRenameProjectTarget(project);
    setRenameValue(project.name);
    setTimeout(() => renameInputRef.current?.select(), 50);
  };
  const submitRenameProject = async () => {
    const next = renameValue.trim();
    if (!next || next === renameProjectTarget?.name) { setRenameProjectTarget(null); return; }
    try {
      await renameProject(renameProjectTarget.id, next);
      if (onMutate) onMutate();
    } catch (err) { console.warn('renameProject failed', err); }
    setRenameProjectTarget(null);
  };

  // Supprimer projet
  const handleDeleteProject = async (project) => {
    const nTracks = (project.tracks || []).length;
    const trackWord = nTracks > 1 ? s.home.trackPlural : s.home.trackSingular;
    const msg = nTracks === 0
      ? s.home.deleteProjectMsgEmpty.replace('{name}', project.name)
      : s.home.deleteProjectMsgWithTracks
          .replace('{name}', project.name)
          .replace('{n}', String(nTracks))
          .replace('{trackWord}', trackWord);
    const ok = await confirmDialog({
      title: s.home.deleteProjectTitle,
      message: msg,
      confirmLabel: s.home.delete,
      cancelLabel: s.home.cancel,
      danger: true,
    });
    if (ok !== 'confirm') return;
    try {
      await deleteProject(project.id);
      if (onMutate) onMutate();
    } catch (err) { console.warn('deleteProject failed', err); }
  };

  // Changer l'image de cover du projet (file input caché, déclenché via menu)
  const coverFileInputRef = useRef(null);
  const [coverUploadTarget, setCoverUploadTarget] = useState(null);
  const handleChangeCoverStart = (project) => {
    setCoverUploadTarget(project);
    // on relance systématiquement la value pour autoriser le re-upload du même fichier
    if (coverFileInputRef.current) coverFileInputRef.current.value = '';
    coverFileInputRef.current?.click();
  };
  const handleCoverFileChange = async (e) => {
    const file = e.target.files?.[0];
    const target = coverUploadTarget;
    e.target.value = '';
    if (!file || !target) { setCoverUploadTarget(null); return; }
    try {
      // Redimensionnement automatique client-side (max 1200px, JPEG/WebP ~85%).
      // Pas de message de taille : on adapte silencieusement.
      const resized = await resizeImageFile(file).catch(() => file);
      await setProjectCoverImage(target.id, resized || file);
      if (onMutate) onMutate();
    } catch (err) { console.warn('setProjectCoverImage failed', err); }
    setCoverUploadTarget(null);
  };
  const handleClearCover = async (project) => {
    try {
      await clearProjectCoverImage(project.id);
      if (onMutate) onMutate();
    } catch (err) { console.warn('clearProjectCoverImage failed', err); }
  };

  // ─── Image d'illustration par titre (track) ────────────────────────
  // Même pattern que le projet : un file input caché + target courante.
  // Propagé vers WhTrackRow via onChangeCover / onClearCover.
  const trackCoverFileInputRef = useRef(null);
  const [trackCoverUploadTarget, setTrackCoverUploadTarget] = useState(null);
  const handleChangeTrackCoverStart = (track) => {
    setTrackCoverUploadTarget(track);
    if (trackCoverFileInputRef.current) trackCoverFileInputRef.current.value = '';
    trackCoverFileInputRef.current?.click();
  };
  const handleTrackCoverFileChange = async (e) => {
    const file = e.target.files?.[0];
    const target = trackCoverUploadTarget;
    e.target.value = '';
    if (!file || !target) { setTrackCoverUploadTarget(null); return; }
    try {
      const resized = await resizeImageFile(file).catch(() => file);
      await setTrackCoverImage(target.id, resized || file);
      if (onMutate) onMutate();
    } catch (err) { console.warn('setTrackCoverImage failed', err); }
    setTrackCoverUploadTarget(null);
  };
  const handleClearTrackCover = async (track) => {
    try {
      await clearTrackCoverImage(track.id);
      if (onMutate) onMutate();
    } catch (err) { console.warn('clearTrackCoverImage failed', err); }
  };

  // Nouveau projet
  const handleNewProject = () => {
    setNewProjectValue('');
    setNewProjectOpen(true);
    setTimeout(() => newProjectInputRef.current?.focus(), 50);
  };
  // Garde anti-double-submit : si l'utilisateur appuie 2 fois sur Entrée
  // ou clique vite 2 fois sur "Créer", on créait 2 projets en DB. Le ref
  // bloque les appels concurrents pendant la requête.
  const creatingProjectRef = useRef(false);
  const submitNewProject = async () => {
    const name = newProjectValue.trim();
    if (!name) return;
    if (creatingProjectRef.current) return;
    creatingProjectRef.current = true;
    try {
      const created = await createProject(name);
      setNewProjectOpen(false);
      setNewProjectValue('');
      if (onMutate) onMutate();
      if (created?.id && onSetCurrentProject) onSetCurrentProject(created.id);
      // Si l'utilisateur venait du bouton "Nouveau titre", on enchaîne
      // directement sur la saisie du titre dans ce projet fraîchement créé.
      if (pendingNewTrackRef.current) {
        pendingNewTrackRef.current = false;
        if (onNewTrack) onNewTrack();
      }
    } catch (err) {
      console.warn('createProject failed', err);
    } finally {
      creatingProjectRef.current = false;
    }
  };

  // Renommer titre
  const handleRenameTrackStart = (track) => {
    setRenameTrackTarget(track);
    setRenameValue(track.title);
    setTimeout(() => renameInputRef.current?.select(), 50);
  };
  const submitRenameTrack = async () => {
    const next = renameValue.trim();
    if (!next || next === renameTrackTarget?.title) { setRenameTrackTarget(null); return; }
    try {
      await renameTrack(renameTrackTarget.id, next);
      if (onMutate) onMutate();
    } catch (err) { console.warn('renameTrack failed', err); }
    setRenameTrackTarget(null);
  };

  // Supprimer titre
  const handleDeleteTrack = async (track) => {
    const n = (track.versions || []).length;
    const versionWord = n > 1 ? s.home.versionPlural : s.home.versionSingular;
    const ok = await confirmDialog({
      title: s.home.deleteTrackTitle,
      message: s.home.deleteTrackMsg
        .replace('{name}', track.title)
        .replace('{n}', String(n))
        .replace('{versionWord}', versionWord),
      confirmLabel: s.home.delete,
      cancelLabel: s.home.cancel,
      danger: true,
    });
    if (ok !== 'confirm') return;
    try {
      await deleteTrack(track.id);
      if (onMutate) onMutate();
    } catch (err) { console.warn('deleteTrack failed', err); }
  };

  const totalProjects = projects.length;
  const isMobile = useMobile();
  // Onboarding tant qu'il n'y a pas AU MOINS UNE version analysée. Un titre
  // créé mais non analysé ne fait pas basculer la Home — on veut que le
  // bloc d'onboarding (checklist "Mise en route") reste visible pour guider
  // l'utilisateur jusqu'à sa première vraie analyse.
  const hasAnalyzedTrack = allTracks.some(
    (t) => (t.versions || []).some((v) => v?.analysisResult)
  );
  const hasContent = totalProjects > 0 && hasAnalyzedTrack;

  // ── Calculs pour la home desktop (hero + stats) ──
  // Ne servent qu'en version desktop ; mobile les ignore entièrement.
  // Bug fix : `latest.date` est une chaîne FR lisible ("30 avr 2026")
  // que new Date() ne sait pas parser → retombait sur NaN → metaLine
  // affichait "aucune analyse" même quand des analyses existaient.
  // On essaie createdAt (ISO) en priorité, puis fallback sur date.
  const tryParseMs = (src) => {
    if (!src) return 0;
    const t = new Date(src).getTime();
    return Number.isFinite(t) ? t : 0;
  };
  const trackLastDateMs = (t) => {
    const latest = t?.versions?.[t.versions.length - 1];
    return Math.max(
      tryParseMs(latest?.createdAt),
      tryParseMs(latest?.date),
      tryParseMs(t?.createdAt),
    );
  };

  const heroInfo = (() => {
    // 1) Titre en cours de lecture dans le player (fil d'Ariane naturel)
    if (playerState?.trackTitle) {
      for (const p of projects) {
        const found = p.tracks?.find((t) => t.title === playerState.trackTitle);
        if (found) {
          const gradIdx = p.coverGradient
            ? p.coverGradient % PROJECT_COLOR_COUNT
            : (projectColorMap.get(p.id) ?? 0);
          return { track: found, project: p, gradIdx };
        }
      }
    }
    // 2) Sinon : titre le plus récemment analysé
    let best = null;
    for (const p of projects) {
      for (const t of p.tracks || []) {
        const ts = trackLastDateMs(t);
        if (!best || ts > best.ts) {
          const gradIdx = p.coverGradient
            ? p.coverGradient % PROJECT_COLOR_COUNT
            : (projectColorMap.get(p.id) ?? 0);
          best = { track: t, project: p, gradIdx, ts };
        }
      }
    }
    return best;
  })();

  const nTitres = allTracks.length;
  const nVersions = allTracks.reduce((s, t) => s + (t.versions?.length || 0), 0);
  const allScores = allTracks
    .map((t) => t.versions?.[t.versions.length - 1]?.analysisResult?.fiche?.globalScore)
    .filter((x) => typeof x === 'number');
  const avgScore = allScores.length
    ? Math.round(allScores.reduce((s, x) => s + x, 0) / allScores.length)
    : null;

  // Derniers scores chronologiques pour le sparkline
  const sparkScores = allTracks
    .flatMap((t) => (t.versions || []).map((v) => ({
      score: v.analysisResult?.fiche?.globalScore,
      ts: new Date(v.date || v.createdAt || 0).getTime(),
    })))
    .filter((e) => typeof e.score === 'number')
    .sort((a, b) => a.ts - b.ts)
    .slice(-8)
    .map((e) => e.score);

  // Dernier titre analysé : track dont la dernière version a une fiche,
  // avec la date la plus récente (priorité à v.date puis v.createdAt).
  // Sert la carte "Dernier titre analysé" du bloc utilisateur (colonne droite).
  const lastAnalyzedInfo = (() => {
    let best = null;
    for (const t of allTracks) {
      const last = t.versions?.[t.versions.length - 1];
      const fiche = last?.analysisResult?.fiche;
      if (!fiche) continue;
      // last.date est une chaîne FR lisible ("30 avr 2026") non parsable
      // par new Date() — on tente d'abord createdAt (ISO), puis on
      // fallback sur date au cas où une migration future le stocke en
      // ISO. Avant ce fix, ts retombait à NaN et formatRelative
      // retournait "—" → la card "Dernier titre analysé" affichait
      // "74/100 · —" au lieu de "74/100 · il y a X jours".
      let ts = NaN;
      if (last?.createdAt) {
        const t1 = new Date(last.createdAt).getTime();
        if (!Number.isNaN(t1)) ts = t1;
      }
      if (Number.isNaN(ts) && last?.date) {
        const t2 = new Date(last.date).getTime();
        if (!Number.isNaN(t2)) ts = t2;
      }
      if (Number.isNaN(ts)) continue; // pas de date exploitable → skip
      if (!best || ts > best.ts) best = { track: t, version: last, fiche, ts };
    }
    return best;
  })();

  // Date de la dernière activité (version la plus récente toutes confondues)
  const lastActivityMs = Math.max(0, ...allTracks.map(trackLastDateMs));
  const formatRelative = (ms) => {
    if (!ms) return s.home.relativeDash;
    // Label informatif "il y a X" — l'impureté de Date.now() est tolérée ici
    // car la valeur n'influence pas l'arbre (pas de condition de rendu).
    // eslint-disable-next-line react-hooks/purity
    const diff = Date.now() - ms;
    const day = 86400000;
    if (diff < day) return s.home.relativeToday;
    if (diff < 2 * day) return s.home.relativeYesterday;
    if (diff < 7 * day) return s.home.relativeDaysAgo.replace('{n}', String(Math.floor(diff / day)));
    if (diff < 30 * day) return s.home.relativeWeeksAgo.replace('{n}', String(Math.floor(diff / (7 * day))));
    return s.home.relativeMonthsAgo.replace('{n}', String(Math.floor(diff / (30 * day))));
  };

  // Score de la dernière analyse du héros (pour badge + CTA "Voir la fiche")
  const heroLatestVersion = heroInfo?.track?.versions?.[heroInfo.track.versions.length - 1];
  const heroScore = heroLatestVersion?.analysisResult?.fiche?.globalScore;
  // Waveform = version réellement jouée si c'est le titre en cours, sinon la dernière
  const heroWaveStoragePath = (
    heroInfo && playerState?.trackTitle === heroInfo.track.title && playerState?.storagePath
  ) ? playerState.storagePath : heroLatestVersion?.storagePath;

  /* ─── Drag & drop Home ──────────────────────────────── */
  const [drag, setDrag] = useState(null);

  const handleDropTrackOnTrack = async (sourceTrackId, sourceProjectId, targetTrackId, targetProjectId, position) => {
    if (sourceTrackId === targetTrackId) return;
    const targetProject = projects.find(p => p.id === targetProjectId);
    if (!targetProject) return;
    const targetOrder = (targetProject.tracks || []).map(t => t.id).filter(id => id !== sourceTrackId);
    const targetIdx = targetOrder.findIndex(id => id === targetTrackId);
    if (targetIdx < 0 && sourceProjectId === targetProjectId) return;
    const insertAt = position === 'before' ? (targetIdx < 0 ? 0 : targetIdx) : (targetIdx < 0 ? targetOrder.length : targetIdx + 1);
    targetOrder.splice(insertAt, 0, sourceTrackId);
    try {
      if (sourceProjectId !== targetProjectId) {
        await moveTrackToProject(sourceTrackId, targetProjectId);
        const sourceProject = projects.find(p => p.id === sourceProjectId);
        if (sourceProject) {
          const sourceOrder = (sourceProject.tracks || []).map(t => t.id).filter(id => id !== sourceTrackId);
          if (sourceOrder.length) await reorderTracksInProject(sourceProjectId, sourceOrder);
        }
      }
      await reorderTracksInProject(targetProjectId, targetOrder);
      if (onMutate) onMutate();
    } catch (err) { console.warn('home drop track on track failed', err); }
  };

  const handleDropTrackOnProject = async (sourceTrackId, sourceProjectId, targetProjectId) => {
    if (sourceProjectId === targetProjectId) return;
    try {
      await moveTrackToProject(sourceTrackId, targetProjectId);
      const sourceProject = projects.find(p => p.id === sourceProjectId);
      if (sourceProject) {
        const sourceOrder = (sourceProject.tracks || []).map(t => t.id).filter(id => id !== sourceTrackId);
        if (sourceOrder.length) await reorderTracksInProject(sourceProjectId, sourceOrder);
      }
      if (onMutate) onMutate();
    } catch (err) { console.warn('home drop track on project failed', err); }
  };

  /* ─── JSX réutilisables (mobile + desktop) ─────────────── */
  const actionsBar = (
    <div className="wh-actions">
      <button
        className="wh-action wh-action-primary"
        onClick={() => setAddModalOpen(true)}
      >
        <span className="wh-action-icon">+</span>
        <span>{s.home.add}</span>
      </button>
    </div>
  );

  const projectsAccordion = totalProjects > 0 ? (
    <div className="wh-tracklist">
      <div className="wh-projects">
        {/* Titre "Mes projets" à l'intérieur du cadre (v4-panel-head) */}
        <div className="wh-section-title wh-projects-title">{s.home.myProjects} <em>{s.home.myProjectsAccent}</em></div>
        <WhSearch
          projects={projects}
          allTracks={allTracks}
          onOpenProject={handleOpenProjectFromSearch}
          onOpenTrack={handleViewFiche}
          onOpenVersion={handleOpenVersionFromSearch}
        />
        {projects.map((project) => {
          const isOpen = project.id === currentProjectId;
          const nTracks = project.tracks?.length || 0;
          // Couleur du projet : priorité à cover_gradient s'il est explicite (>0),
          // sinon index unique issu de assignProjectColors (garanti distinct
          // tant qu'on a ≤ PROJECT_COLOR_COUNT projets).
          const gradIdx = project.coverGradient
            ? project.coverGradient % PROJECT_COLOR_COUNT
            : (projectColorMap.get(project.id) ?? 0);
          const isProjectPlaying = !!(
            playerState?.isPlaying &&
            project.tracks?.some((t) => t.title === playerState.trackTitle)
          );

          return (
            <div
              key={project.id}
              id={`wh-acc-${project.id}`}
              className={`wh-acc-item wh-tint-${gradIdx}${isOpen ? ' open' : ''}${openProjectMenuId === project.id ? ' menu-open' : ''}`}
            >
              {/* Header projet */}
              <div
                className="wh-acc-head"
                onClick={() => toggleProject(project.id)}
                onDragOver={(e) => {
                  // Seul le drop d'un titre depuis un autre projet est accepté sur le header
                  if (!drag || drag.type !== 'track') return;
                  if (drag.sourceProjectId === project.id) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  e.currentTarget.style.background = 'rgba(245,176,86,.08)';
                }}
                onDragLeave={(e) => { e.currentTarget.style.background = ''; }}
                onDrop={(e) => {
                  e.preventDefault();
                  const d = drag;
                  e.currentTarget.style.background = '';
                  setDrag(null);
                  if (!d) return;
                  if (d.type === 'track' && d.sourceProjectId !== project.id) {
                    handleDropTrackOnProject(d.trackId, d.sourceProjectId, project.id);
                  }
                }}
              >
                <div
                  className={`wh-acc-cover wh-gradient-${gradIdx}${project.coverImageUrl ? ' has-image' : ''}`}
                  style={project.coverImageUrl ? {
                    backgroundImage: `url("${project.coverImageUrl}")`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  } : undefined}
                >
                  {/* Play projet — apparaît au hover de la vignette, centré dessus */}
                  <button
                    className={`wh-acc-play${isProjectPlaying ? ' playing' : ''}`}
                    onClick={(e) => handlePlayProject(e, project)}
                    title={isProjectPlaying ? s.home.playing : s.home.heroPlayProject}
                  >
                    {isProjectPlaying ? (
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor"><rect x="3" y="2" width="3" height="10" rx="1"/><rect x="8" y="2" width="3" height="10" rx="1"/></svg>
                    ) : (
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor"><path d="M3 1.5v11l9-5.5z"/></svg>
                    )}
                  </button>
                </div>

                <div className="wh-acc-title">
                  <div className="wh-acc-kicker">{s.home.projectKicker}</div>
                  <div className="wh-acc-name">{project.name}</div>
                  <div className="wh-acc-meta">{metaLine(project)}</div>
                </div>

                {/* Score projet — moyenne des dernières versions de chaque titre.
                    Classe good/mid/low pour colorer selon les seuils 80/60.
                    Eyebrow "Score moyen" au-dessus du chiffre — sans le label
                    on ne savait pas ce que désignait le 75/81 affiché à droite. */}
                {(() => {
                  const pScore = projectScore(project);
                  return (
                    <div className={`wh-acc-score-block ${scoreClass(pScore)}`}>
                      <div className="wh-acc-score-eyebrow">{s.home.projectAvgScore || 'Score moyen'}</div>
                      <div className={`wh-acc-score ${scoreClass(pScore)}`}>
                        {pScore != null ? pScore : s.home.relativeDash}
                      </div>
                    </div>
                  );
                })()}

                {/* Menu 3-points en haut à droite de la carte projet */}
                <button
                  className="wh-acc-menu-btn"
                  aria-label={s.home.projectOptions}
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenProjectMenuId((cur) => (cur === project.id ? null : project.id));
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
                    <circle cx="8" cy="3" r="1.5" />
                    <circle cx="8" cy="8" r="1.5" />
                    <circle cx="8" cy="13" r="1.5" />
                  </svg>
                </button>
                {openProjectMenuId === project.id && (
                  <div className="wh-acc-menu" onClick={(e) => e.stopPropagation()}>
                    <button
                      className="wh-acc-menu-item"
                      onClick={() => { setOpenProjectMenuId(null); handleRenameProjectStart(project); }}
                    >{s.home.rename}</button>
                    <button
                      className="wh-acc-menu-item"
                      onClick={() => { setOpenProjectMenuId(null); handleChangeCoverStart(project); }}
                    >{project.coverImageUrl ? s.home.replaceImage : s.home.changeImage}</button>
                    {project.coverImageUrl && (
                      <button
                        className="wh-acc-menu-item"
                        onClick={() => { setOpenProjectMenuId(null); handleClearCover(project); }}
                      >{s.home.removeImage}</button>
                    )}
                    <div className="wh-acc-menu-sep" />
                    <button
                      className="wh-acc-menu-item"
                      onClick={() => { setOpenProjectMenuId(null); setMembersInitialTrack(null); setMembersTarget(project); }}
                    >{s.home.share || 'Partager / Membres'}</button>
                    <div className="wh-acc-menu-sep" />
                    <button
                      className="wh-acc-menu-item danger"
                      onClick={() => { setOpenProjectMenuId(null); handleDeleteProject(project); }}
                    >{s.home.delete}</button>
                  </div>
                )}
              </div>

              {/* Body : liste des titres */}
              <div className="wh-acc-body">
                {nTracks > 0 ? (
                  <div className="wh-acc-tracklist">
                    {project.tracks.map((track, i, arr) => (
                      <WhTrackRow
                        key={track.id}
                        track={track}
                        project={project}
                        playerState={playerState}
                        onPlay={() => handlePlayTrack(track, project)}
                        onViewFiche={() => handleViewFiche(track)}
                        onRename={() => handleRenameTrackStart(track)}
                        onDelete={() => handleDeleteTrack(track)}
                        onChangeCover={() => handleChangeTrackCoverStart(track)}
                        onClearCover={() => handleClearTrackCover(track)}
                        onShare={() => { setMembersInitialTrack(track.id); setMembersTarget(project); }}
                        drag={drag}
                        setDrag={setDrag}
                        onDropTrackOnTrack={handleDropTrackOnTrack}
                        prevTrackId={arr[i - 1]?.id ?? null}
                        nextTrackId={arr[i + 1]?.id ?? null}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="wh-acc-empty">{s.home.emptyProject}</div>
                )}
                <button
                  className="wh-acc-add-track"
                  onClick={() => handleAddTrackToProject(project)}
                >{s.home.newTrackInProject}</button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  ) : null;

  // Section « Partagés avec moi » — projets + titres dont je suis membre.
  const sharedAccordion = (sharedProjects.length > 0 || sharedTracks.length > 0) ? (
    <div className="wh-tracklist wh-shared-section" style={{ marginTop: 18 }}>
      <div className="wh-projects">
        <div className="wh-section-title wh-projects-title">
          {s.home.sharedWithMe || 'Partagés'} <em>{s.home.sharedWithMeAccent || 'avec moi'}</em>
        </div>
        {sharedProjects.map((project) => {
          const isOpen = openSharedId === project.id;
          const nTracks = project.tracks?.length || 0;
          return (
            <div key={project.id} className={`wh-acc-item wh-tint-0${isOpen ? ' open' : ''}`}>
              <div className="wh-acc-head" onClick={() => setOpenSharedId(isOpen ? null : project.id)}>
                <div
                  className={`wh-acc-cover wh-gradient-0${project.coverImageUrl ? ' has-image' : ''}`}
                  style={project.coverImageUrl ? {
                    backgroundImage: `url("${project.coverImageUrl}")`,
                    backgroundSize: 'cover', backgroundPosition: 'center',
                  } : undefined}
                />
                <div className="wh-acc-info">
                  <div className="wh-acc-name">{project.name}</div>
                  <div className="wh-acc-sub" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    {project.ownerName && <span style={{ opacity: 0.6 }}>{project.ownerName}</span>}
                    <span className="vside-chip" style={{ fontSize: 10, padding: '1px 8px' }}>{sharedRoleLabel(project.role)}</span>
                  </div>
                </div>
              </div>
              {isOpen && (
                <div className="wh-acc-body">
                  {nTracks > 0 ? (
                    <div className="wh-acc-tracklist">
                      {project.tracks.map((track) => (
                        <button
                          key={track.id}
                          className="wh-shared-track"
                          onClick={() => handleViewFiche(track)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                            background: 'none', border: 'none', cursor: 'pointer',
                            padding: '9px 10px', borderRadius: 10, color: 'inherit',
                            font: 'inherit', textAlign: 'left',
                          }}
                        >
                          <span style={{ fontSize: 14, color: '#fff' }}>{track.title}</span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="wh-acc-empty">{s.home.emptyProject}</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {sharedTracks.map((track) => (
          <div key={track.id} className="wh-acc-item wh-tint-0">
            <div className="wh-acc-head" style={{ cursor: 'pointer' }} onClick={() => handleViewFiche(track)}>
              <div className="wh-acc-cover wh-gradient-0" />
              <div className="wh-acc-info">
                <div className="wh-acc-name">{track.title}</div>
                <div className="wh-acc-sub" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ opacity: 0.6 }}>{track.projectName}{track.ownerName ? ` · ${track.ownerName}` : ''}</span>
                  <span className="vside-chip" style={{ fontSize: 10, padding: '1px 8px' }}>{sharedRoleLabel(track.role)}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  ) : null;

  const mobileEmpty = totalProjects === 0 ? (
    <div className="wh-empty">
      <img src="/Logo-Versions-2.png" alt="" style={{ height: 60, width: "auto", opacity: 0.3 }} />
      <div>{s.home.emptyProjectsMobile}</div>
    </div>
  ) : null;

  const modalsSlot = (
    <>
      {/* File input caché — déclenché via le menu "Changer l'image" (projet) */}
      <input
        ref={coverFileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        style={{ display: 'none' }}
        onChange={handleCoverFileChange}
      />
      {/* File input caché — déclenché via le menu "Changer l'image" (titre) */}
      <input
        ref={trackCoverFileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        style={{ display: 'none' }}
        onChange={handleTrackCoverFileChange}
      />
      {renameTrackTarget && (
        <RenameModal
          title={s.home.renameTrackTitle}
          placeholder={s.home.trackNamePlaceholder}
          value={renameValue}
          originalValue={renameTrackTarget.title}
          inputRef={renameInputRef}
          onChange={setRenameValue}
          onCancel={() => setRenameTrackTarget(null)}
          onSubmit={submitRenameTrack}
          confirmLabel={s.home.confirmRename}
        />
      )}
      {renameProjectTarget && (
        <RenameModal
          title={s.home.renameProjectTitle}
          placeholder={s.home.projectNamePlaceholder}
          value={renameValue}
          originalValue={renameProjectTarget.name}
          inputRef={renameInputRef}
          onChange={setRenameValue}
          onCancel={() => setRenameProjectTarget(null)}
          onSubmit={submitRenameProject}
          confirmLabel={s.home.confirmRename}
        />
      )}
      {membersTarget && (
        <ProjectMembersModal
          project={membersTarget}
          s={s}
          initialScope={membersInitialTrack || 'project'}
          onClose={() => { setMembersTarget(null); setMembersInitialTrack(null); if (onMutate) onMutate(); }}
        />
      )}
      {newProjectOpen && (
        <RenameModal
          title={s.home.newProject}
          placeholder={s.home.projectNamePlaceholder}
          value={newProjectValue}
          originalValue=""
          inputRef={newProjectInputRef}
          onChange={setNewProjectValue}
          onCancel={() => setNewProjectOpen(false)}
          onSubmit={submitNewProject}
          confirmLabel={s.home.confirmCreate}
        />
      )}
      {addModalOpen && (
        <AddModal
          onClose={() => { setAddModalOpen(false); if (setAddModalCtx) setAddModalCtx(null); }}
          projects={projects}
          allTracks={allTracks}
          initialContext={addModalCtx}
          defaultDaw={userProfile?.default_daw || ''}
          onCreateProject={async (name) => {
            // Création inline depuis l'étape 'new-project-name'.
            // On retourne le projet créé pour que la modale puisse enchaîner sur l'upload si besoin.
            try {
              const created = await createProject(name);
              if (onMutate) onMutate();
              if (created?.id && onSetCurrentProject) onSetCurrentProject(created.id);
              return created;
            } catch (err) {
              console.warn('createProject from AddModal failed', err);
              throw err;
            }
          }}
          onAnalyze={(cfg) => {
            // Appelé depuis l'étape 'upload' quand l'utilisateur clique sur Lancer l'analyse.
            // La modale se ferme elle-même, on route vers l'écran de chargement ici.
            if (cfg.projectId && onSetCurrentProject) onSetCurrentProject(cfg.projectId);
            onAnalyze?.(cfg);
          }}
        />
      )}
    </>
  );

  /* ─── Desktop-only : hero "Reprends où tu étais" ───────── */
  const heroIsPlaying = !!(
    heroInfo && playerState?.isPlaying &&
    playerState?.trackTitle === heroInfo.track.title
  );
  const desktopHero = heroInfo && (
    <div className="wh-hero">
      <div
        className={`wh-hero-cover tint-${heroInfo.gradIdx}${heroInfo.project?.coverImageUrl ? ' has-image' : ''}`}
        style={heroInfo.project?.coverImageUrl ? {
          backgroundImage: `url("${heroInfo.project.coverImageUrl}")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        } : undefined}
      >
        <button
          className="wh-hero-play"
          onClick={() => handlePlayTrack(heroInfo.track, heroInfo.project)}
          title={heroIsPlaying ? s.home.heroPlaying : s.home.heroListen}
          aria-label={heroIsPlaying ? s.home.heroPause : s.home.heroPlayTrack}
        >
          {heroIsPlaying ? (
            <svg width="22" height="22" viewBox="0 0 14 14" fill="currentColor"><rect x="3" y="2" width="3" height="10" rx="1"/><rect x="8" y="2" width="3" height="10" rx="1"/></svg>
          ) : (
            <svg width="22" height="22" viewBox="0 0 14 14" fill="currentColor"><path d="M3 1.5v11l9-5.5z"/></svg>
          )}
        </button>
      </div>
      <div className="wh-hero-info">
        <div>
          <div className="wh-hero-kicker">{s.home.heroResume}</div>
          <div className="wh-hero-title">{heroInfo.track.title}</div>
          <div className="wh-hero-meta">
            {heroInfo.project.name} · {heroInfo.track.versions?.length || 0} {(heroInfo.track.versions?.length || 0) > 1 ? s.home.versionPlural : s.home.versionSingular}
            {heroLatestVersion?.date ? ` · ${heroLatestVersion.date}` : ''}
          </div>
        </div>
        {heroWaveStoragePath ? (
          <div className="wh-hero-wave-row">
            <HeroWaveform
              storagePath={heroWaveStoragePath}
              isActive={heroIsPlaying}
              resetKey={playerState?.resetKey || 0}
              onFinish={onNext}
            />
            <VolumeControl idle={!heroWaveStoragePath} />
          </div>
        ) : (
          <div className="wh-hero-wave wh-hero-wave-empty" aria-hidden />
        )}
        <div className="wh-hero-bottom">
          {typeof heroScore === 'number' ? (
            <div className="wh-hero-score">
              <span className="num">
                {Math.round(heroScore)}
                <span className="num-suffix">/100</span>
              </span>
              <span className="lbl">{s.home.heroScoreLabel}</span>
            </div>
          ) : <div />}
          <div className="wh-hero-ctas">
            <button className="wh-btn wh-btn-primary" onClick={() => handleViewFiche(heroInfo.track)}>
              {s.home.viewFiche}
            </button>
            <button className="wh-btn" onClick={() => { if (onAddVersion) onAddVersion(heroInfo.track); }}>
              {s.home.newVersionShort}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  /* ─── Desktop-only : ligne de stats (4 tuiles) ─────────── */
  const sparkPath = (() => {
    if (sparkScores.length < 2) return null;
    const w = 120, h = 24;
    const min = Math.min(...sparkScores);
    const max = Math.max(...sparkScores);
    const range = max - min || 1;
    return sparkScores
      .map((s, i) => {
        const x = (i / (sparkScores.length - 1)) * w;
        const y = h - ((s - min) / range) * h;
        return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
      })
      .join(' ');
  })();

  const desktopStats = (
    <div className="wh-stats">
      <div className="wh-stat wh-anim" style={{ '--anim-d': '0ms' }}>
        <div className="wh-stat-label">{s.home.statsTracks}</div>
        <div className="wh-stat-value">{nTitres}</div>
        <div className="wh-stat-hint">{totalProjects} {totalProjects > 1 ? s.home.projectPlural : s.home.projectSingular}</div>
      </div>
      <div className="wh-stat wh-anim" style={{ '--anim-d': '80ms' }}>
        <div className="wh-stat-label">{s.home.statsVersions}</div>
        <div className="wh-stat-value">{nVersions}</div>
        <div className="wh-stat-hint">{formatRelative(lastActivityMs)}</div>
      </div>
      <div className="wh-stat wh-anim" style={{ '--anim-d': '160ms' }}>
        <div className="wh-stat-label">{s.home.statsAvgScore}</div>
        <div className="wh-stat-value">{avgScore != null ? avgScore : s.home.relativeDash}</div>
        <div className="wh-stat-hint">
          {allScores.length
            ? `${s.home.statsOn} ${allScores.length} ${allScores.length > 1 ? s.home.analysisPlural : s.home.analysisSingular}`
            : s.home.statsNoAnalysis}
        </div>
      </div>
      <div className="wh-stat wh-anim" style={{ '--anim-d': '240ms' }}>
        <div className="wh-stat-label">{s.home.statsProgress}</div>
        <div className="wh-stat-spark">
          {sparkPath ? (
            <svg width="100%" height="38" viewBox="0 0 120 24" preserveAspectRatio="none" aria-hidden>
              <path d={sparkPath} fill="none" stroke="var(--amber)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          ) : (
            <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--muted)', marginTop: 8, letterSpacing: 0.5 }}>
              {s.home.statsEmptyCurve}
            </div>
          )}
        </div>
        <div className="wh-stat-hint">
          {sparkScores.length >= 2 ? s.home.statsRecentAnalyses.replace('{n}', String(sparkScores.length)) : s.home.statsCurveBuilding}
        </div>
      </div>
    </div>
  );

  /* ─── Colonne droite : 2 blocs distincts ────────────────────────────
     Bloc 1 « Toi »            : cartes centrées utilisateur (progression,
                                  prochain pas, dernier titre analysé).
     Bloc 2 « Le saviez-vous » : cartes pédagogiques (tip rotatif,
                                  à quoi ça sert, pourquoi VERSIONS, conseil).
  */
  const userBlock = (
    <section className="wh-rcol-section">
      <div className="wh-rcol-title">
        <span className="wh-rcol-dot" />
        {s.home.rightSectionYou}
      </div>
      <div className="wh-rcol-cards">
        <div className="wh-card cerulean">
          <div className="wh-card-kicker">{s.home.cardYourProgress}</div>
          <div className="wh-card-title">
            {avgScore != null ? s.home.cardAvgScore.replace('{n}', String(avgScore)) : s.home.cardLaunchFirst}
          </div>
          <div className="wh-card-body">
            {avgScore != null ? progressionWithScoreTip : progressionNoScoreTip}
          </div>
        </div>
        <div className="wh-card mint">
          <div className="wh-card-kicker">{s.home.cardNextStep}</div>
          <div className="wh-card-title">{prochainPasTip?.title}</div>
          <div className="wh-card-body">{prochainPasTip?.body}</div>
        </div>
        <div className="wh-card amber">
          <div className="wh-card-kicker">{s.home.cardLastAnalyzed}</div>
          {lastAnalyzedInfo ? (
            <>
              <button
                type="button"
                className="wh-card-title wh-card-title-link"
                onClick={() => handleViewFiche(lastAnalyzedInfo.track)}
                title={s.home.trackAnalysis}
              >
                {lastAnalyzedInfo.track.title}
              </button>
              <div className="wh-card-body">
                {typeof lastAnalyzedInfo.fiche.globalScore === 'number'
                  ? `${lastAnalyzedInfo.fiche.globalScore}/100 · ${formatRelative(lastAnalyzedInfo.ts)}`
                  : formatRelative(lastAnalyzedInfo.ts)}
              </div>
            </>
          ) : (
            <div className="wh-card-body">{s.home.cardLastAnalyzedEmpty}</div>
          )}
        </div>
      </div>
    </section>
  );

  const knowBlock = (
    <section className="wh-rcol-section">
      <div className="wh-rcol-title">
        <span className="wh-rcol-dot wh-rcol-dot-violet" />
        {s.home.rightSectionKnow}
      </div>
      <div className="wh-rcol-cards">
        <div className="wh-card violet">
          <div className="wh-card-kicker">{s.home.cardWhyUseful}</div>
          <div className="wh-card-title">{aQuoiTip?.title}</div>
          <div className="wh-card-body">{aQuoiTip?.body}</div>
        </div>
        <div className="wh-card amber">
          <div className="wh-card-kicker">{s.home.cardWhyVersions}</div>
          <div className="wh-card-title">{pourquoiTip?.title}</div>
          <div className="wh-card-body">{pourquoiTip?.body}</div>
        </div>
        <div className="wh-card mint">
          <div className="wh-card-kicker">{s.home.cardAdvice}</div>
          <div className="wh-card-title">{conseilTip?.title}</div>
          <div className="wh-card-body">{conseilTip?.body}</div>
        </div>
      </div>
    </section>
  );

  // Compat : les noms `tipsBlock` / `pedagoBlock` restent utilisés ailleurs
  // (écran onboarding, mobile). On les réexporte vers les nouveaux blocs.
  const tipsBlock = userBlock;
  const pedagoBlock = knowBlock;
  const editorialSidebar = (
    <div className="wh-col-right">
      {userBlock}
      {knowBlock}
    </div>
  );

  /* ─── Desktop-only : hero d'onboarding (compte neuf) ──── */
  const onboardingChecks = [
    { label: s.home.checkCreateProject, done: totalProjects > 0 },
    { label: s.home.checkFirstTrack, done: allTracks.length > 0 },
    { label: s.home.checkCompare, done: nVersions > 1 },
    { label: s.home.checkExploreChat, done: false },
  ];
  const doneCount = onboardingChecks.filter((c) => c.done).length;
  const onboardingProgress = Math.round((doneCount / onboardingChecks.length) * 100);

  const desktopOnboarding = (
    <div className="wh-onboarding">
      <div>
        {/* Constellation de chips décoratives au-dessus du welcome —
            même langage que les chips du hero pricing/landing.
            aria-hidden + pointer-events:none → décor pur, pas de
            confusion possible avec un menu cliquable. */}
        <div className="wh-ob-chips" aria-hidden="true">
          <span className="wh-ob-chip wh-ob-chip-amber">× ESSAI GRATUIT</span>
          <span className="wh-ob-chip wh-ob-chip-cerulean">PREMIERS PAS</span>
          <span className="wh-ob-chip wh-ob-chip-mint">À TON RYTHME</span>
        </div>
        <div className="wh-ob-welcome">
          {displayName ? s.home.welcomeHiName.replace('{name}', displayName) : s.home.welcomeHi}
        </div>
        <div className="wh-ob-tagline">
          {s.home.onboardingTagline}
        </div>
        <div className="wh-ob-ctas">
          {/* Compte 100% neuf (aucun projet) : un seul CTA "Mon premier projet"
              — pas de "premier titre" tant qu'il n'existe pas de projet où
              l'accrocher. Dès qu'un projet existe, on réaffiche les deux CTAs
              normaux (Premier titre + Nouveau projet). */}
          {totalProjects === 0 ? (
            <button className="wh-btn wh-btn-primary" onClick={handleNewProject}>
              {s.home.firstProject}
            </button>
          ) : (
            <>
              <button
                className="wh-btn wh-btn-primary"
                onClick={() => { if (onNewTrack) onNewTrack(); }}
              >{s.home.firstTrack}</button>
              <button className="wh-btn" onClick={handleNewProject}>{s.home.newProject}</button>
            </>
          )}
        </div>
      </div>
      <div className="wh-ob-checklist">
        <div className="wh-card-kicker">{s.home.gettingStarted}</div>
        <div className="wh-ob-progress">
          <div className="wh-ob-progress-fill" style={{ width: `${onboardingProgress}%` }} />
        </div>
        <div className="wh-checklist">
          {onboardingChecks.map((c, i) => (
            <div key={i} className={`wh-check-item${c.done ? ' done' : ''}`}>
              <span className="wh-check-box">✓</span>
              <span className="wh-check-label">{c.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const onboardingColumnLeft = (
    <div className="wh-col-left">
      {pedagoBlock}
    </div>
  );

  // Même JSX pour desktop et mobile : on render systématiquement la structure
  // "desktop" (wh-intro + desktopStats/desktopOnboarding + wh-cols) et on laisse
  // les media queries adapter le layout (stack 2→1 col, réduction typo, etc.).
  // Pour hasContent : intro + stats + projets+cartes. Pour onboarding : intro +
  // hero "bienvenue" + cartes selon nb de projets.
  return (
    <div className="welcome-home wh-desktop">
      {projectsLoaded ? (
        hasContent ? (
          <>
            <div className="wh-intro">
              <div className="wh-intro-row">
                <h1 className="wh-slogan">
                  <span className="wh-slogan-line">{s.home.sloganStart}<em>{s.home.sloganEm}</em>,</span><br />{s.home.sloganEnd.replace(/^,\s*/, '')}
                </h1>
                <div className="wh-tagline-text">{renderTagline(homeTagline)}</div>
              </div>
            </div>
            {/* Eyebrow d'accueil — sorti de .wh-intro et placé juste
                avant les 4 cards stats (refonte 2026-04-29). */}
            <div className="wh-eyebrow wh-eyebrow-floating wh-anim">
              {nTitres === 1
                ? s.home.heroEyebrowActiveSingle.replace('{name}', displayName || '')
                : s.home.heroEyebrowActive.replace('{name}', displayName || '').replace('{n}', String(nTitres))}
            </div>
            {desktopStats}
            {/* Refonte 2026-04-30bis : projets en pleine largeur en haut,
                Recommandations + Le saviez-vous côte à côte en 50/50 en
                dessous — même grammaire que la home des comptes neufs.
                La colonne projets respire et les deux blocs secondaires
                ne s'empilent plus en pile maigre à droite. */}
            <div className="wh-anim" style={{ '--anim-d': '320ms' }}>{projectsAccordion}</div>
            {sharedAccordion && <div className="wh-shared-wrap">{sharedAccordion}</div>}
            <div
              className="wh-cols"
              style={{ gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', marginTop: 32 }}
            >
              <div className="wh-col-left wh-anim" style={{ '--anim-d': '420ms' }}>{userBlock}</div>
              <div className="wh-col-right wh-anim" style={{ '--anim-d': '500ms' }}>{knowBlock}</div>
            </div>
          </>
        ) : (
          <>
            {/* Même structure que hasContent : .wh-intro + bloc action (à la place
                des stats) + 2 colonnes. Garantit l'homogénéité visuelle entre la
                Home "compte neuf" et la Home habituelle, et évite la duplication
                du knowBlock qui existait dans l'ancienne branche. */}
            <div className="wh-intro">
              <div className="wh-intro-row">
                <h1 className="wh-slogan">
                  <span className="wh-slogan-line">{s.home.sloganStart}<em>{s.home.sloganEm}</em>,</span><br />{s.home.sloganEnd.replace(/^,\s*/, '')}
                </h1>
                <div className="wh-tagline-text">{renderTagline(homeTagline)}</div>
              </div>
            </div>
            {/* Eyebrow "Bienvenue {name}…" — sorti de .wh-intro pour être
                cohérent avec la version "hasContent" : juste avant le bloc
                onboarding qui suit. */}
            <div className="wh-eyebrow wh-eyebrow-floating wh-anim">
              {(s.home.heroEyebrowWelcome || '').replace('{name}', displayName || '').trim()}
            </div>
            {desktopOnboarding}
            {totalProjects === 0 ? (
              /* 0 projet : pas d'accordéon perso — mais on affiche quand même
                 les projets/titres PARTAGÉS avec moi (cas d'un compte neuf
                 invité à collaborer), puis Recommandations + Le saviez-vous. */
              <>
                {sharedAccordion && <div className="wh-shared-wrap">{sharedAccordion}</div>}
                <div
                  className="wh-cols"
                  style={{ gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', marginTop: sharedAccordion ? 32 : 0 }}
                >
                  <div className="wh-col-left wh-anim" style={{ '--anim-d': '160ms' }}>{userBlock}</div>
                  <div className="wh-col-right wh-anim" style={{ '--anim-d': '240ms' }}>{knowBlock}</div>
                </div>
              </>
            ) : (
              /* ≥1 projet (refonte 2026-04-30bis) : on remonte les projets
                 en pleine largeur tout en haut, et on bascule Recommandations
                 + Le saviez-vous en dessous, côte à côte en 50/50 — même
                 grammaire de répartition que les comptes neufs. La colonne
                 projets prend toute la place pour respirer, les blocs
                 secondaires ne montent plus en pile maigre à droite. */
              <>
                <div className="wh-anim" style={{ '--anim-d': '160ms' }}>{projectsAccordion}</div>
                {sharedAccordion && <div className="wh-shared-wrap">{sharedAccordion}</div>}
                <div
                  className="wh-cols"
                  style={{ gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', marginTop: 32 }}
                >
                  <div className="wh-col-left wh-anim" style={{ '--anim-d': '240ms' }}>{userBlock}</div>
                  <div className="wh-col-right wh-anim" style={{ '--anim-d': '320ms' }}>{knowBlock}</div>
                </div>
              </>
            )}
          </>
        )
      ) : null /* première session sans cache — on ne montre rien plutôt que flasher l'écran d'onboarding */}

      {modalsSlot}
      <OnboardingHints steps={HOME_STEPS} storageKey={ONBOARDING_STORAGE_KEYS.home} />
    </div>
  );
}

/* ─── Ligne titre dans Home (accordéon ouvert) ─────────────────────── */
function WhTrackRow({ track, project, playerState, onPlay, onViewFiche, onRename, onDelete, onChangeCover, onClearCover, onShare, drag, setDrag, onDropTrackOnTrack, prevTrackId = null, nextTrackId = null }) {
  const { s } = useLang();
  const [hover, setHover] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropOver, setDropOver] = useState(null);
  const menuRef = useRef(null);
  const btnRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const h = (e) => {
      if (menuRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
      setMenuOpen(false);
    };
    const esc = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', h);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', h);
      document.removeEventListener('keydown', esc);
    };
  }, [menuOpen]);

  const latest = track.versions?.[track.versions.length - 1];
  const fiche = latest?.analysisResult?.fiche;
  const hasFiche = !!fiche;
  const dur = fiche?.duration_seconds;
  const durStr = dur ? `${Math.floor(dur / 60)}:${String(Math.floor(dur % 60)).padStart(2, '0')}` : null;
  const dateStr = latest?.date || null;
  const isThisPlaying = playerState?.trackTitle === track.title && !!playerState?.isPlaying;
  // Anciennement gated par hover — provoquait un decalage du bouton
  // "Analyse" quand la souris arrivait sur la row (les ⋯ s'inseraient
  // a droite et poussaient tout vers la gauche). On laisse maintenant
  // les ⋯ toujours visibles ; couleur soft baissee pour qu'ils ne
  // dominent pas en dehors du hover.
  const showDots = true;

  // Rangée entière cliquable quand une fiche existe : on ouvre la fiche
  // au clic sur la card. Les boutons internes (play, menu ⋯, drag handle,
  // chip "Voir fiche") font tous e.stopPropagation pour ne pas déclencher
  // l'ouverture par-dessus leur propre action. Si pas de fiche, la row
  // n'est pas interactive (curseur normal).
  const rowClickable = hasFiche;
  const handleRowClick = rowClickable ? () => onViewFiche?.() : undefined;
  const handleRowKey = rowClickable ? (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onViewFiche?.(); }
  } : undefined;

  return (
    <div
      className={`wh-track-row${menuOpen ? ' menu-open' : ''}${rowClickable ? ' clickable' : ''}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={handleRowClick}
      onKeyDown={handleRowKey}
      role={rowClickable ? 'button' : undefined}
      tabIndex={rowClickable ? 0 : undefined}
      aria-label={rowClickable ? `${s.home.trackAnalysis} — ${track.title}` : undefined}
      onDragOver={(e) => {
        if (!drag || drag.type !== 'track') return;
        if (drag.trackId === track.id) return;
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'move';
        const rect = e.currentTarget.getBoundingClientRect();
        const isAbove = (e.clientY - rect.top) < rect.height / 2;
        // Pas de trait de dépôt si la position visée correspond à la
        // position actuelle du titre déplacé (déplacement nul).
        if (drag.sourceProjectId === project?.id) {
          if (isAbove && drag.nextTrackId === track.id) { setDropOver(null); return; }
          if (!isAbove && drag.prevTrackId === track.id) { setDropOver(null); return; }
        }
        setDropOver(isAbove ? 'before' : 'after');
      }}
      onDragLeave={() => setDropOver(null)}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        const d = drag;
        const rect = e.currentTarget.getBoundingClientRect();
        setDropOver(null);
        if (setDrag) setDrag(null);
        if (!d || d.type !== 'track' || d.trackId === track.id) return;
        const isAbove = (e.clientY - rect.top) < rect.height / 2;
        onDropTrackOnTrack?.(d.trackId, d.sourceProjectId, track.id, project?.id, isAbove ? 'before' : 'after');
      }}
      style={{
        position: 'relative',
        boxShadow: dropOver === 'before' ? 'inset 0 2px 0 0 #f5b056' : (dropOver === 'after' ? 'inset 0 -2px 0 0 #f5b056' : 'none'),
        transition: 'box-shadow .1s',
      }}
    >
      {/* Poignée de déplacement titre */}
      <span
        className="wh-drag-handle"
        draggable={true}
        onClick={(e) => e.stopPropagation()}
        onDragStart={(e) => {
          e.stopPropagation();
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('application/x-versions-dnd', 'track');
          const row = e.currentTarget.closest('.wh-track-row');
          if (row) e.dataTransfer.setDragImage(row, 10, 10);
          if (setDrag) setDrag({ type: 'track', trackId: track.id, sourceProjectId: project?.id, prevTrackId, nextTrackId });
        }}
        onDragEnd={() => { if (setDrag) setDrag(null); setDropOver(null); }}
        title={s.home.trackDragHandle}
        aria-label={s.home.trackMove}
      >
        <svg width="10" height="14" viewBox="0 0 10 14" fill="currentColor" aria-hidden>
          <circle cx="3" cy="3" r="1.1"/><circle cx="7" cy="3" r="1.1"/>
          <circle cx="3" cy="7" r="1.1"/><circle cx="7" cy="7" r="1.1"/>
          <circle cx="3" cy="11" r="1.1"/><circle cx="7" cy="11" r="1.1"/>
        </svg>
      </span>

      {/* Play + cover fusionnés : image en fond si définie, sinon icône note ♪ */}
      <button
        className={`wh-track-play${isThisPlaying ? ' playing' : ''}${track.coverImageUrl ? ' has-image' : ''}`}
        onClick={(e) => { e.stopPropagation(); onPlay?.(e); }}
        title={isThisPlaying ? s.home.playing : s.home.play}
        style={track.coverImageUrl ? {
          backgroundImage: `url("${track.coverImageUrl}")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        } : undefined}
      >
        {/* Icône note de musique (fallback quand pas d'image) */}
        {!track.coverImageUrl && (
          <span className="wh-track-note" aria-hidden>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
              <path d="M11 1.5v7.2a2.3 2.3 0 1 1-1.2-2V3.8L5.2 5v5.7a2.3 2.3 0 1 1-1.2-2V3.8z"/>
            </svg>
          </span>
        )}
        {/* Overlay : triangle play (ou carrés pause) par-dessus l'image au survol */}
        <span className="wh-track-play-overlay">
          {isThisPlaying ? (
            <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor"><rect x="3" y="2" width="3" height="10" rx="1"/><rect x="8" y="2" width="3" height="10" rx="1"/></svg>
          ) : (
            <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor"><path d="M3 1.5v11l9-5.5z"/></svg>
          )}
        </span>
      </button>

      {/* Info — durée à côté du titre ("Your Song · 3:54"), nb de
          versions seul sur la ligne du dessous. */}
      <div className="wh-track-info">
        <div className="wh-track-title">
          <span>{track.title}</span>
          {durStr && <span className="wh-track-title-dur"> · {durStr}</span>}
        </div>
        <div className="wh-track-meta">
          {track.versions?.length || 1} {(track.versions?.length || 1) > 1 ? s.home.versionPlural : s.home.versionSingular}
        </div>
      </div>

      {/* Date */}
      {dateStr && <span className="wh-track-date">{dateStr}</span>}

      {/* Chip "VOIR FICHE" — pilule cerulean, label + petit chevron pour
          signifier "ouvrir, on va ailleurs" (vs déclencher un calcul).
          La row entière est aussi cliquable (cf. handleRowClick), cette
          chip reste comme cible explicite et accessible. */}
      {hasFiche && (
        <button className="wh-track-fiche" onClick={(e) => { e.stopPropagation(); onViewFiche?.(e); }}>
          <span>{s.home.trackAnalysis}</span>
          <svg className="wh-track-fiche-chev" width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M3.5 2l3 3-3 3" />
          </svg>
        </button>
      )}

      {/* Menu ⋯ — toujours visible (même hors hover), avec une opacité
          basse au repos et bumpée sur hover row pour rester discret
          sans provoquer de layout shift quand la souris arrive. */}
      {showDots && (
        <button
          ref={btnRef}
          onClick={(e) => { e.stopPropagation(); setMenuOpen((o) => !o); }}
          title={s.home.trackOptions}
          style={{
            width: 26, height: 26, borderRadius: 6,
            background: menuOpen ? 'rgba(245,176,86,.15)' : 'transparent',
            border: 'none',
            color: menuOpen ? '#f5b056' : (hover ? '#c5c5c7' : 'rgba(197,197,199,0.45)'),
            cursor: 'pointer',
            padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 14, lineHeight: 1, marginLeft: 4,
            transition: 'color .15s, background .15s',
          }}
        >⋯</button>
      )}

      {menuOpen && (
        <div
          ref={menuRef}
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 100,
            minWidth: 180, background: '#141416', border: '1px solid #2a2a2e',
            borderRadius: 10, padding: 6, boxShadow: '0 12px 32px rgba(0,0,0,.55)',
          }}
        >
          <WhMenuItem label={s.home.rename} onClick={() => { setMenuOpen(false); onRename(); }} />
          {onChangeCover && (
            <WhMenuItem
              label={track.coverImageUrl ? s.home.trackReplaceImage : s.home.trackChangeImage}
              onClick={() => { setMenuOpen(false); onChangeCover(); }}
            />
          )}
          {onClearCover && track.coverImageUrl && (
            <WhMenuItem
              label={s.home.trackRemoveImage}
              onClick={() => { setMenuOpen(false); onClearCover(); }}
            />
          )}
          {onShare && (
            <WhMenuItem label={s.home.shareTrack || 'Partager ce titre'} onClick={() => { setMenuOpen(false); onShare(); }} />
          )}
          <div style={{ height: 1, background: '#2a2a2e', margin: '4px 2px' }} />
          <WhMenuItem label={s.home.delete} danger onClick={() => { setMenuOpen(false); onDelete(); }} />
        </div>
      )}
    </div>
  );
}

function WhMenuItem({ label, onClick, danger }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'block', width: '100%', textAlign: 'left',
        padding: '8px 12px', borderRadius: 6, border: 'none',
        background: 'transparent', cursor: 'pointer',
        fontFamily: "'DM Sans', sans-serif", fontSize: 16,
        color: danger ? '#ef6b6b' : '#c5c5c7',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = danger ? 'rgba(239,107,107,.08)' : 'rgba(245,176,86,.06)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >{label}</button>
  );
}

const SIDEBAR_WIDTH = 260;

/* ── Mobile Menu — hamburger (plus d'avatar/photo) ──────────── */
function MobileMenu({ onNavigate, onSignOut, user, userProfile, onAdd, onGoFeedback }) {
  const [open, setOpen] = useState(false);
  const go = (target) => { setOpen(false); onNavigate(target); };
  const displayName = userProfile?.prenom || (user?.email ? user.email.split('@')[0] : 'utilisateur');

  return (
    <>
      {/* ── Top bar ── */}
      <div className="mobile-topbar">
        <div className="brand" onClick={() => go('home')} style={{ cursor: 'pointer', fontSize: 20, letterSpacing: '-0.3px', gap: 8 }}>
          <img src="/Logo-Versions-2.png" alt="" style={{ height: 22, width: 'auto' }} />
          <span>{"VER"}<span className="accent">{"Si"}</span>{"ONS"}</span>
        </div>
        <div className="mobile-avatar-wrap">
          <button
            className={`mobile-avatar-btn${open ? ' open' : ''}`}
            onClick={() => setOpen(!open)}
            aria-label="Menu utilisateur"
          >
            {/* Hamburger picto — remplace l'avatar (plus de photo
                ni d'initiale, pour l'instant on ne garde pas). */}
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="4" y1="7" x2="20" y2="7"/>
              <line x1="4" y1="12" x2="20" y2="12"/>
              <line x1="4" y1="17" x2="20" y2="17"/>
            </svg>
          </button>

          {open && createPortal((
            <>
              {/* Backdrop + popover montés VIA PORTAL dans document.body
                  pour échapper au stacking context du .mobile-topbar
                  (qui a backdrop-filter → containing block des fixed
                  descendants → iOS Safari empile mal les chips de version
                  par-dessus). En portail dans le body, le popover vit
                  dans le stacking context racine, son z-index est absolu. */}
              <div className="mobile-avatar-backdrop" onClick={() => setOpen(false)} />
              <div className="mobile-avatar-popover">
                <div className="mobile-avatar-popover-user">
                  <div className="mobile-avatar-popover-who">{displayName}</div>
                  {user?.email && <div className="mobile-avatar-popover-mail">{user.email}</div>}
                </div>
                {/* Feedback testeur — phase beta. EN PREMIER dans le
                    popover (au-dessus de "+ Ajouter" et de tout le
                    reste) pour qu'il soit l'item le plus visible quand
                    un testeur ouvre le hamburger. Item teinté ambre
                    pour ressortir des entrées neutres et compenser
                    l'absence de pill pulsante (réservée au rail
                    desktop, caché sur mobile). */}
                {onGoFeedback && (
                  <button
                    className="mobile-avatar-popover-item mobile-avatar-popover-feedback"
                    onClick={() => { setOpen(false); onGoFeedback(); }}
                  >
                    <span className="mobile-menu-icon">
                      {/* Cœur Lucide canonique (rempli) — reconnaissable
                          au premier coup d'œil même à 16px. */}
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="none" aria-hidden="true">
                        <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                      </svg>
                    </span>
                    Ton avis compte
                  </button>
                )}
                {onAdd && (
                  <button
                    className="mobile-avatar-popover-item mobile-avatar-popover-add"
                    onClick={() => { setOpen(false); onAdd(); }}
                  >
                    <span className="mobile-menu-icon">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                      </svg>
                    </span>
                    Ajouter
                  </button>
                )}
                <button className="mobile-avatar-popover-item" onClick={() => go('home')}>
                  <span className="mobile-menu-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="12" y1="16" x2="12" y2="12"/>
                      <line x1="12" y1="8" x2="12.01" y2="8"/>
                    </svg>
                  </span>
                  À propos
                </button>
                <button className="mobile-avatar-popover-item" onClick={() => go('reglages')}>
                  <span className="mobile-menu-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="3"/>
                      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>
                    </svg>
                  </span>
                  Réglages
                </button>
                <button
                  className="mobile-avatar-popover-item danger"
                  onClick={async () => { setOpen(false); if (onSignOut) await onSignOut(); }}
                >
                  <span className="mobile-menu-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/>
                      <polyline points="16 17 21 12 16 7"/>
                      <line x1="21" y1="12" x2="9" y2="12"/>
                    </svg>
                  </span>
                  Se déconnecter
                </button>
              </div>
            </>
          ), document.body)}
        </div>
      </div>
    </>
  );
}

/* ── Routing (permet "Précédent/Suivant" navigateur) ──
   URLs propres via History API. Vercel renvoie tout sur index.html via
   le rewrite défini dans vercel.json (sinon refresh = 404). Avant la
   refonte, on utilisait un hash router (`#/...`) ; les anciens liens
   sont migrés à la volée par `migrateHashToPath()` (cf. plus bas). */
// `home` = landing publique, ancrée à la racine `/`. Accessible aux
// visiteurs comme aux connectés (logo, lien "À propos").
// `welcome` = dashboard (WelcomeHome — projets/titres), sur `/dashboard`.
// Cold start sans pathname (`/`) côté connecté → reste sur la landing,
// le routeInit ci-dessous le routera ensuite si besoin.
// `/home` est gardé en alias rétro-compat de `/`.
// Cache localStorage de la liste des projets (rendu instantané au mount,
// stale-while-revalidate). La clé est VERSIONNÉE : bumper le suffixe invalide
// d'office tous les caches écrits par les builds antérieurs — c'est le seul
// moyen de "réparer à distance" un navigateur dont le cache local casse le
// render (page blanche auto-entretenue, cf. commentaire sur useState projects).
const PROJECTS_CACHE_KEY = 'versions_projects_cache_v2';
// Anciennes clés purgées au chargement pour ne pas laisser de données mortes.
const LEGACY_PROJECTS_CACHE_KEYS = ['versions_projects_cache'];

// Lecture défensive du cache : on n'accepte que des entrées qui ressemblent
// vraiment à un projet (objet avec id, et tracks/versions en tableaux). Toute
// entrée douteuse est écartée plutôt que rendue telle quelle.
function readProjectsCache() {
  try {
    for (const k of LEGACY_PROJECTS_CACHE_KEYS) localStorage.removeItem(k);
    const raw = localStorage.getItem(PROJECTS_CACHE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((p) => p && typeof p === 'object' && typeof p.id === 'string')
      .map((p) => ({
        ...p,
        tracks: Array.isArray(p.tracks)
          ? p.tracks
              .filter((t) => t && typeof t === 'object' && typeof t.id === 'string')
              .map((t) => ({ ...t, versions: Array.isArray(t.versions) ? t.versions.filter(Boolean) : [] }))
          : [],
      }));
  } catch {
    // Cache illisible : on repart d'une liste vide plutôt que de propager.
    try { localStorage.removeItem(PROJECTS_CACHE_KEY); } catch { /* ignore */ }
    return [];
  }
}

const SCREEN_PATH = {
  welcome: '/dashboard',
  home: '/',
  sample: '/exemple',
  pricing: '/pricing',
  plugin: '/plugin',
  admin: '/admin',
  loading: '/analyse',
  fiche: '/fiche',
  versions: '/versions',
  privacy: '/privacy',
  terms: '/terms',
};
const PATH_SCREEN = {
  '/': 'home',
  '/home': 'home',
  '/exemple': 'sample',
  '/sample-report': 'sample',
  '/pricing': 'pricing',
  '/tarifs': 'pricing',
  '/plugin': 'plugin',
  '/admin': 'admin',
  '/dashboard': 'welcome',
  '/analyse': 'loading',
  '/fiche': 'fiche',
  '/versions': 'versions',
  '/privacy': 'privacy',
  '/terms': 'terms',
};

// Migration silencieuse des anciennes URLs en `#/...` vers le nouveau
// schéma sans hash. Appelée le plus tôt possible (avant tout useState
// qui lit `location.pathname`), pour qu'un visiteur arrivant sur un
// vieux lien (`/#/fiche/...`) atterrisse sur la bonne page sans flash.
function migrateHashToPath() {
  if (typeof window === 'undefined') return;
  const hash = window.location.hash || '';
  if (!hash.startsWith('#/')) return;
  // Filet de sécurité historique : si jamais Supabase repassait en flow
  // implicit (tokens dans le fragment), on n'écraserait pas le hash
  // avant qu'il ait été parsé. En PKCE c'est inutile mais ça ne coûte rien.
  if (hash.includes('access_token=') || hash.includes('refresh_token=')) return;
  const newPath = hash.slice(1) || '/';
  try {
    window.history.replaceState(null, '', newPath + window.location.search);
  } catch { /* ignore */ }
}
// Migration synchrone au chargement du module — garantit que le 1ᵉʳ
// useState lazy de App lira un pathname propre.
migrateHashToPath();

// Slugifie un nom (titre, version) pour produire un segment d'URL lisible.
// Décompose les accents, met en minuscules, remplace les non-alphanumériques
// par des tirets, puis nettoie. Ex: "Comme un rêve" → "comme-un-reve".
function slugify(name) {
  if (!name) return '';
  return String(name)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

// Parse un pathname en { screen, trackId, versionId }.
// Reconnaît `/fiche/{seg}` et `/fiche/{seg}/{versionSeg}` en plus des
// routes statiques de PATH_SCREEN. Les segments peuvent être soit un slug
// (nouveau format : `comme-un-reve/v2`), soit un UUID (ancien format,
// rétrocompat). Le résolveur tranche au moment du matching.
function parsePath(pathname) {
  const p = pathname || '/';
  const m = p.match(/^\/fiche\/([^/?#]+)(?:\/([^/?#]+))?$/);
  if (m) {
    return { screen: 'fiche', trackId: m[1], versionId: m[2] || null };
  }
  return { screen: PATH_SCREEN[p] || null, trackId: null, versionId: null };
}

// Construit le pathname pour un screen donné. La fiche utilise le slug du
// titre (et de la version si dispo) pour produire des URLs lisibles —
// ex: `/fiche/comme-un-reve/v2`. Fallback UUIDs si le titre manque.
function buildPath(screen, cfg) {
  if (screen === 'fiche') {
    const titleSlug = slugify(cfg?.title || '');
    const versionSlug = slugify(cfg?.version || '');
    if (titleSlug) {
      return versionSlug ? `/fiche/${titleSlug}/${versionSlug}` : `/fiche/${titleSlug}`;
    }
    if (cfg?.trackId) {
      return cfg.versionId
        ? `/fiche/${cfg.trackId}/${cfg.versionId}`
        : `/fiche/${cfg.trackId}`;
    }
  }
  return SCREEN_PATH[screen] || '/';
}

/* ═══════════════════════════════════════════════════════════ */
/* APP                                                        */
/* ═══════════════════════════════════════════════════════════ */
// Extrait un éventuel token de partage (`/p/<token>`) de l'URL courante.
// Retourne null si on n'est pas sur une route publique.
function extractPublicToken() {
  if (typeof window === 'undefined') return null;
  const p = window.location.pathname || '/';
  const m = p.match(/^\/p\/([A-Za-z0-9_-]+)$/);
  return m ? m[1] : null;
}

// Extrait un éventuel token d'invitation (`/join/<token>`) de l'URL.
function extractJoinToken() {
  if (typeof window === 'undefined') return null;
  const p = window.location.pathname || '/';
  const m = p.match(/^\/join\/([A-Za-z0-9_-]+)$/);
  return m ? m[1] : null;
}

export default function VersionsApp() {
  // ── Route publique lien partagé : court-circuite tout (auth, sidebar, etc.)
  // pour que les destinataires du lien n'aient jamais besoin d'un compte.
  const [publicToken, setPublicToken] = useState(() => extractPublicToken());
  const [joinToken, setJoinToken] = useState(() => extractJoinToken());
  useEffect(() => {
    const onPop = () => { setPublicToken(extractPublicToken()); setJoinToken(extractJoinToken()); };
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
    };
  }, []);
  if (publicToken) {
    return <PublicFicheScreen token={publicToken} />;
  }
  if (joinToken) {
    return <JoinScreen token={joinToken} />;
  }

  return <VersionsAppAuthed />;
}

function VersionsAppAuthed() {
  const { user, loading: authLoading, signOut } = useAuth();
  const isMobile = useMobile();
  const isDesktop = !isMobile;
  // Statut admin — lu depuis la base (public.admin_users via la RPC
  // is_admin()). Passé en prop aux écrans publics (landing / pricing /
  // plugin) qui affichent l'entrée Admin dans leur nav.
  const isAdminUser = useIsAdmin(user);

  // Wrapper signOut → redirect explicite vers la home publique.
  // L'effet de logout plus bas met déjà le hash à #/ et bascule sur
  // 'welcome' (qui rend la landing pour un visiteur via l'auth gate),
  // mais on force ici en plus pour éviter tout flash transitoire et
  // garantir un comportement homogène depuis n'importe quelle page.
  const handleSignOut = useCallback(async () => {
    try { clearIsAdminCache(); } catch { /* ignore */ }
    try { await signOut?.(); } catch { /* ignore */ }
    if (typeof window !== 'undefined') {
      try { window.history.replaceState({ screen: 'home' }, '', '/'); } catch { /* ignore */ }
    }
    // setScreen via la route : l'effet "user null → réaligne le screen"
    // s'occupera de tomber sur 'welcome' (auth-gated → landing publique).
    // On ne fait pas setScreen('home') directement parce que 'home' est
    // déjà le rendu autorisé pour un visiteur (cf. block plus bas).
  }, [signOut]);
  // Initial screen aligné sur l'URL pour éviter un flash dashboard→landing
  // au cold start quand le pathname demande explicitement `/` ou `/home`.
  // Les écrans qui dépendent d'un state volatil (fiche, loading) retombent
  // sur welcome dans routeInit ci-dessous.
  const [screen, setScreen] = useState(() => {
    if (typeof window === 'undefined') return 'welcome';
    const p = window.location.pathname;
    if (p === '/' || p === '/home') return 'home';
    if (p === '/exemple' || p === '/sample-report') return 'sample';
    if (p === '/pricing' || p === '/tarifs') return 'pricing';
    if (p === '/plugin') return 'plugin';
    if (p === '/admin') return 'admin';
    if (p === '/privacy') return 'privacy';
    if (p === '/terms') return 'terms';
    // /auth/callback → on reste sur welcome, l'auth gate affiche son spinner
    // pendant que Supabase échange le code, puis on redirige vers /dashboard.
    return 'welcome';
  });
  // Visiteurs non connectés : landing page par défaut, AuthScreen sur clic CTA.
  const [showAuth, setShowAuth] = useState(false);
  const [homeAddOpen, setHomeAddOpen] = useState(false);
  // Contexte transmis à AddModal pour ouvrir directement dans un flow
  // précis (ex. "Nouveau titre" depuis la hero, "Ajouter version" depuis
  // le menu d'une version). Null = modale ouverte sur le menu racine.
  const [addModalCtx, setAddModalCtx] = useState(null);
  // États app-level pour la modale AddModal + prompt "Nouveau projet"
  // utilisés quand l'utilisateur clique sur "+ AJOUTER" dans la sidebar
  // depuis un écran autre que la Home (WelcomeHome a sa propre copie).
  const [newProjectOpenApp, setNewProjectOpenApp] = useState(false);
  const [newProjectValueApp, setNewProjectValueApp] = useState('');
  const newProjectInputRefApp = useRef(null);
  const pendingNewTrackRefApp = useRef(false);
  // Modale membres ouverte DEPUIS LA FICHE (clic sur les avatars du side
  // panel, via onManageMembers). WelcomeHome garde sa propre instance :
  // ces states sont la copie app-level, comme newProjectOpenApp.
  // Crash prod 2026-09-04 (Safari, "Can't find variable:
  // setMembersInitialTrack") : onManageMembers appelait les setters de
  // WelcomeHome, hors de portée ici → ReferenceError au clic.
  const [membersTargetApp, setMembersTargetApp] = useState(null);
  const [membersInitialTrackApp, setMembersInitialTrackApp] = useState(null);
  const isHashSyncRef = useRef(false);
  const routeInitRef = useRef(false);
  const prevScreenRef = useRef(null);
  const scrollContentRef = useRef(null);

  // À chaque changement d'écran, on remonte tout en haut. Selon le layout :
  //   - Layout sidebar (fiche, admin…) : c'est scrollContentRef qui scrolle
  //   - Layout topbar (welcome) : c'est window qui scrolle (page nat. fluide)
  // On reset les deux pour couvrir les 2 cas sans avoir à connaître ici
  // quel layout est en cours.
  useEffect(() => {
    if (scrollContentRef.current) {
      scrollContentRef.current.scrollTop = 0;
    }
    try { window.scrollTo({ top: 0, left: 0, behavior: 'auto' }); } catch {}
  }, [screen]);

  // Le BottomPlayer est maintenant toujours rendu (sticky bas de page),
  // y compris sur la home — donc plus besoin de retirer la réserve de 68px :
  // on la garde partout pour que rien ne passe sous la barre du player.
  // Exception (refonte 2026-04-30) : sur la page admin, le BottomPlayer
  // n'est PAS rendu (cf. condition `screen !== 'admin'` plus bas), donc
  // on applique body.no-bottom-player pour libérer la réserve de 68px et
  // que le contenu admin descende jusqu'au fond du viewport.
  useEffect(() => {
    if (screen === 'admin') {
      document.body.classList.add('no-bottom-player');
      return () => document.body.classList.remove('no-bottom-player');
    }
    document.body.classList.remove('no-bottom-player');
    return undefined;
  }, [screen]);

  // ── Halo ambient — 3 calques qui crossfade au fil de la session ──
  // On insère un conteneur `.ambient-halo` en premier enfant du body,
  // avec 3 calques `.ambient-layer` à l'intérieur. Chaque calque a une
  // variante différente (5 positions/couleurs préréglées dans
  // MockupStyles) — le CSS les fait crossfader sur ~90s avec des
  // offsets, donc la teinte/position dominante évolue lentement tout
  // au long de la session. Le fade-in global se fait via `.loaded`
  // ajoutée au next frame, pour éviter un flash dès le mount.
  useEffect(() => {
    const halo = document.createElement('div');
    halo.className = 'ambient-halo';
    halo.setAttribute('aria-hidden', 'true');
    // 3 variantes distinctes pour éviter les doublons — on shuffle
    // l'ordre [0..4] et on prend les 3 premières.
    const variants = [0, 1, 2, 3, 4]
      .sort(() => Math.random() - 0.5)
      .slice(0, 3);
    variants.forEach((v) => {
      const layer = document.createElement('div');
      layer.className = 'ambient-layer';
      layer.setAttribute('data-variant', String(v));
      halo.appendChild(layer);
    });
    document.body.insertBefore(halo, document.body.firstChild);
    const raf = requestAnimationFrame(() => halo.classList.add('loaded'));
    return () => {
      cancelAnimationFrame(raf);
      halo.remove();
    };
  }, []);

  // ── Layer global d'orbes colorées — continu d'une page à l'autre ──
  // Avant : chaque page (landing/pricing/dashboard) avait son propre
  // ::before / div d'orbes avec sa propre keyframe. Au switch entre
  // pages, l'animation redémarrait de zéro → teinte/saturation/position
  // différentes, déconnexion visuelle.
  // Maintenant : un seul élément monté au body, animation continue sur
  // toute la session. Persiste à travers les changements d'écran. CSS
  // dans MockupStyles (.va-bg-orbs).
  useEffect(() => {
    const orbs = document.createElement('div');
    orbs.className = 'va-bg-orbs';
    orbs.setAttribute('aria-hidden', 'true');
    document.body.insertBefore(orbs, document.body.firstChild);
    return () => orbs.remove();
  }, []);
  const [config, setConfig] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);
  // Deep-link fiche : quand on arrive sur `#/fiche/{trackId}/{versionId}`
  // (refresh ou lien direct), on stocke les IDs ici tant que `projects` n'est
  // pas chargé. Une fois la liste prête, l'effet plus bas appelle
  // handleOpenFiche pour matérialiser config + analysisResult.
  const [pendingFiche, setPendingFiche] = useState(null);
  // Contexte de l'écran intention : { jobId, perception, inheritedIntent, audioHash } ou null.
  // Posé par handleAwaitingIntent quand le backend bascule en 'awaiting_intent'.
  const [intentCtx, setIntentCtx] = useState(null);
  const [askOpen, setAskOpen] = useState(false);
  // Les réglages s'ouvrent en modale (plus de page dédiée)
  const [reglagesOpen, setReglagesOpen] = useState(false);
  // Modale feedback testeurs (questionnaire 6Q, cf. FeedbackModal.jsx +
  // migration 024_feedback.sql). Ouverte depuis le DashboardRail.
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  // When adding a new version from an existing track, we prefill the title
  // and, after analysis completes, auto-open that track's folder in Versions tab
  const [prefillTitle, setPrefillTitle] = useState("");
  const [autoSelectTrackTitle, setAutoSelectTrackTitle] = useState("");
  // ── Projets : état centralisé au niveau App ──
  // Une seule requête loadProjects() par mutation, distribuée à Sidebar
  // et WelcomeHome en props. Évite la triple fetch qu'on avait avant
  // (Sidebar + Home + App pour l'onboarding gate).
  //
  // Cache localStorage pour un rendu instantané : au mount, on lit la
  // dernière liste connue et on l'affiche direct. La requête live met
  // à jour silencieusement derrière (stale-while-revalidate).
  //
  // Durcissement 2026-07-21 : le cache est rendu AVANT le fetch réseau, donc
  // un cache de forme invalide (écrit par un build antérieur, ou tronqué) fait
  // crasher le render → page blanche à CHAQUE chargement, et le crash empêche
  // le refresh d'écraser le cache : la panne s'auto-entretient et survit aux
  // déploiements (le poison est dans le navigateur de l'utilisateur).
  // Deux protections : (1) clé VERSIONNÉE — tout cache d'un ancien format est
  // ignoré d'office, (2) validation de forme au lieu d'un simple Array.isArray.
  const [projects, setProjects] = useState(() => readProjectsCache());
  const [projectsLoaded, setProjectsLoaded] = useState(() => {
    try { return !!localStorage.getItem(PROJECTS_CACHE_KEY); } catch { return false; }
  });
  const [projectsRefreshKey, setProjectsRefreshKey] = useState(0);
  const refreshProjects = useCallback(() => setProjectsRefreshKey((k) => k + 1), []);

  // Projet courant — synchronise l'accordéon Sidebar ↔ Home, et sert de scope
  // à la playlist du player. null = aucun projet explicitement ouvert.
  const [currentProjectId, setCurrentProjectId] = useState(null);

  // Au 1ᵉʳ chargement complet des projets, on ouvre automatiquement le premier
  // pour éviter le déséquilibre visuel avec la colonne de recommandations qui
  // est plus haute. Si l'utilisateur ferme manuellement ensuite, on respecte
  // son choix (un ref bloque les ré-ouvertures auto après le 1ᵉʳ run).
  const initialProjectOpenedRef = useRef(false);
  useEffect(() => {
    if (initialProjectOpenedRef.current) return;
    if (projectsLoaded && projects.length > 0) {
      // Auto-open seulement si rien n'est déjà ouvert (sinon on respecte
      // l'état hérité, ex: deep-link vers une fiche). Le ref se ferme
      // dans tous les cas pour ne plus jamais rouvrir auto après ça.
      if (currentProjectId === null) {
        setCurrentProjectId(projects[0].id);
      }
      initialProjectOpenedRef.current = true;
    }
  }, [projectsLoaded, projects, currentProjectId]);

  // Solde de crédits — affiché dans le rail bas-gauche du dashboard
  // (à côté de Réglages / Déconnexion). Mirror exact de la logique
  // Sidebar (RPC get_or_create_user_credits, balance_remaining).
  // IMPORTANT : ce hook DOIT être AVANT tous les early returns plus bas
  // (sinon Rules of Hooks → React #300 → page blanche).
  const [userCredits, setUserCredits] = useState(null);
  const refreshCredits = useCallback(async () => {
    if (!user?.id) { setUserCredits(null); return null; }
    try {
      const { data, error } = await supabase.rpc('get_or_create_user_credits');
      if (error) {
        console.warn('[topbar] get_or_create_user_credits failed:', error.message);
        return null;
      }
      const row = Array.isArray(data) ? data[0] : data;
      const bal = Number(row?.balance_remaining);
      const next = Number.isFinite(bal) ? bal : null;
      setUserCredits(next);
      return next;
    } catch (e) {
      console.warn('[topbar] credits fetch threw:', e);
      return null;
    }
  }, [user?.id]);
  useEffect(() => {
    let cancelled = false;
    if (!user?.id) { setUserCredits(null); return; }
    (async () => {
      const next = await refreshCredits();
      if (cancelled) return;
      // refreshCredits met déjà à jour userCredits ; ce return ne fait que
      // documenter l'intention.
      void next;
    })();
    return () => { cancelled = true; };
  }, [user?.id, refreshCredits]);

  // Gate "0 crédit" — bloque l'ajout de titre / version / lancement
  // d'analyse quand le solde est à zéro. La création de projet reste
  // libre. CTA → page tarifs (#/pricing).
  const [noCreditsOpen, setNoCreditsOpen] = useState(false);
  const requireCredits = useCallback(() => {
    // userCredits === null = chargement ou erreur de fetch : on laisse
    // passer (le backend re-checkera et renverra 402 si vraiment 0).
    if (userCredits != null && userCredits < 1) {
      setNoCreditsOpen(true);
      return false;
    }
    return true;
  }, [userCredits]);
  // Écoute le 402 émis par LoadingScreen quand le backend refuse une
  // analyse pour cause de solde insuffisant (ex. course entre check FE et
  // débit serveur). Ouvre la même modale que la pré-vérification.
  useEffect(() => {
    const onNoCredits = () => {
      setNoCreditsOpen(true);
      refreshCredits();
    };
    window.addEventListener('versions:no-credits', onNoCredits);
    return () => window.removeEventListener('versions:no-credits', onNoCredits);
  }, [refreshCredits]);

  // ── User profile (avatar, prénom…) ──
  const [userProfile, setUserProfile] = useState(null);
  useEffect(() => {
    if (!user) { setUserProfile(null); return; }
    supabase
      .from("profiles")
      .select("prenom, nom, avatar_url, default_daw, langue")
      .eq("id", user.id)
      .single()
      .then(({ data }) => { if (data) setUserProfile(data); })
      .catch(() => {});
  }, [user]);

  // ── Logout : reset de la route + forçage retour Home ──────
  // Après un signOut, on veut que le prochain login arrive toujours sur la
  // Home, même si la dernière page visitée avant déconnexion était une fiche
  // (`/fiche/...`). Sans ça, routeInitRef reste à true et le pathname reste
  // sur la fiche → au relogin, on voit soit la fiche figée, soit welcome avec
  // une URL incohérente. On neutralise tout ici pour repartir propre.
  //
  // IMPORTANT : on ne touche à rien pendant `authLoading`. Au cold start, `user`
  // est null le temps que Supabase résolve la session — sans ce guard, on
  // efface le pathname (`/dashboard`, `/fiche/...`) avant même que l'auth ne
  // confirme que l'utilisateur est connecté, et il atterrit sur la landing au
  // lieu de la page qu'il rafraîchissait.
  useEffect(() => {
    if (authLoading) return;
    if (user) return;
    routeInitRef.current = false;
    if (typeof window !== 'undefined' && window.location.pathname !== '/') {
      const p = window.location.pathname;
      // Routes publiques accessibles aux visiteurs : on les laisse intactes
      // pour ne pas casser un deep-link landing/sample/privacy/terms.
      const isPublicRoute = p === '/home' || p === '/exemple' || p === '/sample-report' || p === '/privacy' || p === '/terms';
      // /auth/callback : on laisse Supabase finir d'échanger le code, le
      // useEffect ci-dessous redirigera dès que `user` sera hydraté.
      const isAuthCallback = p === '/auth/callback';
      // /update-password : page recovery, rendue par un check spécifique en
      // amont (cf. juste après l'auth gate). On laisse l'URL intacte pour
      // que le check fonctionne au cold start (refresh, lien depuis email).
      const isUpdatePassword = p === '/update-password';
      // /confirm-delete-account : visiteur arrivant via mail de confirmation
      // (peut ne plus avoir de session active). On laisse l'URL intacte.
      const isConfirmDelete = p === '/confirm-delete-account';
      if (!isPublicRoute && !isAuthCallback && !isUpdatePassword && !isConfirmDelete) {
        window.history.replaceState({ screen: 'welcome' }, '', '/');
      }
    }
    // Pour les visiteurs, on aligne `screen` sur la route publique courante :
    // sample/privacy/terms gardent leur écran (rendus plein écran), tout
    // le reste tombe sur 'welcome' (l'auth gate rendra la landing à sa place).
    if (typeof window !== 'undefined') {
      const p = window.location.pathname;
      let targetScreen = 'welcome';
      if (p === '/exemple' || p === '/sample-report') targetScreen = 'sample';
      else if (p === '/privacy') targetScreen = 'privacy';
      else if (p === '/terms') targetScreen = 'terms';
      if (screen !== targetScreen) {
        isHashSyncRef.current = true;
        setScreen(targetScreen);
      }
    } else if (screen !== 'welcome') {
      isHashSyncRef.current = true;
      setScreen('welcome');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  // ── Routing : lecture initiale de l'URL ─────────────────
  // Quand l'utilisateur est connecté, on aligne l'écran sur le pathname courant.
  // Les écrans qui dépendent d'un state en mémoire (loading, fiche) retombent
  // sur welcome si on y arrive directement (refresh, lien entrant).
  useEffect(() => {
    if (!user || routeInitRef.current) return;
    routeInitRef.current = true;
    const rawPath = window.location.pathname;
    // Retour post-login (posé par goAuth quand le visiteur lance le login
    // depuis /plugin ou /pricing) : on le ramène là où il était au lieu du
    // dashboard. Couvre les DEUX flows : email/mdp (même onglet, l'URL est
    // déjà passée à /dashboard par goAuth) ET OAuth Google (retour sur
    // /auth/callback — sessionStorage survit au redirect). Les pages hors
    // arborescence (recovery, confirm-delete) gardent la priorité.
    if (rawPath !== '/update-password' && rawPath !== '/confirm-delete-account') {
      let returnTo = null;
      try {
        returnTo = window.sessionStorage.getItem('vs_return_to');
        if (returnTo) window.sessionStorage.removeItem('vs_return_to');
      } catch { /* stockage indispo → flow normal */ }
      const backScreen = returnTo ? PATH_SCREEN[returnTo] : null;
      if (backScreen) {
        window.history.replaceState({ screen: backScreen }, '', returnTo);
        if (screen !== backScreen) {
          isHashSyncRef.current = true;
          setScreen(backScreen);
        }
        return;
      }
    }
    // Retour OAuth en flow PKCE : Supabase a déjà échangé le `?code=...` au
    // mount du client. Au moment où `user` est posé, on peut nettoyer l'URL
    // et atterrir sur le dashboard.
    if (rawPath === '/auth/callback') {
      const targetPath = SCREEN_PATH['welcome'];
      window.history.replaceState({ screen: 'welcome' }, '', targetPath);
      if (screen !== 'welcome') {
        isHashSyncRef.current = true;
        setScreen('welcome');
      }
      return;
    }
    // /update-password : on laisse l'URL et le screen intactes — l'arbre de
    // rendu détecte ce pathname juste après l'auth gate et monte
    // UpdatePasswordScreen plein écran. Sans ce return, le fallback ci-dessous
    // (`parsePath` ne connaît pas /update-password → fallback 'welcome')
    // remplacerait l'URL par /dashboard et l'utilisateur sortirait du flow.
    if (rawPath === '/update-password') {
      return;
    }
    // /confirm-delete-account : idem update-password, route hors arborescence
    // "screen". Ne pas réécrire l'URL vers /dashboard.
    if (rawPath === '/confirm-delete-account') {
      return;
    }
    // Compat : /reglages ouvre désormais la modale et renvoie sur le dashboard.
    if (rawPath === '/reglages') {
      setReglagesOpen(true);
      window.history.replaceState({ screen: 'welcome' }, '', '/dashboard');
      if (screen !== 'welcome') {
        isHashSyncRef.current = true;
        setScreen('welcome');
      }
      return;
    }
    // `/` explicite (logo, lien direct) → landing même connecté. On laisse
    // tel quel — c'était l'ancien comportement avec `#/`.
    const parsed = parsePath(rawPath);
    // Deep-link fiche : on stocke les IDs et on laisse le résolveur ci-dessous
    // matérialiser config + analysisResult quand `projects` sera chargé.
    if (parsed.screen === 'fiche' && parsed.trackId) {
      setPendingFiche({ trackId: parsed.trackId, versionId: parsed.versionId || null });
      return;
    }
    const target = parsed.screen || 'welcome';
    // Fiche sans IDs / loading sans state → fallback dashboard.
    const safe = (target === 'fiche' || target === 'loading') ? 'welcome' : target;
    const targetPath = SCREEN_PATH[safe];
    if (window.location.pathname !== targetPath) {
      window.history.replaceState({ screen: safe }, '', targetPath);
    }
    if (safe !== screen) {
      isHashSyncRef.current = true;
      setScreen(safe);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // ── Routing : screen → URL ──────────────────────────────
  // Chaque changement d'écran pousse une nouvelle entrée dans l'historique,
  // sauf quand c'est nous-mêmes qui venons de le setter en réaction à popstate.
  // La fiche encode son trackId/versionId pour permettre un reload propre.
  useEffect(() => {
    if (!user) return;
    // /update-password et /confirm-delete-account : pages hors arborescence
    // "screen". On ne touche PAS à l'URL pour ne pas écraser ce path par
    // /dashboard (qui serait le nextPath déduit du screen courant 'welcome').
    if (typeof window !== 'undefined') {
      const p = window.location.pathname;
      if (p === '/update-password' || p === '/confirm-delete-account') {
        return;
      }
    }
    if (isHashSyncRef.current) {
      isHashSyncRef.current = false;
      prevScreenRef.current = screen;
      return;
    }
    const nextPath = buildPath(screen, config);
    if (window.location.pathname !== nextPath) {
      // loading → fiche : on remplace l'entrée "loading" par "fiche" pour que
      // "Précédent" depuis la fiche saute directement avant l'analyse au lieu
      // de repasser sur l'écran de chargement.
      // Idem fiche → fiche (changement de version) : on garde une seule
      // entrée "fiche" dans l'historique pour que Back ramène au dashboard.
      const shouldReplace =
        (prevScreenRef.current === 'loading' && screen === 'fiche') ||
        (prevScreenRef.current === 'fiche' && screen === 'fiche');
      if (shouldReplace) {
        window.history.replaceState({ screen }, '', nextPath);
      } else {
        window.history.pushState({ screen }, '', nextPath);
      }
      // Meta Pixel : ré-émet PageView à chaque navigation SPA. Sans ça,
      // l'index.html ne tire PageView qu'au tout premier chargement et
      // Meta ne voit qu'une "page vue" par session, même si l'utilisateur
      // navigue dans / → /dashboard → /fiche/... → /pricing.
      trackPixelPageView();
    }
    prevScreenRef.current = screen;
  }, [screen, user, config]);

  // ── Routing : URL → screen (Précédent/Suivant) ───────────
  useEffect(() => {
    const onPop = () => {
      const current = window.location.pathname || '/';
      const parsed = parsePath(current);
      // Deep-link fiche via Back/Forward : si le state actuel ne correspond
      // pas, on délègue au résolveur via setPendingFiche.
      if (parsed.screen === 'fiche' && parsed.trackId) {
        // Le segment URL peut être un UUID (anciens liens) ou un slug
        // (nouveau format). On compare avec le config courant sous les
        // deux formes pour éviter un re-fetch inutile.
        const trackMatches =
          parsed.trackId === config?.trackId
          || (!!config?.title && slugify(config.title) === slugify(parsed.trackId));
        const versionMatches =
          !parsed.versionId
          || parsed.versionId === config?.versionId
          || (!!config?.version && slugify(config.version) === slugify(parsed.versionId));
        const matches = trackMatches && versionMatches;
        if (matches && analysisResult) {
          isHashSyncRef.current = true;
          setScreen('fiche');
          return;
        }
        setPendingFiche({ trackId: parsed.trackId, versionId: parsed.versionId || null });
        return;
      }
      const target = parsed.screen || 'welcome';
      // Si on revient sur une vue qui a besoin de state volatil et qu'on ne l'a
      // plus (recharge après précédent), on retombe proprement sur welcome.
      const safe =
        (target === 'fiche' && !analysisResult) ? 'welcome'
        : (target === 'loading' && !config) ? 'welcome'
        : target;
      if (safe !== target) {
        window.history.replaceState({ screen: safe }, '', SCREEN_PATH[safe]);
      }
      isHashSyncRef.current = true;
      setScreen(safe);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [config, analysisResult]);

  // ── Meta Pixel : re-fire PageView aussi sur Précédent/Suivant ────────
  // Le useEffect ci-dessus (pushState quand screen change) couvre la
  // navigation programmatique, mais Back/Forward navigateur n'y passe pas —
  // c'est popstate qui setScreen, et le useEffect pushState voit pathname
  // déjà égal à nextPath → pas de tir. On ajoute donc un listener popstate
  // dédié au tracking.
  useEffect(() => {
    const onPopTrack = () => trackPixelPageView();
    window.addEventListener('popstate', onPopTrack);
    return () => window.removeEventListener('popstate', onPopTrack);
  }, []);

  // ── Attribution acquisition (migration 051) : capture les UTM /
  // fbclid / referrer de l'URL d'arrivée UNE FOIS au mount (avant le
  // gate auth — ce composant monte que l'utilisateur soit connecté ou
  // non), mémorise la provenance first-touch en localStorage et logge
  // la visite anonyme en DB. Cf. src/lib/attribution.js.
  useEffect(() => {
    captureAttribution();
  }, []);

  // ── Deep-link fiche : résolution `pendingFiche` → fiche réelle ────────
  // Posé par routeInit ou popstate quand l'URL est `#/fiche/{seg}/...`.
  // On attend `projects` pour matcher les segments (cache localStorage
  // hydrate déjà la liste à l'init, donc le délai est en général invisible).
  // Le segment peut être un slug (nouveau format) ou un UUID (rétrocompat).
  // Si la version n'est pas précisée, on prend la dernière du titre.
  useEffect(() => {
    if (!pendingFiche || !user || !projectsLoaded) return;
    let alive = true;
    (async () => {
      const { trackId: trackSeg, versionId: versionSeg } = pendingFiche;
      const allTracks = projects.flatMap(p => p.tracks || []);
      // 1) Match direct par UUID (anciennes URLs)
      let track = allTracks.find(t => t.id === trackSeg);
      // 2) Fallback : match par slug du titre. Premier match gagne (les
      //    titres sont uniques par utilisateur dans un projet, et en cas
      //    de collision inter-projet on préfère le déterminisme).
      if (!track && trackSeg) {
        const target = slugify(trackSeg);
        if (target) track = allTracks.find(t => slugify(t.title) === target);
      }
      if (!track) {
        if (!alive) return;
        setPendingFiche(null);
        isHashSyncRef.current = true;
        setScreen('welcome');
        window.history.replaceState({ screen: 'welcome' }, '', SCREEN_PATH['welcome']);
        return;
      }
      let v = null;
      if (versionSeg) {
        v = track.versions?.find(ver => ver.id === versionSeg);
        if (!v) {
          const target = slugify(versionSeg);
          v = track.versions?.find(ver => slugify(ver.name) === target);
        }
      }
      if (!v) v = track.versions?.[track.versions.length - 1];
      if (!v) {
        if (!alive) return;
        setPendingFiche(null);
        isHashSyncRef.current = true;
        setScreen('welcome');
        window.history.replaceState({ screen: 'welcome' }, '', SCREEN_PATH['welcome']);
        return;
      }
      const saved = await getAnalysis(track.id, v.id);
      if (!alive) return;
      if (v.storagePath) {
        loadPlayer(track.title, v.name, v.storagePath);
      }
      // DAW priority : (1) DAW choisi pour CETTE analyse (meta), (2) profil
      // user, (3) fallback Logic Pro. Avant : hardcodé 'Logic Pro' → le chat
      // recommandait des actions Logic Pro même quand l'utilisateur avait
      // choisi LUNA / Pro Tools / etc. dans les réglages du titre.
      const ar = saved || v.analysisResult || null;
      const dawForVersion = ar?.meta?.daw || userProfile?.default_daw || 'Logic Pro';
      setConfig({
        title: track.title,
        version: v.name,
        trackId: track.id,
        versionId: v.id,
        daw: dawForVersion,
      });
      setAnalysisResult(ar);
      setPendingFiche(null);
      isHashSyncRef.current = true;
      setScreen('fiche');
      // Normalise l'URL en slug si on était arrivé en UUID ou sans version.
      const targetPath = buildPath('fiche', {
        title: track.title,
        version: v.name,
        trackId: track.id,
        versionId: v.id,
      });
      if (window.location.pathname !== targetPath) {
        window.history.replaceState({ screen: 'fiche' }, '', targetPath);
      }
    })();
    return () => { alive = false; };
  }, [pendingFiche, projects, projectsLoaded, user]);

  // ── Helper partagé recovery + watcher : navigation directe vers une
  //    fiche persistée en DB (palier 4) sans passer par le résolveur
  //    pendingFiche. On bypass volontairement parce que le résolveur
  //    s'appuie sur `projects` en state — qui peut ne pas avoir le track
  //    fraîchement persisté → fallback welcome non désiré. Ici on fetch
  //    loadProjects() en direct et on construit config + analysisResult
  //    soi-même.
  const navigateToPersistedFiche = useCallback(async (trackId, versionId) => {
    if (!trackId) return false;
    let freshProjects = [];
    try { freshProjects = await loadProjects(); } catch { /* noop */ }
    const allTracks = freshProjects.flatMap(p => p.tracks || []);
    const track = allTracks.find(t => t.id === trackId);
    if (!track) {
      console.warn('[recovery] track not found in DB after persist:', trackId);
      return false;
    }
    let v = versionId ? track.versions?.find(ver => ver.id === versionId) : null;
    if (!v) v = track.versions?.[track.versions.length - 1];
    if (!v) return false;
    const saved = await getAnalysis(track.id, v.id);
    const ar = saved || v.analysisResult || null;
    if (v.storagePath) loadPlayer(track.title, v.name, v.storagePath);
    const dawForVersion = ar?.meta?.daw || userProfile?.default_daw || 'Logic Pro';
    setConfig({
      title: track.title,
      version: v.name,
      trackId: track.id,
      versionId: v.id,
      daw: dawForVersion,
    });
    setAnalysisResult(ar);
    savedRef.current = true;
    isHashSyncRef.current = true;
    setScreen('fiche');
    const targetPath = buildPath('fiche', {
      title: track.title,
      version: v.name,
      trackId: track.id,
      versionId: v.id,
    });
    if (window.location.pathname !== targetPath) {
      window.history.replaceState({ screen: 'fiche' }, '', targetPath);
    }
    refreshProjects();
    return true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile, refreshProjects]);

  // ── Recovery flow : analyse en cours retrouvée au mount ───────────────
  // Si l'utilisateur a refresh / fermé l'onglet / navigué pendant une
  // analyse, le LoadingScreen est démonté mais le backend continue. On
  // a sauvegardé { jobId, audioHash, title, ... } en localStorage juste
  // après le POST /start (cf. pendingJob.js). À chaque mount, dès que
  // user+projects sont prêts, on regarde ce qu'il faut en faire :
  //   1) Job toujours en cours côté backend → resume polling
  //      (setConfig({ resumeJobId }) + setScreen('loading'))
  //   2) Job terminé avec persistedTrackId → deep-link vers la fiche
  //   3) Job expiré côté backend (404, mémoire wipe 60s post-terminal) →
  //      lookup DB par audioHash (la fiche est persistée par le palier 4)
  //   4) Rien trouvé → clear le pending (le cron Supabase refundra)
  //
  // Guard `recoveryRanRef` : on ne tente la récupération qu'une fois par
  // session — sinon, naviguer pendant qu'une analyse tourne re-déclencherait
  // le flow et casserait l'expérience.
  const recoveryRanRef = useRef(false);
  useEffect(() => {
    if (!user || !projectsLoaded) return;
    if (recoveryRanRef.current) return;
    // On NE PAS run le recovery si on est déjà sur /analyse ou /fiche : ces
    // écrans pilotent eux-mêmes leur lifecycle (handleAnalyze ou pendingFiche).
    if (screen === 'loading' || screen === 'fiche') {
      recoveryRanRef.current = true;
      return;
    }
    // Si un deep-link fiche est en cours de résolution (URL /fiche/...),
    // on ne contourne pas — laisse le résolveur pendingFiche faire son
    // travail. Le watcher prendra le relais après si pending existe.
    if (pendingFiche) {
      recoveryRanRef.current = true;
      return;
    }
    const pending = getPending();
    if (!pending) {
      recoveryRanRef.current = true;
      return;
    }
    recoveryRanRef.current = true;
    let alive = true;
    (async () => {
      const { jobId, audioHash, title, version, projectId, vocalType, uploadType } = pending;
      // 1) Demande le status au backend. 3 issues possibles :
      //    - 200 + job vivant → on resume le polling sans relancer d'analyse
      //    - 200 + persistedTrackId → on déeplink direct vers la fiche
      //    - 404 (job expiré en mémoire) → fallback DB lookup par audioHash
      let job = null;
      let httpStatus = 0;
      try {
        const res = await apiFetch(`/api/analyze/status/${jobId}`);
        httpStatus = res.status;
        if (res.ok) job = await res.json();
      } catch (e) {
        console.warn('[recovery] status fetch failed:', e?.message);
      }
      if (!alive) return;

      const tryNavigate = async (trackId, versionId) => {
        clearPending();
        return await navigateToPersistedFiche(trackId, versionId);
      };

      // Helper : lookup DB par audioHash (palier 4 a déjà persisté la
      // version). On utilise findDuplicateAudio avec les params sauvegardés
      // pour resserrer le match (intent, genre, bpm, uploadType).
      const dbLookupAndNavigate = async () => {
        if (!audioHash) return false;
        try {
          const dup = await findDuplicateAudio(title || '', audioHash, {
            uploadType: uploadType || 'mix',
          });
          if (!dup?.trackId) return false;
          return await tryNavigate(dup.trackId, dup.versionId || null);
        } catch (e) {
          console.warn('[recovery] DB lookup failed:', e?.message);
          return false;
        }
      };

      // Cas 404 : le job a été nettoyé. Soit terminé (palier 4 a persisté),
      // soit crashé (palier 2 refund). Tentative DB ; sinon, on clear et on
      // laisse le cron faire son travail.
      if (httpStatus === 404 || !job) {
        const ok = await dbLookupAndNavigate();
        if (!ok) clearPending();
        return;
      }

      // Cas erreur backend : on clear, le refund a déjà été fait côté serveur.
      if (job.status === 'error') {
        clearPending();
        return;
      }

      // Cas terminal heureux : on essaie d'abord les IDs persist backend,
      // sinon fallback DB lookup.
      const isComplete = job.status === 'complete' || job.stage === 'all_done';
      if (isComplete) {
        if (job.persistedTrackId) {
          await tryNavigate(job.persistedTrackId, job.persistedVersionId || null);
          return;
        }
        if (pending.persistedTrackId) {
          await tryNavigate(pending.persistedTrackId, pending.persistedVersionId || null);
          return;
        }
        const ok = await dbLookupAndNavigate();
        if (!ok) clearPending();
        return;
      }

      // Cas job encore vivant (running, awaiting_intent…) : on resume le
      // polling SANS reposter /start. Le LoadingScreen sait gérer ce mode
      // via config.resumeJobId. On recharge aussi un minimum de config pour
      // que l'écran affiche le titre + version courants.
      console.log('[recovery] resuming job', jobId, 'status=', job.status, 'stage=', job.stage);
      setConfig({
        resumeJobId: jobId,
        title: title || '',
        version: version || '',
        projectId: projectId || null,
        audioHash: audioHash || null,
        vocalType: vocalType || 'vocal',
        uploadType: uploadType || 'mix',
      });
      setAnalysisResult(null);
      savedRef.current = false;
      setScreen('loading');
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, projectsLoaded]);

  // ── Pending watcher : analyse en cours alors que l'utilisateur a
  //    navigué hors de l'écran de chargement (cancel, back, click logo).
  // Le LoadingScreen unmount tue son polling. Sans ce watcher, l'utilisateur
  // ne verrait jamais le résultat sans refresh manuel. On polle toutes les
  // 5s tant qu'on a une entrée pending ET qu'on n'est pas sur loading/fiche.
  // Dès qu'on détecte la complétion, on deeplink vers la fiche.
  const pendingWatchRef = useRef(null);
  useEffect(() => {
    // On laisse le LoadingScreen / FicheScreen piloter leur cycle ;
    // le watcher ne tourne que sur les autres écrans.
    if (!user) return;
    if (screen === 'loading' || screen === 'fiche') return;
    if (!getPending()) return;

    let stopped = false;
    const tick = async () => {
      if (stopped) return;
      const pending = getPending();
      if (!pending) return;
      const { jobId, audioHash, title, uploadType } = pending;
      const lookupAndNav = async () => {
        if (!audioHash) return false;
        try {
          const dup = await findDuplicateAudio(title || '', audioHash, {
            uploadType: uploadType || 'mix',
          });
          if (!dup?.trackId) return false;
          clearPending();
          return await navigateToPersistedFiche(dup.trackId, dup.versionId || null);
        } catch { return false; }
      };
      try {
        const res = await apiFetch(`/api/analyze/status/${jobId}`);
        if (res.status === 404) {
          const ok = await lookupAndNav();
          if (!ok) clearPending();
          return;
        }
        if (!res.ok) return; // erreur transitoire — on réessaie au tick suivant
        const job = await res.json();
        if (job.status === 'error') {
          clearPending();
          return;
        }
        const isComplete = job.status === 'complete' || job.stage === 'all_done';
        if (isComplete) {
          const trackId = job.persistedTrackId || pending.persistedTrackId || null;
          const versionId = job.persistedVersionId || pending.persistedVersionId || null;
          if (trackId) {
            clearPending();
            await navigateToPersistedFiche(trackId, versionId);
            return;
          }
          await lookupAndNav();
        }
      } catch (e) { console.warn('[pendingWatch] poll error:', e?.message); }
    };
    // Premier tick immédiat (le mount peut suivre une navigation à l'instant
    // où le job vient de finir), puis interval.
    tick();
    pendingWatchRef.current = setInterval(tick, 5000);
    // Visibility : si l'onglet revient, on force un tick immédiat.
    const onVis = () => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      stopped = true;
      document.removeEventListener('visibilitychange', onVis);
      if (pendingWatchRef.current) {
        clearInterval(pendingWatchRef.current);
        pendingWatchRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, screen]);

  // ── Onboarding gate : true si user connecté mais sans projet ──
  // (needsOnboarding supprimé : plus de modale bloquante au premier login.
  // L'utilisateur arrive sur la Home vide, invité à agir par les CTAs du hero.
  // La création de projet se fait au besoin via AddModal — cf. step
  // 'new-project-name' qui est automatiquement proposé si aucun projet existe.)

  // ── Chargement des projets + onboarding gate + preload premier audio ──
  // Une seule requête par mutation grâce à projectsRefreshKey centralisé.
  // Alimente projects/projectsLoaded, met à jour le cache localStorage,
  // déclenche l'onboarding bloquant et pré-charge le premier audio pour
  // éliminer le délai de première lecture.
  useEffect(() => {
    // Auth pas encore stabilisée : on garde le state initialisé depuis le cache
    // localStorage sans le réinitialiser à []. Sans ce guard, le !user ci-dessous
    // s'exécute avec user=null pendant le chargement auth et efface le cache →
    // tips visibles un instant avant que les projets réapparaissent (layout shift).
    if (authLoading) return;
    if (!user) {
      setProjects([]);
      setProjectsLoaded(false);
      return;
    }
    // DEV preview : force l'affichage de certains états de la Home sans toucher
    // aux données réelles. Utile pour QA sans créer de compte jetable. Actif
    // uniquement en dev (import.meta.env.DEV) — aucune fuite possible en prod.
    //
    // Modes disponibles :
    //   ?preview=home-empty        → 0 projet (Home vide, CTAs d'invitation)
    //   ?preview=home-one-project  → 1 projet vide (0 titre)
    //   ?preview=home-one-track    → 1 projet avec 1 titre non analysé (0 version)
    //
    // NB : les actions CRUD sont "read-only" en mode preview — les mutations
    // peuvent persister en DB mais le useEffect re-écrasera l'état au rechargement.
    if (import.meta.env.DEV) {
      const preview = new URLSearchParams(window.location.search).get('preview');
      if (preview === 'home-empty') {
        setProjects([]);
        setProjectsLoaded(true);
        return;
      }
      if (preview === 'home-one-project') {
        const now = new Date().toISOString();
        setProjects([{
          id: '__preview-project__',
          name: 'Projet test',
          coverGradient: 0,
          coverImageUrl: null,
          position: 0,
          createdAt: now,
          tracks: [],
        }]);
        setProjectsLoaded(true);
        setCurrentProjectId((cur) => cur ?? '__preview-project__');
        return;
      }
      if (preview === 'home-one-track') {
        const now = new Date().toISOString();
        setProjects([{
          id: '__preview-project__',
          name: 'Projet test',
          coverGradient: 0,
          coverImageUrl: null,
          position: 0,
          createdAt: now,
          tracks: [{
            id: '__preview-track__',
            title: 'Mon premier titre',
            coverImageUrl: null,
            createdAt: now,
            projectId: '__preview-project__',
            positionInProject: 0,
            versions: [],
          }],
        }]);
        setProjectsLoaded(true);
        setCurrentProjectId((cur) => cur ?? '__preview-project__');
        return;
      }
    }
    let alive = true;
    loadProjects().then((list) => {
      if (!alive) return;
      const next = list || [];
      setProjects(next);
      setProjectsLoaded(true);
      try { localStorage.setItem(PROJECTS_CACHE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      if (next.length === 0) return;
      const firstProject = next[0];
      // NB : on ne précharge plus l'audio du 1ᵉʳ morceau au boot.
      // L'utilisateur n'écoute pas systématiquement dès la connexion,
      // ça consommait de l'egress Supabase pour rien. Le preload ciblé
      // se fait désormais à l'ouverture d'une fiche (preloadTrackVersions).
      if (firstProject?.id) setCurrentProjectId((cur) => cur ?? firstProject.id);
    }).catch(() => {});
    return () => { alive = false; };
  }, [user, projectsRefreshKey, authLoading]);

  // ── Language ──
  // Priorité de chargement : localStorage > profile.langue Supabase > navigator.language > 'fr'
  // L'init est SYNCHRONE pour éviter un flash FR→EN au démarrage.
  const detectInitialLang = () => {
    try {
      const stored = localStorage.getItem("versions_lang");
      if (stored === "fr" || stored === "en") return stored;
    } catch {}
    // Fallback : langue du navigateur (ex. "en-US" → "en")
    try {
      const nav = (typeof navigator !== "undefined" && (navigator.language || navigator.userLanguage)) || "";
      if (nav.toLowerCase().startsWith("en")) return "en";
    } catch {}
    return "fr";
  };
  const [lang, setLangState] = useState(detectInitialLang);

  // Déclare la langue RÉELLE de la page au navigateur (2026-07-22).
  // Enjeu : quand `<html lang>` ne correspond pas au contenu, les navigateurs
  // proposent/déclenchent leur traduction automatique. Or celle-ci réécrit les
  // nœuds de texte gérés par React, qui lève ensuite « removeChild ... not a
  // child of this node » et démonte tout l'arbre → page blanche (diagnostiqué
  // chez un utilisateur Edge le 2026-07-22 via la table client_errors).
  // index.html pose déjà lang="fr" + translate="no" ; ici on garde l'attribut
  // aligné sur le sélecteur FR/EN pour rester cohérent (SEO + lecteurs d'écran).
  useEffect(() => {
    try { document.documentElement.lang = lang; } catch { /* ignore */ }
  }, [lang]);

  // Sync côté Supabase : quand le profil se charge, si une préférence serveur existe
  // et diffère du cache local, on l'adopte (l'utilisateur a pu changer sur un autre appareil).
  useEffect(() => {
    if (!userProfile) return;
    const serverLang = userProfile.langue;
    if ((serverLang === "fr" || serverLang === "en") && serverLang !== lang) {
      setLangState(serverLang);
      try { localStorage.setItem("versions_lang", serverLang); } catch {}
    }
  }, [userProfile]); // eslint-disable-line react-hooks/exhaustive-deps

  const setLang = useCallback((l) => {
    if (l !== "fr" && l !== "en") return;
    setLangState(l);
    try { localStorage.setItem("versions_lang", l); } catch {}
    // Persiste côté serveur si l'utilisateur est connecté (silent — on n'attend pas)
    if (user?.id) {
      supabase.from("profiles")
        .upsert({ id: user.id, langue: l, updated_at: new Date().toISOString() })
        .then(() => {})
        .catch((e) => console.warn("save langue:", e));
    }
    // Met à jour le state local de userProfile pour rester cohérent
    setUserProfile((prev) => prev ? { ...prev, langue: l } : prev);
  }, [user]);

  const s = STRINGS[lang];
  const t = useCallback((path, vars) => pick(lang, path, vars), [lang]);

  // ── Persistent player state ──
  const [playerState, setPlayerState] = useState(null);
  const resetKeyRef = useRef(0);

  const play = (trackTitle, versionName, storagePath, playlist, currentIdx, keepProgress) => {
    if (!storagePath) return; // pas d'audio disponible
    const samePath = playerState?.storagePath === storagePath;
    const sameTrack = playerState?.trackTitle === trackTitle;
    // Restart si : même fichier rejoué, OU titre différent (playlist avance)
    // Pas de restart si : switch de version au sein du même titre (lecture ininterrompue)
    if (samePath && !keepProgress) {
      resetKeyRef.current += 1;  // replay même fichier
    } else if (!sameTrack) {
      resetKeyRef.current += 1;  // nouveau titre → reprend du début
    }
    // Si sameTrack && !samePath → switch de version, pas de bump → lecture continue
    setPlayerState({
      trackTitle, versionName, storagePath, isPlaying: true,
      playlist: playlist || [], currentIdx: currentIdx || 0,
      resetKey: resetKeyRef.current,
    });
  };
  const loadPlayer = (trackTitle, versionName, storagePath) => {
    if (!storagePath) return;
    resetKeyRef.current += 1;
    setPlayerState({
      trackTitle, versionName, storagePath, isPlaying: false,
      playlist: [], currentIdx: 0,
      resetKey: resetKeyRef.current,
    });
  };
  const togglePlay = () => setPlayerState(prev => prev ? { ...prev, isPlaying: !prev.isPlaying } : null);
  const stopPlay = () => setPlayerState(null);
  const playNext = () => {
    if (!playerState?.playlist?.length) return;
    const nextIdx = playerState.currentIdx + 1;
    if (nextIdx >= playerState.playlist.length) {
      setPlayerState(prev => ({ ...prev, isPlaying: false }));
      return;
    }
    const next = playerState.playlist[nextIdx];
    resetKeyRef.current += 1;
    setPlayerState({
      trackTitle: next.trackTitle, versionName: next.versionName,
      storagePath: next.storagePath,
      isPlaying: true, playlist: playerState.playlist, currentIdx: nextIdx,
      resetKey: resetKeyRef.current,
    });
  };
  const playPrev = () => {
    if (!playerState?.playlist?.length) return;
    const prevIdx = playerState.currentIdx - 1;
    if (prevIdx < 0) return;
    const prev = playerState.playlist[prevIdx];
    resetKeyRef.current += 1;
    setPlayerState({
      trackTitle: prev.trackTitle, versionName: prev.versionName,
      storagePath: prev.storagePath,
      isPlaying: true, playlist: playerState.playlist, currentIdx: prevIdx,
      resetKey: resetKeyRef.current,
    });
  };
  const hasNext = playerState?.playlist?.length > 0 && playerState.currentIdx < playerState.playlist.length - 1;
  const hasPrev = playerState?.playlist?.length > 0 && playerState.currentIdx > 0;

  // Resync de la playlist du player après un reorder/déplacement de titres.
  // Sans ça, playerState.playlist est un snapshot figé au play() initial :
  // si l'utilisateur drag&drop le morceau en cours (ou un autre du même projet),
  // hasNext/hasPrev continuent de calculer sur l'ancien ordre tant qu'on n'a
  // pas refresh la page → un titre ramené en 1ʳᵉ position garde son statut
  // de "dernier" et n'a pas de bouton suivant. Idem pour le bouton précédent.
  // Le fix recompose la playlist depuis le projet qui contient le morceau en
  // cours et recalcule le currentIdx pour pointer sur la nouvelle position.
  useEffect(() => {
    if (!playerState?.playlist?.length) return;
    if (!Array.isArray(projects) || projects.length === 0) return;
    const playingTitle = playerState.trackTitle;
    const owner = projects.find(p => (p.tracks || []).some(t => t.title === playingTitle));
    if (!owner) return;
    const nextPlaylist = (owner.tracks || [])
      .map((t) => {
        const latest = t.versions?.[t.versions.length - 1];
        if (!latest?.storagePath) return null;
        return { trackTitle: t.title, versionName: latest.name, storagePath: latest.storagePath };
      })
      .filter(Boolean);
    if (!nextPlaylist.length) return;
    const nextIdx = nextPlaylist.findIndex(p => p.trackTitle === playingTitle);
    if (nextIdx < 0) return;
    // Pas de set inutile si rien n'a bougé (titres + ordre identiques).
    const prevPlaylist = playerState.playlist;
    const sameOrder = nextPlaylist.length === prevPlaylist.length
      && nextPlaylist.every((p, i) => p.trackTitle === prevPlaylist[i]?.trackTitle);
    if (sameOrder && nextIdx === playerState.currentIdx) return;
    setPlayerState((prev) => prev ? { ...prev, playlist: nextPlaylist, currentIdx: nextIdx } : prev);
  }, [projects, playerState?.trackTitle]);

  // ── Background polling for progressive results ──
  const pollingRef = useRef(null);

  // Track saved state to avoid double-saving
  const savedRef = useRef(false);

  // Intention saisie à l'écran IntentionScreen, capturée dans une ref pour
  // survivre aux closures périmés du polling de fond et aux spreads de config.
  // C'est la source de vérité FIABLE pour le write post-analyse de l'intention
  // (cf. handleIntentSubmit / handleIntentSkip + les 2 call sites de persist).
  // Bug historique (2026-06-18) : on lisait l'intention depuis merged.intent_used
  // (pas toujours exposé top-level côté client) ou config._pendingIntent (perdu
  // dans le closure du bg poll) → tracks.artistic_intent jamais écrit → aucune
  // intention héritée sur les versions suivantes ("Non renseignée").
  const pendingIntentRef = useRef(null);

  const startBackgroundPolling = (jobId) => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    pollingRef.current = setInterval(async () => {
      try {
        const res = await apiFetch(`/api/analyze/status/${jobId}`);
        const job = await res.json();
        if (job.fiche) {
          setAnalysisResult(prev => ({ ...prev, fiche: job.fiche, _stage: job.stage }));
        }
        if (job.listening) {
          setAnalysisResult(prev => ({ ...prev, listening: job.listening, _stage: job.stage }));
        }
        if (job.evolution) {
          setAnalysisResult(prev => ({ ...prev, evolution: job.evolution, _stage: job.stage }));
        }
        if (job.status === "complete" || job.status === "error") {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          // Save completed analysis to localStorage
          if (job.status === "complete" && !savedRef.current) {
            savedRef.current = true;
            setAnalysisResult(prev => {
              const full = { ...prev, fiche: job.fiche || prev?.fiche, listening: job.listening || prev?.listening, evolution: job.evolution || prev?.evolution || null, storagePath: job.storagePath || prev?.storagePath || null, _stage: "all_done" };
              // Fix architectural 2026-05-21 — préférence à la persistance
              // backend si elle a réussi (job.persistedTrackId présent). Le
              // crédit n'est jamais perdu : la ligne version est déjà en DB.
              // Sinon, fallback historique sur saveAnalysis côté client.
              const persistedIds = (job.persistedTrackId && job.persistedVersionId)
                ? { trackId: job.persistedTrackId, versionId: job.persistedVersionId, fromBackend: true }
                : null;
              const savePromise = persistedIds
                ? Promise.resolve(persistedIds)
                : saveAnalysis(config, full, job.storagePath || prev?.storagePath || null, lang);
              savePromise
                .then(async (ids) => {
                  if (!ids?.trackId) {
                    handleSaveFailure(jobId, new Error('saveAnalysis returned no trackId (bg poll)'));
                    return;
                  }
                  // Persiste l'intention au scope choisi (track/version) —
                  // même chemin que la branche fallback : l'intention n'est
                  // pas dans les champs persistés backend, on la met à jour
                  // côté front. Source fiable = pendingIntentRef (le closure
                  // `config` du bg poll peut être périmé, d'où le bug d'origine).
                  await persistIntentFromRun(ids, full, config);
                  if (ids?.trackId) {
                    setConfig(c => ({ ...(c || {}), trackId: ids.trackId, versionId: ids.versionId || c?.versionId }));
                  }
                  // Bascule maintenant sur l'écran fiche : la fiche est
                  // pleinement sauvegardée (savedRef=true) donc le guard
                  // inUploadFlow=false → FicheScreen rend direct, plus
                  // jamais de re-mount LoadingScreen (bug verdoljose2 fix).
                  setScreen("fiche");
                  refreshCredits();
                  return refreshProjects();
                })
                .catch(e => { handleSaveFailure(jobId, e); });
              return full;
            });
          }
        }
      } catch (e) { console.error("bg poll error:", e); }
    }, 3000);
  };

  // Cleanup on unmount
  useEffect(() => () => { if (pollingRef.current) clearInterval(pollingRef.current); }, []);

  // ── Gestion d'un échec de saveAnalysis ──
  // Si la sauvegarde côté front rate (RLS, session expirée, réseau coupé,
  // bug applicatif), l'utilisateur a perdu visuellement sa fiche ET son
  // crédit est débité côté serveur. On déclenche :
  //   1) un refund immédiat via RPC refund_my_failed_analysis (anti-abus :
  //      la RPC vérifie qu'aucune version n'a réellement été persistée)
  //   2) un modal d'alerte qui rassure (texte adapté selon succès du refund)
  //   3) un reset vers le dashboard pour que l'utilisateur puisse réessayer
  // Si jobId est absent (cas pathologique : on n'a même pas l'ID du job),
  // on bascule en mode fallback "écris-nous" — pas de refund possible.
  const handleSaveFailure = async (jobId, origin) => {
    console.warn('[saveFailure]', { jobId, origin: origin?.message || origin });
    let refundOk = false;
    if (jobId) {
      try {
        const { data, error } = await supabase.rpc('refund_my_failed_analysis', { p_job_id: jobId });
        refundOk = !error && data?.ok === true;
        if (error) console.warn('[saveFailure] RPC error:', error.message);
        else if (!refundOk) console.warn('[saveFailure] RPC declined:', data?.error);
      } catch (e) {
        console.warn('[saveFailure] RPC threw:', e?.message);
      }
    }
    try { await refreshCredits(); } catch { /* noop */ }
    await confirmDialog({
      title: s.errors.saveFailedTitle,
      message: refundOk ? s.errors.saveFailedMessageRefunded : s.errors.saveFailedMessageManual,
      confirmLabel: s.errors.saveFailedOk,
      cancelLabel: null,
    });
    // Reset : on libère le garde-fou savedRef et on ramène l'utilisateur
    // sur welcome pour qu'il puisse relancer une analyse sereinement.
    savedRef.current = false;
    setAnalysisResult(null);
    setConfig(null);
    setScreen('welcome');
  };

  // ── Handlers ──
  // Écrit l'intention artistique du run dans les colonnes dédiées
  // (tracks.artistic_intent / versions.version_intent) pour qu'elle soit
  // affichée ET héritée par les versions suivantes. Idempotent, best-effort.
  // Priorité des sources : pendingIntentRef (capturé au submit, fiable) >
  // champ top-level du résultat (intent_used) > config (_pendingIntent).
  const persistIntentFromRun = async (ids, runResult, cfg) => {
    if (!ids?.trackId) return;
    const pend = pendingIntentRef.current;
    const intent = (pend?.intent) || runResult?.intent_used || cfg?._pendingIntent || null;
    const scope = (pend?.scope) || runResult?._intent_scope || cfg?._pendingIntentScope || 'track';
    if (!intent) return;
    try {
      const ok = (scope === 'version' && ids.versionId)
        ? await updateVersionIntent(ids.versionId, intent)
        : await updateTrackIntent(ids.trackId, intent);
      if (!ok) console.warn('[intent] persist post-analyse a échoué', { scope, trackId: ids.trackId });
    } catch (e) {
      console.warn('[intent] persist post-analyse erreur:', e?.message);
    } finally {
      // Consommé : on évite de ré-écrire cette intention sur un run ultérieur.
      pendingIntentRef.current = null;
    }
  };

  const handleAnalyze = (cfg) => {
    // Garde-fou crédits : bloque le lancement si solde à 0. Le backend
    // re-vérifie en 402, mais on intercepte ici pour éviter l'aller-retour.
    if (!requireCredits()) return;
    // Injecte le projet cible (choisi explicitement dans AddModal ou déduit du contexte)
    const cfgWithProject = { ...cfg, projectId: cfg.projectId || currentProjectId || null };
    setConfig(cfgWithProject);
    setAnalysisResult(null);
    savedRef.current = false;
    // Repart d'une ardoise propre : pas d'intention héritée d'un run précédent.
    pendingIntentRef.current = null;
    setScreen("loading");
    // Meta Pixel : custom event LaunchAnalysis pour suivre l'action de valeur
    // (= démarrage effectif d'une analyse, post-garde-fou crédits). Utilisable
    // ensuite comme audience custom + objectif d'optim Conversions dès qu'on
    // a accumulé ~50 events.
    trackPixelAnalysisStarted();
  };
  const handleLoaded = (result) => {
    console.log("📥 VERSIONS handleLoaded called", { stage: result?._stage, jobId: result?._jobId, hasFiche: !!result?.fiche, currentScreen: screen });
    // Called with partial or complete results — always go to fiche
    // On injecte le scope choisi à l'écran IntentionScreen pour que
    // IntentPanel puisse afficher « appliquée à ce titre / cette version ».
    const injectedScope = config?._pendingIntentScope || null;
    const merged = {
      ...(analysisResult || {}),
      ...result,
      ...(injectedScope ? { _intent_scope: injectedScope } : {}),
    };
    setAnalysisResult(merged);
    const cfgWithHash = result.audioHash ? { ...config, audioHash: result.audioHash } : config;
    if (result.audioHash) setConfig(cfgWithHash);
    // Charge le morceau dans le BottomPlayer dès qu'on a le storagePath. Sans
    // ça, le player reste idle (playerState=null) à la fin de l'analyse — il
    // fallait refresh la page pour que la route handler /fiche/* déclenche
    // loadPlayer. Ne PAS auto-jouer : juste charger pour que l'user puisse
    // appuyer sur play immédiatement.
    // Guard idempotence : handleLoaded peut être appelé 2× (partial puis
    // all_done) avec le même storagePath. On ne reload QUE si le player est
    // idle ou pointe ailleurs — sinon on couperait la lecture si l'user a
    // déjà appuyé sur play entre-temps.
    if (
      merged?.storagePath
      && config?.title
      && config?.version
      && playerState?.storagePath !== merged.storagePath
    ) {
      loadPlayer(config.title, config.version, merged.storagePath);
    }
    // Ne bascule sur l'écran fiche QUE quand l'analyse est pleinement
    // terminée (stage 'all_done' + result.fiche présent). Avant ce point,
    // on garde l'utilisateur sur LoadingScreen qui continue à poller — ça
    // évite l'incohérence visuelle "URL/topbar = fiche, body = loading"
    // qu'on avait quand on basculait sur les résultats partiels (rag_done,
    // measures_done) avant la fin de la rédaction Claude.
    const isFullyReady = result._stage === "all_done" && !!result.fiche;
    if (screen !== "fiche" && isFullyReady) {
      console.log("➡️ VERSIONS setScreen('fiche') triggered from handleLoaded (all_done)");
      setScreen("fiche");
      if (!savedRef.current) {
        savedRef.current = true;
        // Fix architectural 2026-05-21 — préférence à la persistance backend.
        // Si le pipeline a déjà inséré tracks/versions (job.persistedTrackId
        // présent), on saute saveAnalysis et on utilise ces IDs direct.
        // Sinon, fallback historique sur saveAnalysis côté client.
        const persistedIds = (merged?.persistedTrackId && merged?.persistedVersionId)
          ? { trackId: merged.persistedTrackId, versionId: merged.persistedVersionId, fromBackend: true }
          : null;
        const savePromise = persistedIds
          ? Promise.resolve(persistedIds)
          : saveAnalysis(cfgWithHash, merged, merged.storagePath || null, lang);
        savePromise
          .then(async (ids) => {
            // Échec silencieux : saveAnalysis a returné null (RLS, session
            // expirée, projet introuvable, etc.). On traite comme un fail.
            if (!ids?.trackId) {
              handleSaveFailure(merged?._jobId, new Error('saveAnalysis returned no trackId (all_done)'));
              return;
            }
            // Persiste l'intention utilisée au scope choisi. Source fiable =
            // pendingIntentRef (capturé au submit), fallbacks = champs du run.
            await persistIntentFromRun(ids, merged, cfgWithHash);
            // Injecte les IDs en config pour que l'URL devienne
            // `#/fiche/{trackId}/{versionId}` (deep-link refresh OK).
            if (ids?.trackId) {
              setConfig(c => ({ ...(c || {}), trackId: ids.trackId, versionId: ids.versionId || c?.versionId }));
            }
            // Le backend a débité 1 crédit au start de l'analyse.
            // On rafraîchit le solde affiché (sidebar + topbar).
            refreshCredits();
            return refreshProjects();
          })
          .catch(e => { handleSaveFailure(merged?._jobId, e); });
      }
    } else if (screen !== "fiche" && merged._jobId && !savedRef.current) {
      // FIX 2026-05-21 (révisé) — relais du polling SANS basculer l'écran.
      // ⚠️ Historique du bug : la première version de ce fix faisait
      // `setScreen('fiche')` ici directement. Mais comme la fiche n'est
      // pas encore prête (listening_done), le guard du case fiche
      // (`inUploadFlow && !ficheReady`) re-mountait LoadingScreen, qui
      // relançait alors hashAudioFile + findDuplicateAudio + POST /start.
      // Si la persistance backend (palier 4) avait déjà créé la version,
      // findDuplicateAudio l'attrapait → modale "Analyse déjà existante"
      // → navigation vers /fiche/slug/v-N → page blanche pour certains
      // utilisateurs. Verdoljose2 a hit ce cas le 2026-05-21 18:36.
      // Solution : on garde l'utilisateur sur LoadingScreen (qui reste
      // visuellement actif via son timer displayedPct), on relance juste
      // le polling de fond. Le setScreen('fiche') est déplacé dans la
      // callback de fin de bg poll (cf. startBackgroundPolling), pour
      // qu'il fire UNIQUEMENT quand la fiche est entièrement prête —
      // donc savedRef=true et plus jamais de re-mount possible.
      console.log("➡️ VERSIONS bg poll relais (stay on loading, partial _stage =", result._stage, ")");
      startBackgroundPolling(merged._jobId);
    }
  };
  const goHome = () => {
    setScreen("welcome");
    setConfig(null);
    setAnalysisResult(null);
    setPrefillTitle("");
    setIntentCtx(null);
  };

  // ── Handlers intention artistique ──
  // Appelé par LoadingScreen quand le backend passe en 'awaiting_intent'.
  // On stocke le contexte (jobId, perception, intention héritée) et on
  // bascule sur l'écran intention.
  const handleAwaitingIntent = (ctx) => {
    setIntentCtx(ctx);
    setScreen("intention");
  };

  // L'artiste a saisi une intention + choisi son scope (track/version).
  // 1) POST /api/analyze/diagnose/:jobId avec l'intention → relance le job
  // 2) On revient sur LoadingScreen en mode "inlineIntent" (ne repassera
  //    plus par awaiting_intent, donc le pipeline enchaîne naturellement)
  // 3) Le write Supabase (updateTrackIntent / updateVersionIntent) se fait
  //    au retour de l'analyse, dans handleLoaded, via intent_used.
  const handleIntentSubmit = async (intent, scope) => {
    if (!intentCtx?.jobId) return;
    try {
      await apiFetch(`/api/analyze/diagnose/${intentCtx.jobId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ intent, scope }),
      });
    } catch (e) {
      console.warn("[intent] diagnose POST failed:", e.message);
    }
    // Mémorise le scope choisi pour le write post-analyse.
    // Ref = source fiable (immune aux closures périmés / spreads de config).
    pendingIntentRef.current = { intent: (intent || '').trim() || null, scope: scope || 'track' };
    // resumeJobId = signal à LoadingScreen : NE PAS redémarrer un nouveau job,
    // juste poller le job existant que le backend vient de reprendre avec
    // l'intention reçue.
    setConfig((c) => ({
      ...(c || {}),
      _pendingIntent: intent,
      _pendingIntentScope: scope,
      resumeJobId: intentCtx.jobId,
    }));
    setIntentCtx(null);
    setScreen("loading");
  };

  const handleIntentSkip = async () => {
    if (!intentCtx?.jobId) return;
    try {
      await apiFetch(`/api/analyze/diagnose/${intentCtx.jobId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ skipIntent: true }),
      });
    } catch (e) {
      console.warn("[intent] skip POST failed:", e.message);
    }
    // Skip = pas d'intention explicite saisie pour ce run. On ne ré-écrit rien
    // (une intention de titre déjà en base reste héritée telle quelle).
    pendingIntentRef.current = null;
    // Même logique : on reprend le même job côté backend.
    setConfig((c) => ({ ...(c || {}), resumeJobId: intentCtx.jobId }));
    setIntentCtx(null);
    setScreen("loading");
  };

  // Sidebar handlers
  // Focus uniquement : charge l'audio dans le player et met à jour config/analysisResult,
  // SANS basculer sur l'écran fiche (David: seuls les boutons "Analyse" et "Voir la fiche"
  // ouvrent la fiche d'analyse).
  const handleSidebarSelectVersion = async (track, v) => {
    // Charger l'audio EN PREMIER (avant l'await) pour éviter la coupure
    if (v.storagePath) {
      if (playerState?.isPlaying) {
        play(track.title, v.name, v.storagePath);
      } else {
        loadPlayer(track.title, v.name, v.storagePath);
      }
    }
    const saved = await getAnalysis(track.id, v.id);
    // Priority DAW : analyse > profil user > config courant > fallback.
    const arSel = saved || v.analysisResult || null;
    const dawSel = arSel?.meta?.daw || userProfile?.default_daw || config?.daw || 'Logic Pro';
    setConfig({
      title: track.title,
      version: v.name,
      trackId: track.id,
      versionId: v.id,
      daw: dawSel,
    });
    setAnalysisResult(arSel);
    // Si on est déjà dans la fiche (Timeline), on y reste. Sinon on ne change pas d'écran.
  };

  // Ouvrir la fiche d'analyse : réservé aux boutons "Analyse" / "Voir la fiche".
  const handleOpenFiche = async (track, v) => {
    if (v?.storagePath) {
      if (playerState?.isPlaying) {
        play(track.title, v.name, v.storagePath);
      } else {
        loadPlayer(track.title, v.name, v.storagePath);
      }
    }
    const saved = v ? await getAnalysis(track.id, v.id) : null;
    // Priority DAW : analyse > profil user > config courant > fallback.
    const arOpen = saved || v?.analysisResult || null;
    const dawOpen = arOpen?.meta?.daw || userProfile?.default_daw || config?.daw || 'Logic Pro';
    setConfig({
      title: track.title,
      version: v?.name,
      trackId: track.id,
      versionId: v?.id,
      daw: dawOpen,
    });
    setAnalysisResult(arOpen);
    setScreen("fiche");
  };
  const handleSidebarNewTrack = () => {
    // Garde-fou crédits : "ajouter un titre" suppose une analyse derrière,
    // donc on bloque tôt pour ne pas laisser l'utilisateur uploader un
    // fichier qu'il ne pourra pas analyser. La création de projet reste libre.
    if (!requireCredits()) return;
    // Ouvre la modale d'ajout dans le flow "Nouveau titre".
    // AddModal se charge de la suite (pick-project / upload selon le
    // nombre de projets). Plus d'écran isolé /nouveau.
    setPrefillTitle("");
    setAutoSelectTrackTitle("");
    setAnalysisResult(null);
    setConfig(null);
    setAddModalCtx({ mode: 'new-track' });
    setHomeAddOpen(true);
  };
  // Annulation depuis LoadingScreen : reset état + retour dashboard.
  // Pas d'AddModal ouvert derrière (l'utilisateur a cliqué "Annuler",
  // pas "Nouveau titre"). Sans setScreen, on restait coincé sur
  // screen=loading + config=null → useEffect relance run() avec config
  // null → throw à `if (config.file)` → page vide / erreur silencieuse.
  const handleCancelAnalysis = () => {
    setAnalysisResult(null);
    setConfig(null);
    setScreen("welcome");
  };
  const handleAddVersionFromPicker = (track) => {
    // Garde-fou crédits : même rationale que handleSidebarNewTrack.
    if (!requireCredits()) return;
    // Pré-sélectionne le projet du titre pour que l'upload y atterrisse.
    // On ouvre la modale directement à l'étape upload, titre verrouillé.
    if (track?.projectId) setCurrentProjectId(track.projectId);
    setPrefillTitle(track.title);
    setAutoSelectTrackTitle(track.title);
    // IMPORTANT : on ne nettoie PAS analysisResult/config ici. Sinon,
    // depuis une fiche d'analyse, cliquer "Nouvelle version" wipe la
    // fiche courante AVANT que l'utilisateur ne valide l'upload — si
    // on annule la modale, on retombe sur une fiche vide ("page
    // factice"). Le nettoyage se fait naturellement quand handleAnalyze
    // tourne (setConfig + setAnalysisResult avant setScreen('loading')).
    setAddModalCtx({ mode: 'add-version', trackId: track?.id });
    setHomeAddOpen(true);
  };

  // Handlers app-level pour AddModal — miroir de ceux définis
  // localement dans WelcomeHome (utilisés hors de l'écran Home).
  const handleNewProjectApp = () => {
    setNewProjectValueApp('');
    setNewProjectOpenApp(true);
    setTimeout(() => newProjectInputRefApp.current?.focus(), 50);
  };
  // Garde anti-double-submit (cf. submitNewProject dans WelcomeHome).
  const creatingProjectAppRef = useRef(false);
  const submitNewProjectApp = async () => {
    const name = newProjectValueApp.trim();
    if (!name) return;
    if (creatingProjectAppRef.current) return;
    creatingProjectAppRef.current = true;
    try {
      const created = await createProject(name);
      setNewProjectOpenApp(false);
      setNewProjectValueApp('');
      refreshProjects();
      if (created?.id) setCurrentProjectId(created.id);
      if (pendingNewTrackRefApp.current) {
        pendingNewTrackRefApp.current = false;
        handleSidebarNewTrack();
      }
    } catch (err) {
      console.warn('createProject failed', err);
    } finally {
      creatingProjectAppRef.current = false;
    }
  };

  // ── Screen routing ──
  const renderContent = () => {
    switch (screen) {
      case "welcome":
        return (
          <WelcomeHome
            userProfile={userProfile}
            currentProjectId={currentProjectId}
            onSetCurrentProject={setCurrentProjectId}
            onNewTrack={handleSidebarNewTrack}
            onAddVersion={handleAddVersionFromPicker}
            onAnalyze={handleAnalyze}
            onSelectVersion={handleSidebarSelectVersion}
            onOpenFiche={handleOpenFiche}
            onPlay={play}
            onToggle={togglePlay}
            onNext={playNext}
            playerState={playerState}
            projects={projects}
            projectsLoaded={projectsLoaded}
            onMutate={refreshProjects}
            addModalOpen={homeAddOpen}
            setAddModalOpen={setHomeAddOpen}
            addModalCtx={addModalCtx}
            setAddModalCtx={setAddModalCtx}
          />
        );
      case "loading":
        return (
          <LoadingScreen
            config={config}
            onDone={handleLoaded}
            onAwaitingIntent={handleAwaitingIntent}
            onBackToInput={handleCancelAnalysis}
          />
        );
      case "intention": {
        // Un titre est "V1" s'il n'existe pas encore dans les projets
        // (ou si c'est sa toute première version). Utilisé par
        // IntentionScreen pour choisir le layout (colonne vs compact).
        const title = (config?.title || "").trim().toLowerCase();
        const allTracks = (projects || []).flatMap((p) => p.tracks || []);
        const existing = title ? allTracks.find((t) => (t.title || "").trim().toLowerCase() === title) : null;
        const isFirstVersion = !existing || !(existing.versions?.length);
        return (
          <IntentionScreen
            perception={intentCtx?.perception || null}
            config={config}
            isFirstVersion={isFirstVersion}
            inheritedIntent={intentCtx?.inheritedIntent || null}
            onSubmit={handleIntentSubmit}
            onSkip={handleIntentSkip}
            onCancel={goHome}
          />
        );
      }
      case "fiche": {
        // Garde-fou : si screen='fiche' a été déclenché (par le route handler,
        // un useEffect, ou autre) MAIS qu'on est encore en train d'analyser
        // un fichier (audioHash set + pas encore sauvé) ET que la fiche n'a
        // pas d'items rendus, on rend LoadingScreen plutôt que FicheScreen.
        // Sinon FicheScreen affiche son AnalyzingState interne entouré de sa
        // topbar fiche -> incohérence visuelle (URL/topbar = fiche, body = loading).
        const ficheReady = !!(analysisResult?.fiche?.elements?.length);
        const inUploadFlow = !!config?.audioHash && !savedRef.current;
        if (inUploadFlow && !ficheReady) {
          return (
            <LoadingScreen
              config={config}
              onDone={handleLoaded}
              onAwaitingIntent={handleAwaitingIntent}
              onBackToInput={handleCancelAnalysis}
            />
          );
        }
        return (
          <FicheScreen
            config={config}
            analysisResult={analysisResult}
            onSelectVersion={handleSidebarSelectVersion}
            onGoHome={goHome}
            refreshKey={projectsRefreshKey}
            onAddVersion={handleAddVersionFromPicker}
            onManageMembers={(projectId, trackId) => {
              const open = (proj) => {
                if (!proj) return;
                setMembersInitialTrackApp(trackId || null);
                setMembersTargetApp(proj);
              };
              const own = projects.find((p) => p.id === projectId);
              if (own) { open(own); return; }
              // Projet partagé AVEC moi : pas dans `projects` (loadProjects
              // ne renvoie que les projets possédés) → lookup à la demande.
              loadSharedProjectsWithTracks()
                .then((list) => open((list || []).find((p) => p.id === projectId)))
                .catch(() => {});
            }}
          />
        );
      }
      case "versions":
        return (
          <VersionsScreen
            onViewAnalysis={handleOpenFiche}
            onAddVersion={handleAddVersionFromPicker}
            autoSelectTrackTitle={autoSelectTrackTitle}
            onAutoSelectConsumed={() => setAutoSelectTrackTitle("")}
            onPlay={play}
            onStop={stopPlay}
            onToggle={togglePlay}
            playerState={playerState}
          />
        );
      case "admin":
        return <AdminScreen />;
      default:
        // Fallback si screen invalide : on retombe sur la home
        // (plus de page /nouveau dédiée : tout passe par la modale Add).
        return (
          <WelcomeHome
            userProfile={userProfile}
            currentProjectId={currentProjectId}
            onSetCurrentProject={setCurrentProjectId}
            onNewTrack={handleSidebarNewTrack}
            onAddVersion={handleAddVersionFromPicker}
            onAnalyze={handleAnalyze}
            onSelectVersion={handleSidebarSelectVersion}
            onOpenFiche={handleOpenFiche}
            onPlay={play}
            onToggle={togglePlay}
            onNext={playNext}
            playerState={playerState}
            projects={projects}
            projectsLoaded={projectsLoaded}
            onMutate={refreshProjects}
            addModalOpen={homeAddOpen}
            setAddModalOpen={setHomeAddOpen}
            addModalCtx={addModalCtx}
            setAddModalCtx={setAddModalCtx}
          />
        );
    }
  };

  // Auth gate
  if (authLoading) {
    return (
      <>
        <FontLink />
        <GlobalStyles />
        <div style={{minHeight:"100vh",display:"grid",placeItems:"center",background:T.black,color:T.muted,fontFamily:T.mono,fontSize:14,letterSpacing:2}}>
          CHARGEMENT...
        </div>
      </>
    );
  }
  // Route /update-password : page d'atterrissage du lien recovery email.
  // Rendue plein écran qu'il y ait user ou pas — UpdatePasswordScreen check
  // la session par lui-même (Supabase pose la session via le token recovery).
  // Doit être placé AVANT le check `!user` car au moment où le lien email est
  // cliqué dans un nouvel onglet, la session n'est pas encore initialisée.
  if (typeof window !== 'undefined' && window.location.pathname === '/update-password') {
    return (
      <LangContext.Provider value={{ lang, s, setLang, t }}>
        <FontLink />
        <GlobalStyles />
        <MockupStyles />
        <UpdatePasswordScreen />
      </LangContext.Provider>
    );
  }
  // Route /confirm-delete-account : page de confirmation finale du flow
  // de suppression de compte (le user a cliqué le lien dans le mail, on
  // affiche un écran de dernière confirmation rouge avant l'appel API).
  // Même pattern que /update-password : rendu plein écran indépendamment
  // de l'auth gate. Le token signé en query param fait office d'auth.
  if (typeof window !== 'undefined' && window.location.pathname === '/confirm-delete-account') {
    return (
      <LangContext.Provider value={{ lang, s, setLang, t }}>
        <FontLink />
        <GlobalStyles />
        <MockupStyles />
        <ConfirmDeleteAccountScreen />
      </LangContext.Provider>
    );
  }
  if (!user) {
    // Quand l'utilisateur clique le CTA de la landing pour s'inscrire/se
    // connecter, on aligne l'URL sur le dashboard. Comme ça, après login,
    // routeInit lit `/dashboard` et renvoie sur le bon écran (au lieu de
    // re-rendre la landing parce que l'URL était restée à `/`).
    const goAuth = () => {
      // Attribution : le visiteur "passe le CTA" (landing, pricing,
      // footer… tous les chemins vers l'inscription convergent ici).
      // Une fois par session navigateur, anonyme, fail-safe.
      logCtaClick();
      setShowAuth(true);
      if (typeof window !== 'undefined') {
        // Retour post-login : si l'inscription part d'une page publique
        // "retournable" (/plugin, /pricing), on mémorise où on était pour
        // y revenir après le login (routeInit lit vs_return_to). Sinon on
        // NETTOIE la clé (pas de retour périmé d'une visite précédente).
        // sessionStorage (pas localStorage) : per-onglet, meurt avec lui —
        // et il survit au redirect OAuth Google (/auth/callback), là où le
        // pathname est perdu.
        const p = window.location.pathname;
        try {
          if (p === '/plugin' || p === '/pricing') {
            window.sessionStorage.setItem('vs_return_to', p);
          } else {
            window.sessionStorage.removeItem('vs_return_to');
          }
        } catch { /* stockage indispo (Safari privé) → dashboard, comme avant */ }
        window.history.replaceState({}, '', '/dashboard');
      }
    };
    const goSample = () => {
      setShowAuth(false);
      setScreen('sample');
      if (typeof window !== 'undefined') {
        window.history.pushState({ screen: 'sample' }, '', '/exemple');
      }
    };
    const goLanding = () => {
      setShowAuth(false);
      setScreen('welcome');
      if (typeof window !== 'undefined') {
        window.history.pushState({ screen: 'welcome' }, '', '/');
      }
    };
    const goPricing = () => {
      setShowAuth(false);
      setScreen('pricing');
      if (typeof window !== 'undefined') {
        window.history.pushState({ screen: 'pricing' }, '', '/pricing');
      }
    };
    const goPlugin = () => {
      setShowAuth(false);
      setScreen('plugin');
      if (typeof window !== 'undefined') {
        window.history.pushState({ screen: 'plugin' }, '', '/plugin');
      }
    };
    const goPrivacy = () => {
      setShowAuth(false);
      setScreen('privacy');
      if (typeof window !== 'undefined') {
        window.history.pushState({ screen: 'privacy' }, '', '/privacy');
      }
    };
    const goTerms = () => {
      setShowAuth(false);
      setScreen('terms');
      if (typeof window !== 'undefined') {
        window.history.pushState({ screen: 'terms' }, '', '/terms');
      }
    };
    let view;
    if (showAuth) view = <AuthScreen />;
    else if (screen === 'sample') view = <SampleFicheScreen onSignup={goAuth} onBackToLanding={goLanding} />;
    else if (screen === 'pricing') view = <PricingScreen onStart={goAuth} onBackToLanding={goLanding} onViewDashboard={goAuth} onViewPlugin={goPlugin} isAuthenticated={false} />;
    else if (screen === 'plugin') view = <PluginScreen onBackToLanding={goLanding} onViewPricing={goPricing} onViewDashboard={goAuth} isAuthenticated={false} />;
    else if (screen === 'privacy') view = <PrivacyScreen onBackToLanding={goLanding} onGoTerms={goTerms} />;
    else if (screen === 'terms') view = <TermsScreen onBackToLanding={goLanding} onGoPrivacy={goPrivacy} />;
    else view = <LandingScreen onStart={goAuth} onViewSample={goSample} onViewPricing={goPricing} onViewPlugin={goPlugin} onViewDashboard={goAuth} onGoPrivacy={goPrivacy} onGoTerms={goTerms} isAuthenticated={false} />;
    return (
      <LangContext.Provider value={{ lang, s, setLang, t }}>
        <FontLink />
        <GlobalStyles />
        <MockupStyles />
        {view}
      </LangContext.Provider>
    );
  }

  // Utilisateur connecté qui visite la landing (#/home, ou clic "À propos"
  // dans la sidebar) : rendu plein écran sans sidebar/BottomPlayer pour
  // garder le ton "page de présentation". Les CTAs renvoient au dashboard.
  // On rend en plus le DashboardRail (pill crédits + Réglages +
  // Déconnexion) en bas-gauche, comme sur le dashboard, pour que
  // l'utilisateur connecté ait toujours ses outils de compte à portée.
  if (screen === 'home') {
    return (
      <LangContext.Provider value={{ lang, s, setLang, t }}>
        <FontLink />
        <GlobalStyles />
        <MockupStyles />
        <LandingScreen
          onStart={() => setScreen('welcome')}
          ctaPrimaryLabel={s.sidebar.dashboardLink}
          ctaFooterLabel={s.sidebar.dashboardLink}
          onViewSample={() => setScreen('sample')}
          onViewPricing={() => setScreen('pricing')}
          onViewPlugin={() => setScreen('plugin')}
          onViewDashboard={() => setScreen('welcome')}
          onGoPrivacy={() => setScreen('privacy')}
          onGoTerms={() => setScreen('terms')}
          isAuthenticated={true}
          credits={userCredits}
          isAdmin={isAdminUser}
          onGoAdmin={() => setScreen('admin')}
          onGoReglages={() => setReglagesOpen(true)}
          onSignOut={handleSignOut}
        />
        {!!user && (
          <DashboardRail
            credits={userCredits}
            onGoPricing={() => setScreen('pricing')}
            onGoReglages={() => setReglagesOpen(true)}
            onSignOut={handleSignOut}
            onGoAdmin={() => setScreen('admin')}
            onGoFeedback={() => setFeedbackOpen(true)}
            user={user}
          />
        )}
        {/* Réglages modal accessible depuis le rail */}
        <ReglagesModal
          open={reglagesOpen}
          onClose={() => setReglagesOpen(false)}
          onSignOut={handleSignOut}
          onProfileUpdate={setUserProfile}
        />
        {/* Feedback testeurs — modale globale ouvrable depuis le rail */}
        {feedbackOpen && (
          <FeedbackModal
            onClose={() => setFeedbackOpen(false)}
            versionId={config?.versionId || null}
            trackId={config?.trackId || null}
          />
        )}
      </LangContext.Provider>
    );
  }

  // Connecté + #/exemple : sample plein écran, libellés CTAs adaptés au
  // contexte (pas de "créer un compte" — l'utilisateur est déjà identifié).
  if (screen === 'sample') {
    return (
      <LangContext.Provider value={{ lang, s, setLang, t }}>
        <FontLink />
        <GlobalStyles />
        <MockupStyles />
        <SampleFicheScreen
          onSignup={() => setScreen('welcome')}
          onBackToLanding={() => setScreen('home')}
          topbarCtaLabel={s.sidebar.dashboardLink}
          bottomCtaLabel={s.sidebar.dashboardLink}
        />
      </LangContext.Provider>
    );
  }

  // Connecté + #/pricing : page tarifs plein écran, sans sidebar.
  // CTA primaire renvoie au dashboard (pas d'AuthScreen quand on est déjà
  // connecté), retour pointe sur la landing #/.
  // Idem que la landing connectée : rail bas-gauche (crédits + Réglages
  // + Déconnexion) ajouté pour les utilisateurs connectés uniquement.
  if (screen === 'pricing') {
    return (
      <LangContext.Provider value={{ lang, s, setLang, t }}>
        <FontLink />
        <GlobalStyles />
        <MockupStyles />
        <PricingScreen
          onStart={() => setScreen('welcome')}
          onBackToLanding={() => setScreen('home')}
          onViewDashboard={() => setScreen('welcome')}
          onViewPlugin={() => setScreen('plugin')}
          ctaPrimaryLabel={s.sidebar.dashboardLink}
          isAuthenticated={true}
          credits={userCredits}
          isAdmin={isAdminUser}
          onGoAdmin={() => setScreen('admin')}
          onGoReglages={() => setReglagesOpen(true)}
          onSignOut={handleSignOut}
        />
        {!!user && (
          <DashboardRail
            credits={userCredits}
            onGoPricing={() => setScreen('pricing')}
            onGoReglages={() => setReglagesOpen(true)}
            onSignOut={handleSignOut}
            onGoAdmin={() => setScreen('admin')}
            onGoFeedback={() => setFeedbackOpen(true)}
            user={user}
          />
        )}
        <ReglagesModal
          open={reglagesOpen}
          onClose={() => setReglagesOpen(false)}
          onSignOut={handleSignOut}
          onProfileUpdate={setUserProfile}
        />
        {feedbackOpen && (
          <FeedbackModal
            onClose={() => setFeedbackOpen(false)}
            versionId={config?.versionId || null}
            trackId={config?.trackId || null}
          />
        )}
      </LangContext.Provider>
    );
  }

  // Connecté + /plugin : page plugin plein écran, sans sidebar — même
  // pattern que pricing (rail bas-gauche pour les outils de compte).
  if (screen === 'plugin') {
    return (
      <LangContext.Provider value={{ lang, s, setLang, t }}>
        <FontLink />
        <GlobalStyles />
        <MockupStyles />
        <PluginScreen
          onBackToLanding={() => setScreen('home')}
          onViewPricing={() => setScreen('pricing')}
          onViewDashboard={() => setScreen('welcome')}
          isAuthenticated={true}
          credits={userCredits}
          isAdmin={isAdminUser}
          onGoAdmin={() => setScreen('admin')}
          onGoReglages={() => setReglagesOpen(true)}
          onSignOut={handleSignOut}
        />
        {!!user && (
          <DashboardRail
            credits={userCredits}
            onGoPricing={() => setScreen('pricing')}
            onGoReglages={() => setReglagesOpen(true)}
            onSignOut={handleSignOut}
            onGoAdmin={() => setScreen('admin')}
            onGoFeedback={() => setFeedbackOpen(true)}
            user={user}
          />
        )}
        <ReglagesModal
          open={reglagesOpen}
          onClose={() => setReglagesOpen(false)}
          onSignOut={handleSignOut}
          onProfileUpdate={setUserProfile}
        />
        {feedbackOpen && (
          <FeedbackModal
            onClose={() => setFeedbackOpen(false)}
            versionId={config?.versionId || null}
            trackId={config?.trackId || null}
          />
        )}
      </LangContext.Provider>
    );
  }

  // Connecté + #/privacy ou #/terms : pages légales plein écran, sans
  // sidebar ni rail. Cohérent avec home / pricing : la lecture prime,
  // pas d'éléments de navigation flottants.
  if (screen === 'privacy') {
    return (
      <LangContext.Provider value={{ lang, s, setLang, t }}>
        <FontLink />
        <GlobalStyles />
        <MockupStyles />
        <PrivacyScreen
          onBackToLanding={() => setScreen('home')}
          onGoTerms={() => setScreen('terms')}
        />
      </LangContext.Provider>
    );
  }
  if (screen === 'terms') {
    return (
      <LangContext.Provider value={{ lang, s, setLang, t }}>
        <FontLink />
        <GlobalStyles />
        <MockupStyles />
        <TermsScreen
          onBackToLanding={() => setScreen('home')}
          onGoPrivacy={() => setScreen('privacy')}
        />
      </LangContext.Provider>
    );
  }

  // (Note : #/admin est rendu en layout topbar — voir TOPBAR_SCREENS
  // ci-dessous. Décision 2026-04-30 : cohérence avec home / pricing /
  // welcome ; pas de sidebar sur la page admin, juste la topbar et le
  // rail flottant pour les outils de compte.)

  // Deep-link fiche en cours de résolution : on affiche le même loader que
  // l'auth gate pour éviter un flash dashboard avant que le résolveur ne
  // matérialise config/analysisResult.
  if (pendingFiche) {
    return (
      <>
        <FontLink />
        <GlobalStyles />
        <div style={{minHeight:"100vh",display:"grid",placeItems:"center",background:T.black,color:T.muted,fontFamily:T.mono,fontSize:14,letterSpacing:2}}>
          CHARGEMENT...
        </div>
      </>
    );
  }

  // On desktop, the sidebar shows the tracks list so we don't need the "versions" screen.
  // Exception (test 2026-04-29) : sur le DASHBOARD ('welcome'), on bascule en
  // layout topbar comme la landing / pricing — pas de sidebar. Les autres
  // écrans authentifiés (fiche, versions, admin) gardent la sidebar pour
  // l'instant. La sidebar pourrait à terme être réservée aux fiches
  // d'analyse uniquement.
  // Refonte 2026-04-30 : la sidebar est maintenant retirée de welcome,
  // fiche ET admin. Les autres écrans authentifiés (versions) la gardent
  // pour l'instant. Liste des écrans en layout topbar :
  const TOPBAR_SCREENS = new Set(['welcome', 'fiche', 'admin']);
  // Refonte 2026-04-30 (suite) : pages d'analyse en mode "minimal" — pas
  // de sidebar, pas de topbar nav, pas de rail. Juste un logo top-left
  // rendu par l'écran lui-même + contenu centré. Cohérent avec le ton
  // "page plus jeune" : moins de chrome pendant un moment qui doit
  // capter toute l'attention de l'utilisateur (l'analyse en cours).
  const MINIMAL_SCREENS = new Set(['loading']);
  const isMinimalScreen = MINIMAL_SCREENS.has(screen);
  const showSidebar = isDesktop && !TOPBAR_SCREENS.has(screen) && !isMinimalScreen;
  const contentMarginLeft = showSidebar ? SIDEBAR_WIDTH : 0;
  // Topbar dashboard — visible sur welcome ET fiche (desktop + mobile),
  // pour que le pattern soit identique à la landing/pricing : logo +
  // nav + FR/EN, sans avatar/menu déroulant. Sur les autres écrans
  // mobiles (admin, versions), c'est MobileMenu qui prend le relais
  // (cf. condition !showWelcomeTopbar plus bas). Masquée en mode
  // MINIMAL_SCREENS — l'écran d'analyse rend son propre header.
  const showWelcomeTopbar = !showSidebar && TOPBAR_SCREENS.has(screen) && !!user && !isMinimalScreen;

  return (
    <LangContext.Provider value={{ lang, s, setLang, t }}>
      <FontLink />
      <GlobalStyles />
      <MockupStyles />
<div
        className={showSidebar ? "app" : "dapp"}
        style={
          /* En mode topbar (welcome desktop), on override les règles
             .dapp de GlobalStyles qui forcent height: 100vh + overflow:
             hidden — sinon la page entière ne peut pas scroller (le
             topbar resterait visuellement "sticky" parce que le scroll
             est piégé en interne). En layout sidebar/mobile, on laisse
             le comportement par défaut. */
          showWelcomeTopbar
            ? { height: 'auto', minHeight: '100vh', overflow: 'visible' }
            : undefined
        }
      >
        {/* Desktop Sidebar */}
        {showSidebar && (
          <Sidebar
            currentTrackTitle={config?.title}
            currentVersionName={config?.version}
            currentProjectId={currentProjectId}
            onSetCurrentProject={setCurrentProjectId}
            onSelectVersion={handleSidebarSelectVersion}
            onOpenFiche={handleOpenFiche}
            onNewTrack={handleSidebarNewTrack}
            onGoReglages={() => setReglagesOpen(true)}
            onGoPricing={() => setScreen('pricing')}
            onGoFeedback={() => setFeedbackOpen(true)}
            onAskOpen={() => setAskOpen(true)}
            onAdd={() => setHomeAddOpen(true)}
            onPlay={play}
            onToggle={togglePlay}
            onMutate={refreshProjects}
            onStop={stopPlay}
            playerState={playerState}
            user={user}
            userProfile={userProfile}
            onSignOut={handleSignOut}
            onGoLanding={() => setScreen('home')}
            onGoDashboard={() => setScreen('welcome')}
            onGoAdmin={() => setScreen('admin')}
            projects={projects}
            projectsLoaded={projectsLoaded}
          />
        )}

        {/* Main column */}
        <div style={showSidebar ? { display: "flex", flexDirection: "column", minWidth: 0 } : { marginLeft: contentMarginLeft, display: "flex", flexDirection: "column", minHeight: "100vh", transition: "margin-left .2s" }}>
          {/* Mobile top bar — masqué sur welcome car DashboardTopbar
              gère le mobile aussi maintenant (parité visuelle avec
              landing/pricing). Reste affiché sur les autres écrans
              authentifiés (admin, fiche, versions, etc.). */}
          {isMobile && !showWelcomeTopbar && !isMinimalScreen && (
            <MobileMenu
              onNavigate={(target) => {
                setAskOpen(false);
                // Réglages → modale, pas d'écran dédié
                if (target === 'reglages') { setReglagesOpen(true); return; }
                setScreen(target);
              }}
              onSignOut={handleSignOut}
              user={user}
              userProfile={userProfile}
              onAdd={() => setHomeAddOpen(true)}
              onGoFeedback={() => setFeedbackOpen(true)}
            />
          )}

          {/* Desktop topbar pour les écrans sans sidebar (welcome).
              Calé sur le topbar de la landing / pricing : logo + wordmark à
              gauche, nav (Accueil / Tableau de bord current / Tarifs /
              Réglages / Déconnexion / FR/EN) à droite. */}
          {showWelcomeTopbar && (
            <DashboardTopbar
              currentScreen={screen}
              onGoLanding={() => setScreen('home')}
              onGoDashboard={() => setScreen('welcome')}
              onGoPricing={() => setScreen('pricing')}
              onGoPlugin={() => setScreen('plugin')}
              onGoReglages={() => setReglagesOpen(true)}
              onSignOut={handleSignOut}
              onGoAdmin={() => setScreen('admin')}
              onGoFeedback={() => setFeedbackOpen(true)}
              user={user}
              lang={lang}
              setLang={setLang}
              credits={userCredits}
            />
          )}

          {/* Ask Modal */}
          {askOpen && <AskModal onClose={() => setAskOpen(false)} />}

          {/* Solde de crédits à zéro — bloque l'ajout de titre/version
              et le lancement d'analyse, redirige vers la page tarifs. */}
          <NoCreditsModal
            open={noCreditsOpen}
            onClose={() => setNoCreditsOpen(false)}
            onGoPricing={() => {
              setNoCreditsOpen(false);
              setScreen('pricing');
              if (typeof window !== 'undefined') {
                window.history.pushState({ screen: 'pricing' }, '', '/pricing');
              }
            }}
          />

          {/* Nouvelles d'Archipel Audio — carte de sollicitation pour les
              comptes sans choix (migration 052), tableau de bord seulement. */}
          <ArchipelConsentToast user={user} active={screen === 'welcome'} />

          {/* Réglages — modale globale, ouvrable depuis sidebar desktop
              ou menu avatar mobile */}
          <ReglagesModal
            open={reglagesOpen}
            onClose={() => setReglagesOpen(false)}
            onSignOut={handleSignOut}
            onProfileUpdate={setUserProfile}
          />

          {/* Feedback — modale questionnaire testeurs, ouvrable depuis
              la sidebar (desktop) ou le DashboardTopbar (welcome).
              Capte version/track courant si disponibles dans config
              (utile quand l'utilisateur soumet depuis une fiche). */}
          {feedbackOpen && (
            <FeedbackModal
              onClose={() => setFeedbackOpen(false)}
              versionId={config?.versionId || null}
              trackId={config?.trackId || null}
            />
          )}

          {/* AddModal accessible depuis la sidebar sur n'importe quel
              écran autre que la Home (la Home garde ses propres instances
              pour ne pas casser ses flux internes). */}
          {screen !== "welcome" && newProjectOpenApp && (
            <RenameModal
              title={s.home.newProject}
              placeholder={s.home.projectNamePlaceholder}
              value={newProjectValueApp}
              originalValue=""
              inputRef={newProjectInputRefApp}
              onChange={setNewProjectValueApp}
              onCancel={() => setNewProjectOpenApp(false)}
              onSubmit={submitNewProjectApp}
              confirmLabel={s.home.confirmCreate}
            />
          )}
          {membersTargetApp && (
            <ProjectMembersModal
              project={membersTargetApp}
              s={s}
              initialScope={membersInitialTrackApp || 'project'}
              onClose={() => { setMembersTargetApp(null); setMembersInitialTrackApp(null); refreshProjects(); }}
            />
          )}
          {screen !== "welcome" && homeAddOpen && (
            <AddModal
              onClose={() => { setHomeAddOpen(false); setAddModalCtx(null); }}
              projects={projects}
              allTracks={projects.flatMap((p) => (p.tracks || []).map((t) => ({ ...t, _projectName: p.name, projectId: p.id })))}
              initialContext={addModalCtx}
              defaultDaw={userProfile?.default_daw || ''}
              onCreateProject={async (name) => {
                try {
                  const created = await createProject(name);
                  refreshProjects();
                  if (created?.id) setCurrentProjectId(created.id);
                  return created;
                } catch (err) {
                  console.warn('createProject from AddModal (app) failed', err);
                  throw err;
                }
              }}
              onAnalyze={(cfg) => {
                if (cfg.projectId) setCurrentProjectId(cfg.projectId);
                handleAnalyze(cfg);
              }}
            />
          )}

          {/* Content
              Deux modes de scroll selon le layout :
                - Layout sidebar (fiche, admin…) : le scrollContentRef
                  scrolle (overflowY auto, flex 1, minHeight 0). La
                  sidebar reste sticky.
                - Layout topbar (welcome) : le scroll est délégué à la
                  page entière (window). Le topbar défile naturellement
                  avec le contenu, plus de "topbar fixe en haut" qui
                  dérangeait. Le contenu est borné à 1180px centré
                  (cf. .lp-section / .pr-section). */}
          <div
            ref={scrollContentRef}
            style={
              showWelcomeTopbar
                ? { display: "flex", flexDirection: "column", width: "100%", paddingBottom: 80 }
                : { flex: 1, overflowY: "auto", overflowX: "hidden", display: "flex", flexDirection: "column", width: "100%", minHeight: 0, paddingBottom: 80 }
            }
          >
            {/* Wrap centré pour les écrans en layout topbar :
                  - welcome : 1080px (aligné avec landing/pricing)
                  - fiche : 920px (un poil plus étroit pour laisser de
                    la place au chat anchored qui flotte à droite en
                    position:fixed sans être dans la colonne)

                ⚠️ Le wrapper est TOUJOURS monté, et neutralisé via
                `display: contents` quand il ne sert pas. Avant (2026-07-21),
                il était monté/démonté selon showWelcomeTopbar : la structure
                de l'arbre changeait à chaque bascule d'écran, donc React
                DÉMONTAIT et REMONTAIT tout le contenu. Conséquence coûteuse :
                au passage analyse → fiche (déclenché par le premier résultat
                partiel), LoadingScreen se remontait et relançait une analyse
                complète — un crédit de plus débité à l'utilisateur à chaque
                analyse. Ne jamais revenir à un wrapper conditionnel ici. */}
            <div
              style={
                showWelcomeTopbar
                  ? { width: "100%", maxWidth: 920, margin: "0 auto", padding: "0 24px", display: "flex", flexDirection: "column" }
                  : { display: "contents" }
              }
            >
              {renderContent()}
            </div>
          </div>

          {/* Bottom Player — toujours présent (sticky), y compris sur la home.
              État "idle" quand aucune piste n'est lancée : transport grisé,
              waveform placeholder, meta vide. Masqué en MINIMAL_SCREENS
              (loading) pour garder l'écran d'analyse focalisé, et sur la
              page admin (page de gestion, pas de lecture audio attendue). */}
          {!isMinimalScreen && screen !== 'admin' && (
          <BottomPlayer
            trackTitle={playerState?.trackTitle}
            versionName={playerState?.versionName}
            storagePath={playerState?.storagePath}
            isPlaying={!!playerState?.isPlaying}
            onToggle={togglePlay}
            onNext={playNext}
            onPrev={playPrev}
            hasNext={hasNext}
            hasPrev={hasPrev}
            resetKey={playerState?.resetKey || 0}
            idle={!playerState}
            playlist={playerState?.playlist}
            currentIdx={playerState?.currentIdx}
          />
          )}

          {/* Pill "Ajouter" — miroir gauche du chat pill, statique
              (ne s'anime pas tout seul, ne réagit qu'au hover). Click
              ouvre la modale d'ajout (projet / titre / version). Visible
              sur tous les écrans authentifiés sauf MINIMAL_SCREENS et
              admin (la page admin est en consultation, pas en édition).
              Refonte 2026-04-30bis : tous les layouts authentifiés
              partagent maintenant la largeur 920px (fiche + dashboard
              + autres), donc layoutWidth fixé à 'fiche'. */}
          {!isMinimalScreen && screen !== 'admin' && !!user && (
            <AddPill
              onOpen={() => setHomeAddOpen(true)}
              isOpen={homeAddOpen}
              layoutWidth="fiche"
              label={s.common?.add || 'Ajouter'}
              placeholder={s.common?.addPillPlaceholder || 'Projet, titre, version'}
            />
          )}

          {/* BottomNav retiré — remplacé par le hamburger menu */}
        </div>
      </div>
    </LangContext.Provider>
  );
}
